import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import {
  defaultRangeExtractor,
  observeElementRect,
  useVirtualizer,
} from "@tanstack/react-virtual";
import { toast } from "react-hot-toast";

import { Empty, SegmentedControl } from "../../components/WorkspacePrimitives";
import {
  Button,
  Checkbox,
  ConfirmDialog,
  CopyIcon,
  EditButton,
  FullscreenIcon,
  HistoryIcon,
  IconButton,
  OpenIcon,
  Selector,
  TextArea,
  TextInput,
  TrashButton,
  type SelectorOption,
} from "../../components/ui";
import { copyText } from "../../lib/clipboard";

import { applyOptimisticXLMStructuralModel } from "../api/e2e";
import type {
  AgentStoredDocument,
  XLMCellAddress,
  XLMDocumentReference,
  XLMEditCellOperation,
  XLMEditOperation,
  XLMModel,
  XLMNode,
  XLMNodeType,
  XLMValue,
} from "../api/e2e";
import { displayXLMUnit, formatXLMNumber } from "../lib/xlmDisplay";
import {
  XLM_FIGURE_COLOR_CSS_BY_ROLE,
  xlmFigureColorContext,
  xlmFigureColorRole,
} from "../lib/xlmFigureColor";
import {
  applyFormulaAutocomplete,
  formulaAutocompleteContext,
  formulaAutocompleteSuggestions,
  type FormulaAutocompleteSuggestion,
} from "../lib/formulaAutocomplete";
import {
  modelFormulaParts,
  modelFormulaReferences,
  modelReferenceTarget,
  sameModelReferencePeriod,
  type ModelReference,
  type ModelReferenceCadence,
} from "../lib/modelReferences";
import { xlmCellReviewLinks, type XLMReviewTab } from "../lib/xlmReviews";
import { XLMDocumentEvidence, xlmLocatorLabel } from "./XLMDocumentEvidence";
import StatementContextMenu, {
  type StatementContextMenuAction,
} from "./StatementContextMenu";
import StatementShortcutHelp from "./StatementShortcutHelp";

export type XLMCadence = ModelReferenceCadence;

export interface XLMPeriodColumn {
  cadence: XLMCadence;
  year: number;
  part?: number;
  label: string;
  endingDate: string | null;
  forecast: boolean;
}

interface ViewerRow {
  key: string;
  node: XLMNode;
  depth: number;
  section: string;
  label?: string;
  values?: XLMValue[] | null;
  boundary: "section" | "heading" | null;
  role: "detail" | "subtotal" | "total";
  bold: boolean;
  italic: boolean;
  excelIndent: number;
  gapBefore: boolean;
  ancestorKeys: string[];
  descendantCount: number;
  unit: string | null;
  unitLabel: string | null;
  selectable: boolean;
  referenceable: boolean;
  breakdown?: {
    node: XLMNode;
    first: boolean;
    last: boolean;
  };
}

interface SelectedCell {
  row: ViewerRow;
  period: XLMPeriodColumn;
  value: XLMValue;
}

interface CellDraft {
  value: string;
  formula: string;
  comment: string;
  documentReferences: XLMDocumentReference[];
}

interface ActiveCellDraft {
  cellKey: string;
  baseline: CellDraft;
  draft: CellDraft;
  validationError: string | null;
}

interface CellUndoEntry {
  kind: "cell";
  cellKey: string;
  draft: CellDraft;
  operation: XLMEditCellOperation;
}

interface BatchUndoEntry {
  kind: "batch";
  operations: XLMEditCellOperation[];
}

interface StructuralUndoEntry {
  kind: "structural";
}

type XLMUndoEntry = CellUndoEntry | BatchUndoEntry | StructuralUndoEntry;

type XLMCellSaveStatus = "idle" | "saving" | "saved" | "failed" | "conflict";

interface GridCellSelection extends SelectedCell {
  rowIndex: number;
  periodIndex: number;
}

interface DragSelection {
  anchor: GridCellSelection;
  focus: GridCellSelection;
  startX: number;
  startY: number;
  formulaSelection?: { start: number; end: number };
}

interface FillDrag {
  source: GridSelectionRange;
  target: GridCellSelection;
}

interface XLMAddRowDraft {
  mode: "add";
  placement: XLMRowPlacement;
  name: string;
  type: XLMNodeType;
  unit: string;
  attempted: boolean;
}

type XLMAuthoredRowTarget =
  | {
      kind: "node";
      node: XLMNode;
      group?: {
        kind: "section" | "heading";
        parentSectionIdentifier: string | null;
      };
    }
  | {
      kind: "breakdown_item";
      node: XLMNode;
      breakdownIdentifier: string;
    };

type XLMRowPlacement =
  | {
      kind: "node";
      beforeIdentifier: string | null;
      afterIdentifier: string | null;
      unit: string | null;
    }
  | {
      kind: "breakdown_item";
      breakdownIdentifier: string;
      beforeIdentifier: string | null;
      afterIdentifier: string | null;
      unit: string | null;
    };

interface XLMEditRowDraft {
  mode: "edit";
  target: XLMAuthoredRowTarget;
  name: string;
  unit: string;
  initialFocus: "name" | "unit";
  convertToTruth: boolean;
  attempted: boolean;
}

interface XLMBreakdownEditorState {
  node: XLMNode;
  name: string;
  unit: string;
  description: string;
  renderTotal: boolean;
  renderGrowth: boolean;
}

type XLMRowEditorState = XLMAddRowDraft | XLMEditRowDraft;

type XLMRowDropPosition = "before" | "after";

interface XLMRowDragState {
  source: XLMAuthoredRowTarget;
  target: XLMAuthoredRowTarget | null;
  position: XLMRowDropPosition | null;
}

interface XLMPendingRowMove {
  id: number;
  operation: XLMEditOperation;
}

interface XLMStructuralSaveOptions {
  optimistic?: boolean;
}

type StatementContextMenuState =
  | {
      kind: "cell";
      x: number;
      y: number;
      cell: GridCellSelection;
    }
  | {
      kind: "row";
      x: number;
      y: number;
      row: ViewerRow;
      rowIndex: number;
    };

interface StatementFigureColorStyle extends CSSProperties {
  "--statement-figure-color": string;
}

const STATEMENT_ROW_HEIGHT = 34;
const STATEMENT_ROW_GAP = 12;
const STATEMENT_BREAKDOWN_CONTROL_HEIGHT = 28;
const STATEMENT_VIRTUAL_OVERSCAN = 8;
const STATEMENT_INITIAL_RECT = { width: 1280, height: 640 };
const STATEMENT_ZOOM_MIN = 50;
const STATEMENT_ZOOM_MAX = 150;
const STATEMENT_ZOOM_STEP = 10;
const STATEMENT_ZOOM_BASE_PERCENT = 70;
const STATEMENT_DRAG_THRESHOLD = 6;
const STATEMENT_LINE_COLUMN_WIDTH = 300;
const STATEMENT_UNIT_COLUMN_WIDTH = 120;
const STATEMENT_VALUE_COLUMN_WIDTH = 94;
const STATEMENT_FORMULA_COLUMN_WIDTH = 190;
const XLM_ROW_TYPE_OPTIONS = [
  {
    value: "truth",
    label: "Line item",
    secondary: "Editable values or formulas",
  },
  {
    value: "computed",
    label: "Calculated",
    secondary: "Formula-driven line item",
  },
  { value: "total", label: "Total", secondary: "Emphasized total row" },
  { value: "title", label: "Title", secondary: "Group heading" },
  {
    value: "section_title",
    label: "Section",
    secondary: "Top-level section heading",
  },
  { value: "separator", label: "Blank", secondary: "Blank spacer row" },
] as const satisfies readonly SelectorOption<XLMNodeType>[];
const XLM_VALUE_ROW_TYPES: ReadonlySet<XLMNodeType> = new Set([
  "truth",
  "computed",
  "total",
]);
const XLM_UNIT_OPTIONS = [
  { value: "", label: "No unit" },
  { value: "currency_b", label: "Currency · billions" },
  { value: "currency_mm", label: "Currency · millions" },
  { value: "currency_k", label: "Currency · thousands" },
  { value: "currency_unit", label: "Currency · units" },
  { value: "units_b", label: "Units · billions" },
  { value: "units_mm", label: "Units · millions" },
  { value: "units_k", label: "Units · thousands" },
  { value: "units_unit", label: "Units" },
  { value: "shares_mm", label: "Shares · millions" },
  { value: "shares_k", label: "Shares · thousands" },
  { value: "shares", label: "Shares" },
  { value: "percent", label: "Percent" },
  { value: "boolean", label: "Boolean" },
] as const satisfies readonly SelectorOption<string>[];
const useBrowserLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const CUSTOM_ROW_UNIT_VALUE = "__custom_row_unit__";

function rowUnitOptions(): readonly SelectorOption<string>[] {
  return XLM_UNIT_OPTIONS;
}

function RowUnitSelector({
  value,
  disabled,
  autoFocus = false,
  onValueChange,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  autoFocus?: boolean;
  onValueChange: (value: string) => void;
  onCommit?: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [custom, setCustom] = useState(
    () =>
      value !== "" &&
      !XLM_UNIT_OPTIONS.some((option) => option.value === value),
  );
  const customInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (custom) customInputRef.current?.focus();
  }, [custom]);
  if (custom) {
    return (
      <div className="xlm-inline-row-editor__custom-unit">
        <input
          ref={customInputRef}
          autoFocus
          value={value}
          aria-label="Custom row unit"
          placeholder="Custom unit"
          maxLength={200}
          disabled={disabled}
          onChange={(event) => onValueChange(event.target.value)}
          onBlur={(event) => {
            const relatedTarget = event.relatedTarget;
            if (
              relatedTarget instanceof Node &&
              event.currentTarget.closest("tr")?.contains(relatedTarget)
            ) {
              return;
            }
            onCommit?.();
          }}
        />
        <button
          type="button"
          aria-label="Choose a preset row unit"
          title="Choose a preset unit"
          disabled={disabled}
          onClick={() => {
            onValueChange("");
            setCustom(false);
          }}
        >
          <span aria-hidden="true">⌄</span>
        </button>
      </div>
    );
  }
  const customUnit = searchQuery.trim();
  const options: readonly SelectorOption<string>[] = customUnit
    ? [
        {
          value: CUSTOM_ROW_UNIT_VALUE,
          label: `Use “${customUnit}” as custom unit`,
          searchText: customUnit,
          secondary: "Set custom formatting unit",
        },
      ]
    : [
        ...rowUnitOptions(),
        {
          value: CUSTOM_ROW_UNIT_VALUE,
          label: "Custom…",
          secondary: "Enter a custom formatting unit",
        },
      ];
  return (
    <Selector
      className="xlm-inline-row-editor__unit"
      ariaLabel="Row unit"
      options={options}
      value={value}
      disabled={disabled}
      autoFocus={autoFocus}
      searchable
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      portalMenu
      onValueChange={(nextValue) => {
        if (nextValue === CUSTOM_ROW_UNIT_VALUE) {
          onValueChange(customUnit);
          setCustom(true);
          return;
        }
        onValueChange(nextValue);
        onCommit?.();
      }}
    />
  );
}

function rowTypeLabel(type: XLMNodeType): string {
  return (
    XLM_ROW_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type
  );
}

function rowUnitDetailLabel(row: ViewerRow): string {
  const unit = row.unit?.trim() || row.unitLabel?.trim() || "";
  return (
    XLM_UNIT_OPTIONS.find((option) => option.value === unit)?.label ??
    (unit || "No unit")
  );
}

function statementRowSize(
  row: ViewerRow | undefined,
  showGap: boolean,
): number {
  return (
    STATEMENT_ROW_HEIGHT +
    (showGap && row?.gapBefore ? STATEMENT_ROW_GAP : 0) +
    (row?.breakdown?.first && row.boundary === null
      ? STATEMENT_BREAKDOWN_CONTROL_HEIGHT
      : 0)
  );
}

function lastIndexAtOrBefore(indexes: number[], target: number): number | null {
  for (let index = indexes.length - 1; index >= 0; index -= 1) {
    const candidate = indexes[index];
    if (candidate !== undefined && candidate <= target) return candidate;
  }
  return null;
}

function modelReferenceCell(
  row: ViewerRow,
  period: XLMPeriodColumn,
): ModelReference["start"] {
  return {
    identifier: row.node.identifier,
    period: {
      cadence: period.cadence,
      year: period.year,
      ...(period.part === undefined ? {} : { part: period.part }),
    },
  };
}

function gridSelectionReference(
  anchor: GridCellSelection,
  focus: GridCellSelection,
): ModelReference {
  if (
    anchor.rowIndex === focus.rowIndex &&
    anchor.periodIndex === focus.periodIndex
  ) {
    const cell = modelReferenceCell(anchor.row, anchor.period);
    return { kind: "cell", start: cell, end: cell };
  }
  const firstRow = anchor.rowIndex <= focus.rowIndex ? anchor.row : focus.row;
  const lastRow = anchor.rowIndex <= focus.rowIndex ? focus.row : anchor.row;
  const firstPeriod =
    anchor.periodIndex <= focus.periodIndex ? anchor.period : focus.period;
  const lastPeriod =
    anchor.periodIndex <= focus.periodIndex ? focus.period : anchor.period;
  return {
    kind: "range",
    start: modelReferenceCell(firstRow, firstPeriod),
    end: modelReferenceCell(lastRow, lastPeriod),
  };
}

function formulaReferenceToken(
  cell: GridCellSelection,
  formulaPeriod: XLMPeriodColumn,
): string {
  if (sameModelReferencePeriod(cell.period, formulaPeriod)) {
    return cell.row.node.identifier;
  }
  const period =
    cell.period.cadence === "quarterly"
      ? `${cell.period.year}Q${cell.period.part}`
      : cell.period.cadence === "half-year"
        ? `${cell.period.year}H${cell.period.part}`
        : String(cell.period.year);
  return `${cell.row.node.identifier}[${period}]`;
}

function formulaReferenceExpression(
  anchor: GridCellSelection,
  focus: GridCellSelection,
  rows: ViewerRow[],
  periods: XLMPeriodColumn[],
  formulaPeriod: XLMPeriodColumn,
): string {
  const range = {
    startRow: Math.min(anchor.rowIndex, focus.rowIndex),
    endRow: Math.max(anchor.rowIndex, focus.rowIndex),
    startPeriod: Math.min(anchor.periodIndex, focus.periodIndex),
    endPeriod: Math.max(anchor.periodIndex, focus.periodIndex),
  };
  const references: string[] = [];
  for (let rowIndex = range.startRow; rowIndex <= range.endRow; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!row || row.boundary !== null || !row.referenceable) {
      throw new Error(
        "Formula references cannot include a read-only model row.",
      );
    }
    for (
      let periodIndex = range.startPeriod;
      periodIndex <= range.endPeriod;
      periodIndex += 1
    ) {
      const period = periods[periodIndex];
      if (!period)
        throw new Error("The formula reference includes an unknown period.");
      references.push(
        formulaReferenceToken(
          {
            row,
            period,
            value: matchingValue(row.values, period) ?? blankXLMValue(period),
            rowIndex,
            periodIndex,
          },
          formulaPeriod,
        ),
      );
    }
  }
  if (references.length === 0) {
    throw new Error("Select at least one formula reference.");
  }
  const first = references[0] ?? "";
  const last = references.at(-1) ?? first;
  return references.length === 1 ? first : `${first}:${last}`;
}

const observeStatementRect: typeof observeElementRect = (instance, callback) =>
  observeElementRect(instance, (rect) =>
    callback(rect.height > 0 ? rect : STATEMENT_INITIAL_RECT),
  );

function isForecast(
  model: XLMModel,
  cadence: XLMCadence,
  year: number,
  part?: number,
): boolean {
  const cutoff = model.last_historical_period;
  if (!cutoff) return false;
  if (year !== cutoff.year) return year > cutoff.year;
  if (cadence === "annual") return cutoff.quarter < 4;
  if (cadence === "quarterly") return (part ?? 0) > cutoff.quarter;
  return (part ?? 0) * 2 > cutoff.quarter;
}

export function xlmPeriods(
  model: XLMModel,
  cadence: XLMCadence,
): XLMPeriodColumn[] {
  if (cadence === "annual") {
    return [...model.fiscal_years]
      .sort((left, right) => left.year - right.year)
      .map((period) => ({
        cadence,
        year: period.year,
        label: `FY${String(period.year).slice(-2)}`,
        endingDate: period.ending_date ?? null,
        forecast: isForecast(model, cadence, period.year),
      }));
  }
  if (cadence === "quarterly") {
    return [...model.quarters]
      .sort(
        (left, right) => left.year - right.year || left.quarter - right.quarter,
      )
      .map((period) => ({
        cadence,
        year: period.year,
        part: period.quarter,
        label: `Q${period.quarter} FY${String(period.year).slice(-2)}`,
        endingDate: period.ending_date ?? null,
        forecast: isForecast(model, cadence, period.year, period.quarter),
      }));
  }
  return [...model.half_years]
    .sort((left, right) => left.year - right.year || left.half - right.half)
    .map((period) => ({
      cadence,
      year: period.year,
      part: period.half,
      label: `H${period.half} FY${String(period.year).slice(-2)}`,
      endingDate: period.ending_date ?? null,
      forecast: isForecast(model, cadence, period.year, period.half),
    }));
}

/** Show every period registered in the pinned model version. */
export function displayedXLMPeriods(
  model: XLMModel,
  cadence: XLMCadence,
): XLMPeriodColumn[] {
  return xlmPeriods(model, cadence);
}

function valuesFor(node: XLMNode, cadence: XLMCadence): XLMValue[] | null {
  if (cadence === "annual") return node.yearly_values ?? null;
  if (cadence === "quarterly") return node.quarterly_values ?? null;
  return node.half_year_values ?? null;
}

function reportedValues(node: XLMNode, cadence: XLMCadence): XLMValue[] | null {
  if (!node.breakdown) return null;
  if (cadence === "annual")
    return node.breakdown.yearly_reported_totals ?? null;
  if (cadence === "quarterly") {
    return node.breakdown.quarterly_reported_totals ?? null;
  }
  return node.breakdown.half_year_reported_totals ?? null;
}

function matchingValue(
  values: XLMValue[] | null | undefined,
  period: XLMPeriodColumn,
): XLMValue | undefined {
  return values?.find((value) => {
    if (value.year !== period.year) return false;
    if (period.cadence === "quarterly") return value.quarter === period.part;
    if (period.cadence === "half-year") return value.half === period.part;
    return true;
  });
}

function blankXLMValue(period: XLMPeriodColumn): XLMValue {
  if (period.cadence === "quarterly") {
    return { year: period.year, quarter: period.part };
  }
  if (period.cadence === "half-year") {
    return { year: period.year, half: period.part };
  }
  return { year: period.year };
}

function xlmCellAddress(
  row: ViewerRow,
  period: XLMPeriodColumn,
): XLMCellAddress {
  const reportedTotal =
    row.node.breakdown !== null && row.node.breakdown !== undefined;
  const collection =
    period.cadence === "annual"
      ? reportedTotal
        ? "yearly_reported_total"
        : "yearly"
      : period.cadence === "quarterly"
        ? reportedTotal
          ? "quarterly_reported_total"
          : "quarterly"
        : reportedTotal
          ? "half_year_reported_total"
          : "half_year";
  return {
    identifier: row.node.identifier,
    collection,
    year: period.year,
    index: period.part ?? null,
  };
}

function xlmCellAddressKey(row: ViewerRow, period: XLMPeriodColumn): string {
  const address = xlmCellAddress(row, period);
  return `${address.identifier}:${address.collection}:${address.year}:${address.index ?? 0}`;
}

function mergedValues(
  values: XLMValue[] | null,
  reported: XLMValue[] | null,
  cadence: XLMCadence,
): XLMValue[] | null {
  if (!reported?.length) return values;
  const key = (value: XLMValue): string =>
    cadence === "quarterly"
      ? `${value.year}Q${value.quarter ?? 0}`
      : cadence === "half-year"
        ? `${value.year}H${value.half ?? 0}`
        : String(value.year);
  const merged = new Map((values ?? []).map((value) => [key(value), value]));
  for (const value of reported) merged.set(key(value), value);
  return [...merged.values()];
}

function hasDisplayValue(values: XLMValue[] | null): boolean {
  return Boolean(
    values?.some((value) => {
      const resolved = value.computed_value ?? value.value;
      return typeof resolved === "number" || typeof resolved === "boolean";
    }),
  );
}

export function displayXLMValue(
  value: XLMValue | undefined,
  unit: string | null | undefined,
): string {
  if (!value) return "—";
  const resolved = value.computed_value ?? value.value;
  if (typeof resolved === "boolean") return resolved ? "TRUE" : "FALSE";
  if (typeof resolved === "number") return formatXLMNumber(resolved, unit);
  return "—";
}

type GrowthKind = "yoy" | "qoq" | "hoh";

function resolvedNumber(value: XLMValue | undefined): number | null {
  const resolved = value?.computed_value ?? value?.value;
  return typeof resolved === "number" && Number.isFinite(resolved)
    ? resolved
    : null;
}

function growthResult(
  current: XLMValue | undefined,
  previous: XLMValue | undefined,
): number | null {
  const currentNumber = resolvedNumber(current);
  const previousNumber = resolvedNumber(previous);
  if (
    currentNumber === null ||
    previousNumber === null ||
    previousNumber === 0
  ) {
    return 0;
  }
  return currentNumber / previousNumber - 1;
}

function synthesizedGrowthNode(
  item: XLMNode,
  cadence: XLMCadence,
  kind: GrowthKind,
): XLMNode | null {
  if (kind === "yoy" && !item.yearly_values?.length) return null;
  if (kind === "qoq" && !item.quarterly_values?.length) return null;
  if (kind === "hoh" && !item.half_year_values?.length) return null;

  const sourceValues = valuesFor(item, cadence) ?? [];
  const byPeriod = new Map(
    sourceValues.map((value) => [
      `${value.year}:${value.quarter ?? value.half ?? 0}`,
      value,
    ]),
  );
  const growthValues: XLMValue[] = [];
  for (const current of sourceValues) {
    let previousYear = current.year;
    let previousPart = current.quarter ?? current.half ?? 0;
    if (kind === "yoy") {
      previousYear -= 1;
    } else if (cadence === "quarterly") {
      if (previousPart === 1) {
        previousYear -= 1;
        previousPart = 4;
      } else {
        previousPart -= 1;
      }
    } else if (cadence === "half-year") {
      if (previousPart === 1) {
        previousYear -= 1;
        previousPart = 2;
      } else {
        previousPart = 1;
      }
    } else {
      continue;
    }
    const previous = byPeriod.get(`${previousYear}:${previousPart}`);
    if (!previous) continue;
    const currentPeriod =
      kind === "yoy"
        ? ""
        : cadence === "quarterly"
          ? `[${current.year}Q${current.quarter}]`
          : cadence === "half-year"
            ? `[${current.year}H${current.half}]`
            : "";
    const previousPeriod =
      cadence === "quarterly"
        ? `[${previousYear}Q${previousPart}]`
        : cadence === "half-year"
          ? `[${previousYear}H${previousPart}]`
          : `[${previousYear}]`;
    growthValues.push({
      year: current.year,
      quarter: current.quarter,
      half: current.half,
      formula: `=IFERROR(${item.identifier}${currentPeriod}/${item.identifier}${previousPeriod}-1,0)`,
      computed_value: growthResult(current, previous),
    });
  }
  if (growthValues.length === 0) return null;

  const label = kind === "yoy" ? "YoY" : kind === "qoq" ? "QoQ" : "HoH";
  return {
    identifier: `${item.identifier}_${kind}`,
    name: `${label} Growth - ${item.name}`,
    type: "computed",
    unit: "percent",
    yearly_values: cadence === "annual" ? growthValues : null,
    quarterly_values: cadence === "quarterly" ? growthValues : null,
    half_year_values: cadence === "half-year" ? growthValues : null,
  };
}

function synthesizedTotalValues(
  items: XLMNode[],
  cadence: XLMCadence,
): XLMValue[] | null {
  const valuesByPeriod = new Map<string, XLMValue[]>();
  for (const item of items) {
    for (const value of valuesFor(item, cadence) ?? []) {
      const key = `${value.year}:${value.quarter ?? value.half ?? 0}`;
      valuesByPeriod.set(key, [...(valuesByPeriod.get(key) ?? []), value]);
    }
  }
  if (valuesByPeriod.size === 0) return null;
  const itemIds = items.map((item) => item.identifier);
  const formula =
    itemIds.length === 1
      ? `=${itemIds[0]}`
      : `=SUM(${itemIds[0]}:${itemIds[itemIds.length - 1]})`;
  return [...valuesByPeriod.values()]
    .flatMap((periodValues) => {
      const first = periodValues[0];
      if (!first) return [];
      const numbers = periodValues
        .map((value) => resolvedNumber(value))
        .filter((value): value is number => value !== null);
      return [
        {
          year: first.year,
          quarter: first.quarter,
          half: first.half,
          formula,
          computed_value:
            numbers.length > 0
              ? numbers.reduce((total, value) => total + value, 0)
              : null,
        },
      ];
    })
    .sort(
      (left, right) =>
        left.year - right.year ||
        (left.quarter ?? left.half ?? 0) - (right.quarter ?? right.half ?? 0),
    );
}

function synthesizedTotalNode(node: XLMNode): XLMNode | null {
  const items = node.breakdown?.items ?? [];
  if (items.length === 0) return null;
  const name = node.name.toLocaleLowerCase().startsWith("total ")
    ? node.name
    : `Total ${node.name}`;
  return {
    identifier: `${node.identifier}_total`,
    name,
    type: "total",
    unit: node.breakdown?.unit ?? node.unit ?? null,
    yearly_values: synthesizedTotalValues(items, "annual"),
    quarterly_values: synthesizedTotalValues(items, "quarterly"),
    half_year_values: synthesizedTotalValues(items, "half-year"),
  };
}

function growthSourceIdentifier(row: ViewerRow): string | null {
  if (!/^\s*(yoy|qoq|hoh)\b/i.test(row.node.name)) return null;
  const formula = row.values?.find((value) => value.formula)?.formula;
  if (!formula) return null;
  return formula.match(/^=(?:IFERROR\()?([A-Za-z_][A-Za-z0-9_]*)/)?.[1] ?? null;
}

function modelRows(
  model: XLMModel,
  cadence: XLMCadence,
  includeEmptyAuthoredRows = false,
): ViewerRow[] {
  const rows: ViewerRow[] = [];
  let section = "";
  let sectionKey: string | null = null;
  let headingKey: string | null = null;
  let previousNodeType: XLMNode["type"] | null = null;
  let previousBoundary: ViewerRow["boundary"] = null;
  for (const node of model.nodes) {
    const successionGap =
      previousNodeType !== null &&
      previousNodeType !== "separator" &&
      (node.type === "section_title" ||
        (node.type === "title" && previousNodeType !== "section_title"));
    if (node.type === "separator") {
      const ancestorKeys = [sectionKey, headingKey].filter(
        (key): key is string => key !== null,
      );
      const depth = headingKey ? 2 : sectionKey ? 1 : 0;
      rows.push({
        key: node.identifier,
        node,
        depth,
        section,
        values: [],
        boundary: null,
        role: "detail",
        bold: false,
        italic: false,
        excelIndent: 0,
        gapBefore: false,
        ancestorKeys,
        descendantCount: 0,
        unit: null,
        unitLabel: null,
        selectable: false,
        referenceable: false,
      });
      previousBoundary = null;
      previousNodeType = node.type;
      continue;
    }
    const rendersBreakdownHeader = node.breakdown?.render_header !== false;
    const boundary =
      node.type === "section_title"
        ? ("section" as const)
        : node.breakdown
          ? rendersBreakdownHeader
            ? ("heading" as const)
            : null
          : node.type === "title"
            ? ("heading" as const)
            : null;
    const ancestorKeys = [sectionKey, headingKey].filter(
      (key): key is string => key !== null,
    );
    if (boundary === "section") {
      section = node.identifier;
      sectionKey = node.identifier;
      headingKey = null;
    } else if (boundary === "heading") {
      headingKey = node.identifier;
    }
    const nodeValues = mergedValues(
      valuesFor(node, cadence),
      reportedValues(node, cadence),
      cadence,
    );
    const nodeUnit = node.breakdown?.unit ?? node.unit ?? null;
    const nodeRows: ViewerRow[] = [];
    const emptyValueRow =
      includeEmptyAuthoredRows &&
      !node.breakdown &&
      (node.type === "truth" ||
        node.type === "computed" ||
        node.type === "total");
    if (
      boundary ||
      (!node.breakdown && (hasDisplayValue(nodeValues) || emptyValueRow))
    ) {
      nodeRows.push({
        key: node.identifier,
        node,
        depth:
          boundary === "section"
            ? 0
            : boundary === "heading"
              ? 1
              : headingKey
                ? 2
                : sectionKey
                  ? 1
                  : 0,
        section,
        values: nodeValues,
        boundary,
        role:
          node.type === "total" ||
          (node.breakdown?.render_total && hasDisplayValue(nodeValues))
            ? "total"
            : "detail",
        bold: node.type === "total",
        italic: node.type === "computed",
        excelIndent: 0,
        gapBefore: false,
        ancestorKeys:
          boundary === "section"
            ? []
            : boundary === "heading"
              ? sectionKey
                ? [sectionKey]
                : []
              : ancestorKeys,
        descendantCount: 0,
        unit: nodeUnit,
        unitLabel: null,
        selectable:
          boundary === null ||
          (boundary === "heading" && hasDisplayValue(nodeValues)),
        referenceable:
          boundary === null ||
          (boundary === "heading" && hasDisplayValue(nodeValues)),
      });
    }
    const parentDepth = headingKey ? 2 : sectionKey ? 1 : 0;
    const breakdownDepth = rendersBreakdownHeader
      ? parentDepth + 1
      : parentDepth;
    for (const item of node.breakdown?.items ?? []) {
      const itemValues = valuesFor(item, cadence);
      const emptyValueRow =
        includeEmptyAuthoredRows &&
        (item.type === "truth" ||
          item.type === "computed" ||
          item.type === "total");
      if (!hasDisplayValue(itemValues) && !emptyValueRow) continue;
      nodeRows.push({
        key: `${node.identifier}:${item.identifier}`,
        node: item,
        depth: breakdownDepth,
        section,
        values: itemValues,
        boundary: null,
        role: item.type === "total" ? "total" : "detail",
        bold: item.type === "total",
        italic: item.type === "computed",
        excelIndent: 1,
        gapBefore: false,
        ancestorKeys: [sectionKey, headingKey].filter(
          (key): key is string => key !== null,
        ),
        descendantCount: 0,
        unit: item.unit ?? nodeUnit,
        unitLabel: null,
        selectable: true,
        referenceable: true,
      });
    }
    const totalNode =
      node.breakdown?.render_total !== false
        ? synthesizedTotalNode(node)
        : null;
    if (totalNode) {
      const totalValues = valuesFor(totalNode, cadence);
      if (hasDisplayValue(totalValues)) {
        nodeRows.push({
          key: `${node.identifier}:${totalNode.identifier}`,
          node: totalNode,
          depth: breakdownDepth,
          section,
          values: totalValues,
          boundary: null,
          role: "total",
          bold: true,
          italic: false,
          excelIndent: 1,
          gapBefore: false,
          ancestorKeys: [sectionKey, headingKey].filter(
            (key): key is string => key !== null,
          ),
          descendantCount: 0,
          unit: totalNode.unit ?? nodeUnit,
          unitLabel: null,
          selectable: true,
          referenceable: false,
        });
      }
    }
    if (node.breakdown?.render_growth) {
      const growthSources: { node: XLMNode; role: ViewerRow["role"] }[] =
        node.breakdown.items.map((item) => ({ node: item, role: "detail" }));
      if (totalNode) {
        growthSources.push({
          node: totalNode,
          role: "subtotal",
        });
      }
      const growthKinds: GrowthKind[] =
        cadence === "annual"
          ? ["yoy"]
          : cadence === "quarterly"
            ? ["yoy", "qoq"]
            : ["yoy", "hoh"];
      for (const kind of growthKinds) {
        for (const source of growthSources) {
          const growthNode = synthesizedGrowthNode(source.node, cadence, kind);
          if (!growthNode) continue;
          nodeRows.push({
            key: `${node.identifier}:${growthNode.identifier}`,
            node: growthNode,
            depth: breakdownDepth,
            section,
            values: valuesFor(growthNode, cadence),
            boundary: null,
            role: source.role,
            bold: source.role !== "detail",
            italic: true,
            excelIndent: kind === "yoy" ? 0 : 1,
            gapBefore: false,
            ancestorKeys: [sectionKey, headingKey].filter(
              (key): key is string => key !== null,
            ),
            descendantCount: 0,
            unit: "percent",
            unitLabel: null,
            selectable: true,
            referenceable: false,
          });
        }
      }
    }
    for (const [index, row] of nodeRows.entries()) {
      const computedPercent = isHistoricallyFormulaDrivenPercentRow(model, row);
      const excelIndent = row.excelIndent + (computedPercent ? 1 : 0);
      const spacedGroup =
        index === 0 && boundary !== null && successionGap && rows.length > 0;
      const percentLeadsSection =
        previousBoundary === "section" && computedPercent;
      rows.push({
        ...row,
        excelIndent,
        gapBefore: spacedGroup || percentLeadsSection,
        ...(node.breakdown
          ? {
              breakdown: {
                node,
                first: index === 0,
                last: index === nodeRows.length - 1,
              },
            }
          : {}),
      });
      previousBoundary = row.boundary;
    }
    previousNodeType = node.type;
  }
  const descendantCounts = new Map<string, number>();
  const boldByIdentifier = new Map(
    rows.map((row) => [row.node.identifier, row.bold]),
  );
  for (const row of rows) {
    for (const key of row.ancestorKeys) {
      descendantCounts.set(key, (descendantCounts.get(key) ?? 0) + 1);
    }
  }
  return rows.map((row) => {
    const growthSource = growthSourceIdentifier(row);
    return {
      ...row,
      bold:
        row.bold || Boolean(growthSource && boldByIdentifier.get(growthSource)),
      descendantCount: descendantCounts.get(row.key) ?? 0,
    };
  });
}

function authoredRowTarget(
  model: XLMModel,
  row: ViewerRow | null,
): XLMAuthoredRowTarget | null {
  if (!row) return null;
  const topLevel = model.nodes.find(
    (node) => node.identifier === row.node.identifier,
  );
  if (topLevel) {
    if (topLevel.breakdown) return { kind: "node", node: topLevel };
    const group =
      topLevel.type === "section_title"
        ? {
            kind: "section" as const,
            parentSectionIdentifier: null,
          }
        : topLevel.type === "title"
          ? {
              kind: "heading" as const,
              parentSectionIdentifier: row.ancestorKeys[0] ?? null,
            }
          : undefined;
    return { kind: "node", node: topLevel, ...(group ? { group } : {}) };
  }
  for (const node of model.nodes) {
    const item = node.breakdown?.items.find(
      (candidate) => candidate.identifier === row.node.identifier,
    );
    if (item) {
      return {
        kind: "breakdown_item",
        node: item,
        breakdownIdentifier: node.identifier,
      };
    }
  }
  return null;
}

function authoredRowTargetsForSelection(
  model: XLMModel,
  rows: ViewerRow[],
  selection: GridSelectionRange | null,
): XLMAuthoredRowTarget[] | null {
  if (!selection) return null;
  const selectedRows = rows
    .slice(selection.startRow, selection.endRow + 1)
    .filter((row) => row.boundary === null);
  if (selectedRows.length === 0) return null;
  const targets: XLMAuthoredRowTarget[] = [];
  for (const row of selectedRows) {
    const target = authoredRowTarget(model, row);
    if (!target) return null;
    if (targets.some((candidate) => sameAuthoredRowTarget(candidate, target))) {
      continue;
    }
    targets.push(target);
  }
  return targets.length > 0 ? targets : null;
}

function addRowPlacement(
  model: XLMModel,
  row: ViewerRow | null,
  position: "before" | "after" = "after",
): XLMRowPlacement {
  const target = authoredRowTarget(model, row);
  if (target?.kind === "breakdown_item") {
    return {
      kind: "breakdown_item",
      breakdownIdentifier: target.breakdownIdentifier,
      beforeIdentifier: position === "before" ? target.node.identifier : null,
      afterIdentifier: position === "after" ? target.node.identifier : null,
      unit: row?.unit ?? null,
    };
  }
  const topLevel = row
    ? model.nodes.find(
        (node) =>
          node.identifier === row.node.identifier ||
          row.key.startsWith(`${node.identifier}:`),
      )
    : undefined;
  const anchor = topLevel ?? model.nodes.at(-1);
  return {
    kind: "node",
    beforeIdentifier:
      position === "before" ? (anchor?.identifier ?? null) : null,
    afterIdentifier: position === "after" ? (anchor?.identifier ?? null) : null,
    unit: row?.unit ?? anchor?.unit ?? null,
  };
}

function inlineRowIdentifier(name: string): string {
  const normalized = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/gu, "")
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9_]+/gu, "_")
    .replace(/^_+|_+$/gu, "");
  if (normalized === "") return "";
  return /^[0-9]/u.test(normalized) ? `row_${normalized}` : normalized;
}

function uniqueInlineNodeIdentifier(model: XLMModel, name: string): string {
  const base = inlineRowIdentifier(name);
  if (base === "") return "";
  const identifiers = new Set(model.nodes.map((node) => node.identifier));
  if (!identifiers.has(base)) return base;
  let suffix = 2;
  while (identifiers.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
}

function addRowDraft(placement: XLMRowPlacement): XLMAddRowDraft {
  return {
    mode: "add",
    placement,
    name: "",
    type: "truth",
    unit: placement.unit ?? "",
    attempted: false,
  };
}

function editRowDraft(
  target: XLMAuthoredRowTarget,
  initialFocus: "name" | "unit" = "name",
): XLMEditRowDraft {
  return {
    mode: "edit",
    target,
    name: target.node.type === "separator" ? "" : target.node.name,
    unit: target.node.unit ?? "",
    initialFocus,
    convertToTruth: target.node.type === "separator",
    attempted: false,
  };
}

function topLevelRowIdentifier(model: XLMModel, row: ViewerRow): string | null {
  const direct = model.nodes.find(
    (node) => node.identifier === row.node.identifier,
  );
  if (direct) return direct.identifier;
  return (
    model.nodes.find((node) => row.key.startsWith(`${node.identifier}:`))
      ?.identifier ?? null
  );
}

function rowPlacementInsertionIndex(
  model: XLMModel,
  rows: ViewerRow[],
  placement: XLMRowPlacement,
): number {
  if (placement.kind === "breakdown_item") {
    const anchorIdentifier =
      placement.beforeIdentifier ?? placement.afterIdentifier;
    const anchorIndex = rows.findIndex(
      (row) => row.node.identifier === anchorIdentifier,
    );
    if (anchorIndex < 0) return rows.length;
    if (placement.beforeIdentifier) return anchorIndex;
    for (let index = anchorIndex + 1; index < rows.length; index += 1) {
      const row = rows[index];
      if (!row) continue;
      const target = authoredRowTarget(model, row);
      if (
        target?.kind === "breakdown_item" &&
        target.breakdownIdentifier === placement.breakdownIdentifier &&
        target.node.identifier !== anchorIdentifier
      ) {
        return index;
      }
      if (
        target?.kind === "node" &&
        target.node.identifier !== placement.breakdownIdentifier
      ) {
        return index;
      }
    }
    return rows.length;
  }

  const anchorIdentifier =
    placement.beforeIdentifier ?? placement.afterIdentifier;
  if (!anchorIdentifier) return rows.length;
  const anchorNodeIndex = model.nodes.findIndex(
    (node) => node.identifier === anchorIdentifier,
  );
  if (anchorNodeIndex < 0) return rows.length;
  if (placement.beforeIdentifier) {
    const index = rows.findIndex(
      (row) => topLevelRowIdentifier(model, row) === anchorIdentifier,
    );
    return index < 0 ? rows.length : index;
  }
  const index = rows.findIndex((row) => {
    const identifier = topLevelRowIdentifier(model, row);
    if (!identifier) return false;
    return (
      model.nodes.findIndex((node) => node.identifier === identifier) >
      anchorNodeIndex
    );
  });
  return index < 0 ? rows.length : index;
}

function sameAuthoredRowTarget(
  left: XLMAuthoredRowTarget,
  right: XLMAuthoredRowTarget,
): boolean {
  return (
    left.kind === right.kind &&
    left.node.identifier === right.node.identifier &&
    (left.kind === "breakdown_item"
      ? right.kind === "breakdown_item" &&
        left.breakdownIdentifier === right.breakdownIdentifier
      : right.kind === "node" &&
        left.group?.kind === right.group?.kind &&
        left.group?.parentSectionIdentifier ===
          right.group?.parentSectionIdentifier)
  );
}

function groupedHeaderLastIdentifier(
  model: XLMModel,
  target: Extract<XLMAuthoredRowTarget, { kind: "node" }>,
): string {
  const start = model.nodes.findIndex(
    (node) => node.identifier === target.node.identifier,
  );
  if (start < 0 || !target.group) return target.node.identifier;
  let end = start + 1;
  while (end < model.nodes.length) {
    const candidate = model.nodes[end];
    if (!candidate) break;
    if (candidate.type === "section_title") break;
    if (target.group.kind === "heading" && candidate.type === "title") break;
    end += 1;
  }
  return model.nodes[end - 1]?.identifier ?? target.node.identifier;
}

function nodeParentSectionIdentifier(
  model: XLMModel,
  node: XLMNode,
): string | null {
  const index = model.nodes.indexOf(node);
  if (index < 0) return null;
  return (
    model.nodes
      .slice(0, index + 1)
      .reverse()
      .find((candidate) => candidate.type === "section_title")?.identifier ??
    null
  );
}

function xlmMoveRowOperation(
  model: XLMModel,
  source: XLMAuthoredRowTarget,
  target: XLMAuthoredRowTarget,
  position: XLMRowDropPosition,
): XLMEditOperation | null {
  if (source.kind !== target.kind || sameAuthoredRowTarget(source, target)) {
    return null;
  }
  if (source.kind === "node" && target.kind === "node" && source.group) {
    if (
      !target.group ||
      source.group.kind !== target.group.kind ||
      source.group.parentSectionIdentifier !==
        target.group.parentSectionIdentifier
    ) {
      return null;
    }
    const siblings = model.nodes.filter((node) =>
      source.group?.kind === "section"
        ? node.type === "section_title"
        : node.type === "title" &&
          nodeParentSectionIdentifier(model, node) ===
            source.group?.parentSectionIdentifier,
    );
    const identifiers = siblings.map((node) => node.identifier);
    const sourceIndex = identifiers.indexOf(source.node.identifier);
    const targetIndex = identifiers.indexOf(target.node.identifier);
    if (sourceIndex < 0 || targetIndex < 0) return null;
    const reordered = [...identifiers];
    reordered.splice(sourceIndex, 1);
    const remainingTargetIndex = reordered.indexOf(target.node.identifier);
    reordered.splice(
      remainingTargetIndex + (position === "after" ? 1 : 0),
      0,
      source.node.identifier,
    );
    if (
      reordered.every((identifier, index) => identifier === identifiers[index])
    ) {
      return null;
    }
    return {
      kind: "move_node",
      identifier: source.node.identifier,
      scope: "group",
      ...(position === "before"
        ? { before_identifier: target.node.identifier }
        : { after_identifier: groupedHeaderLastIdentifier(model, target) }),
    };
  }
  if (
    source.kind === "node" &&
    target.kind === "node" &&
    (source.group || target.group)
  ) {
    return null;
  }
  let siblings: XLMNode[];
  if (source.kind === "breakdown_item") {
    if (
      target.kind !== "breakdown_item" ||
      source.breakdownIdentifier !== target.breakdownIdentifier
    ) {
      return null;
    }
    const breakdown = model.nodes.find(
      (node) => node.identifier === source.breakdownIdentifier,
    )?.breakdown;
    if (!breakdown) return null;
    siblings = breakdown.items;
  } else {
    siblings = model.nodes;
  }
  const identifiers = siblings.map((node) => node.identifier);
  const sourceIndex = identifiers.indexOf(source.node.identifier);
  const targetIndex = identifiers.indexOf(target.node.identifier);
  if (sourceIndex < 0 || targetIndex < 0) return null;
  const reordered = [...identifiers];
  reordered.splice(sourceIndex, 1);
  const remainingTargetIndex = reordered.indexOf(target.node.identifier);
  reordered.splice(
    remainingTargetIndex + (position === "after" ? 1 : 0),
    0,
    source.node.identifier,
  );
  if (
    reordered.every((identifier, index) => identifier === identifiers[index])
  ) {
    return null;
  }
  const anchor =
    position === "before"
      ? { before_identifier: target.node.identifier }
      : { after_identifier: target.node.identifier };
  return source.kind === "breakdown_item"
    ? {
        kind: "move_breakdown_item",
        breakdown_identifier: source.breakdownIdentifier,
        identifier: source.node.identifier,
        ...anchor,
      }
    : {
        kind: "move_node",
        identifier: source.node.identifier,
        ...anchor,
      };
}

interface XLMClipboardPayload {
  text: string;
  cellCount: number;
  lineItemCount?: number;
}

interface GridSelectionRange {
  startRow: number;
  endRow: number;
  startPeriod: number;
  endPeriod: number;
}

type XLMClipboardMode = "formulas" | "values";

function sanitizeClipboardField(
  value: string,
  preserveFormula: boolean,
): string {
  const flattened = value.replace(/[\t\r\n]+/gu, " ");
  if (preserveFormula && flattened.startsWith("=")) return flattened;
  return /^[=+\-@]/u.test(flattened) ? `'${flattened}` : flattened;
}

function clipboardTableText(
  rows: readonly (readonly string[])[],
  preserveFormulas = false,
): string {
  return rows
    .map((row) =>
      row
        .map((value) => sanitizeClipboardField(value, preserveFormulas))
        .join("\t"),
    )
    .join("\r\n");
}

function clipboardCellText(
  row: ViewerRow,
  period: XLMPeriodColumn,
  mode: XLMClipboardMode,
): string {
  const value = matchingValue(row.values, period);
  if (!value) return "";
  if (mode === "formulas" && value.formula) return value.formula;
  return displayXLMValue(value, row.unit ?? row.unitLabel);
}

function gridReferenceRange(
  rows: ViewerRow[],
  periods: XLMPeriodColumn[],
  reference: ModelReference | null,
  cadence: XLMCadence,
): GridSelectionRange | null {
  if (!reference || reference.start.period.cadence !== cadence) return null;
  const startRow = rows.findIndex(
    (row) =>
      row.selectable && row.node.identifier === reference.start.identifier,
  );
  const endRow = rows.findIndex(
    (row) => row.selectable && row.node.identifier === reference.end.identifier,
  );
  const startPeriod = periods.findIndex((period) =>
    sameModelReferencePeriod(period, reference.start.period),
  );
  const endPeriod = periods.findIndex((period) =>
    sameModelReferencePeriod(period, reference.end.period),
  );
  if (
    startRow < 0 ||
    endRow < startRow ||
    startPeriod < 0 ||
    endPeriod < startPeriod
  ) {
    return null;
  }
  return { startRow, endRow, startPeriod, endPeriod };
}

/** Serialize the exact highlighted grid rectangle. Cell-range copies omit row
 * metadata; complete line-item selections opt in to their labels and units. */
function gridSelectionClipboardPayload(
  rows: ViewerRow[],
  periods: XLMPeriodColumn[],
  range: GridSelectionRange,
  includeLineItemMetadata = false,
  reportingCurrency?: string | null,
  mode: XLMClipboardMode = "formulas",
): XLMClipboardPayload | null {
  const selectedRows = rows
    .slice(range.startRow, range.endRow + 1)
    .filter((row) => row.boundary === null);
  const selectedPeriods = periods.slice(range.startPeriod, range.endPeriod + 1);
  if (selectedRows.length === 0 || selectedPeriods.length === 0) return null;
  return {
    text: clipboardTableText(
      selectedRows.map((row) => {
        const values = selectedPeriods.map((period) =>
          clipboardCellText(row, period, mode),
        );
        if (!includeLineItemMetadata) return values;
        const unit = displayXLMUnit(row.unit, row.unitLabel, reportingCurrency);
        return [
          row.label ?? row.node.name,
          unit === "—" ? "" : unit,
          ...values,
        ];
      }),
      mode === "formulas",
    ),
    cellCount: selectedRows.length * selectedPeriods.length,
    ...(includeLineItemMetadata ? { lineItemCount: selectedRows.length } : {}),
  };
}

function preservesNativeKeyboardInput(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return (
    target.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
    ) !== null
  );
}

type XLMClipboardCell =
  | { kind: "blank" }
  | { kind: "formula"; formula: string }
  | { kind: "number"; value: number };

interface XLMClipboardMatrix {
  cells: XLMClipboardCell[][];
  rowCount: number;
  columnCount: number;
}

interface XLMPasteResult {
  operations: XLMEditCellOperation[];
  undoOperations: XLMEditCellOperation[];
  range: GridSelectionRange;
}

type XLMFillResult = XLMPasteResult;

function normalizedModulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

function shiftedPeriodToken(
  year: number,
  kind: "Q" | "H" | undefined,
  part: number | undefined,
  source: XLMPeriodColumn,
  target: XLMPeriodColumn,
): string | null {
  if (source.cadence === "annual" && kind === undefined) {
    return String(year + target.year - source.year);
  }
  if (
    source.cadence === "quarterly" &&
    kind === "Q" &&
    part !== undefined &&
    source.part !== undefined &&
    target.part !== undefined
  ) {
    const delta =
      target.year * 4 + target.part - (source.year * 4 + source.part);
    const shifted = year * 4 + part - 1 + delta;
    return `${Math.floor(shifted / 4)}Q${normalizedModulo(shifted, 4) + 1}`;
  }
  if (
    source.cadence === "half-year" &&
    kind === "H" &&
    part !== undefined &&
    source.part !== undefined &&
    target.part !== undefined
  ) {
    const delta =
      target.year * 2 + target.part - (source.year * 2 + source.part);
    const shifted = year * 2 + part - 1 + delta;
    return `${Math.floor(shifted / 2)}H${normalizedModulo(shifted, 2) + 1}`;
  }
  return null;
}

export function shiftXLMFormulaForFill(
  formula: string,
  source: XLMPeriodColumn,
  target: XLMPeriodColumn,
): string {
  if (sameModelReferencePeriod(source, target)) return formula;
  return formula.replace(
    /\[(\d{4})(?:(Q|H)([1-4]))?\]/gu,
    (
      token,
      yearText: string,
      kindText: string | undefined,
      partText: string | undefined,
    ) => {
      const kind = kindText === "Q" || kindText === "H" ? kindText : undefined;
      const shifted = shiftedPeriodToken(
        Number(yearText),
        kind,
        partText === undefined ? undefined : Number(partText),
        source,
        target,
      );
      return shifted === null ? token : `[${shifted}]`;
    },
  );
}

function parseClipboardNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "–" || trimmed === "—") return 0;
  const parenthesized = trimmed.startsWith("(") && trimmed.endsWith(")");
  const signed = parenthesized ? trimmed.slice(1, -1).trim() : trimmed;
  const percent = signed.endsWith("%");
  const numeric = signed
    .replace(/%$/u, "")
    .replace(/x$/iu, "")
    .replace(/^(?:[A-Z]{3}\s+|[$€£¥])/u, "")
    .replace(/[\s,]/gu, "");
  if (numeric === "") return null;
  const parsed = Number(numeric);
  if (!Number.isFinite(parsed)) return null;
  const scaled = percent ? parsed / 100 : parsed;
  return parenthesized ? -scaled : scaled;
}

function clipboardFields(text: string): string[][] {
  const normalized = text.replace(/\r\n?/gu, "\n");
  const rawRows = normalized.split("\n");
  while (rawRows.length > 1 && rawRows.at(-1) === "") rawRows.pop();
  const columns = rawRows.map((row) => row.split("\t"));
  const columnCount = columns[0]?.length ?? 0;
  if (
    columns.length === 0 ||
    columnCount === 0 ||
    columns.some((row) => row.length !== columnCount)
  ) {
    throw new Error("Paste a rectangular table of cells.");
  }
  return columns;
}

function parseClipboardCell(raw: string): XLMClipboardCell {
  const value = raw.trim();
  if (value === "") return { kind: "blank" };
  if (value.startsWith("=")) return { kind: "formula", formula: value };
  const parsed = parseClipboardNumber(value);
  if (parsed === null) {
    throw new Error(
      `“${value.slice(0, 40)}” is not a number, formula, or blank cell.`,
    );
  }
  return { kind: "number", value: parsed };
}

function parseClipboardFields(columns: string[][]): XLMClipboardMatrix {
  if (columns.length === 0 || (columns[0]?.length ?? 0) === 0) {
    throw new Error("Paste at least one cell.");
  }
  const cells = columns.map((row) => row.map(parseClipboardCell));
  const columnCount = cells[0]?.length ?? 0;
  return { cells, rowCount: cells.length, columnCount };
}

export function parseXLMClipboardText(text: string): XLMClipboardMatrix {
  return parseClipboardFields(clipboardFields(text));
}

function clipboardValueFields(text: string): string[][] {
  let fields = clipboardFields(text);
  const hasWorkbookHeader =
    fields[0]?.[0]?.trim().toLocaleLowerCase() === "line item" &&
    fields[0]?.[1]?.trim().toLocaleLowerCase() === "unit";
  if (hasWorkbookHeader) fields = fields.slice(1);
  const hasLineItemMetadata =
    fields.length > 0 &&
    fields.every((row) => {
      if (row.length < 3) return false;
      try {
        parseClipboardCell(row[0] ?? "");
        return false;
      } catch {
        return true;
      }
    });
  if (!hasLineItemMetadata) return fields;
  const values = fields.map((row) => row.slice(2));
  return hasWorkbookHeader
    ? values.filter((row) => row.some((value) => value.trim() !== ""))
    : values;
}

function xlmPasteOperations(
  text: string,
  rows: ViewerRow[],
  periods: XLMPeriodColumn[],
  selection: GridSelectionRange,
): XLMPasteResult {
  const source = parseClipboardFields(clipboardValueFields(text));
  const selectedRows = selection.endRow - selection.startRow + 1;
  const selectedColumns = selection.endPeriod - selection.startPeriod + 1;
  const sourceIsSingleCell = source.rowCount === 1 && source.columnCount === 1;
  const selectionIsSingleCell = selectedRows === 1 && selectedColumns === 1;
  if (
    !sourceIsSingleCell &&
    !selectionIsSingleCell &&
    (source.rowCount !== selectedRows || source.columnCount !== selectedColumns)
  ) {
    throw new Error(
      `The copied ${source.rowCount}×${source.columnCount} range does not match the selected ${selectedRows}×${selectedColumns} range.`,
    );
  }
  const targetRows = sourceIsSingleCell ? selectedRows : source.rowCount;
  const targetColumns = sourceIsSingleCell
    ? selectedColumns
    : source.columnCount;
  const range: GridSelectionRange = {
    startRow: selection.startRow,
    endRow: selection.startRow + targetRows - 1,
    startPeriod: selection.startPeriod,
    endPeriod: selection.startPeriod + targetColumns - 1,
  };
  if (range.endRow >= rows.length || range.endPeriod >= periods.length) {
    throw new Error("The pasted range extends beyond the model grid.");
  }

  const operations: XLMEditCellOperation[] = [];
  const undoOperations: XLMEditCellOperation[] = [];
  for (let rowOffset = 0; rowOffset < targetRows; rowOffset += 1) {
    const row = rows[range.startRow + rowOffset];
    if (!row || row.boundary !== null || !row.referenceable) {
      throw new Error("The pasted range includes a read-only model row.");
    }
    for (
      let columnOffset = 0;
      columnOffset < targetColumns;
      columnOffset += 1
    ) {
      const period = periods[range.startPeriod + columnOffset];
      if (!period)
        throw new Error("The pasted range includes an unknown period.");
      const sourceCell =
        source.cells[sourceIsSingleCell ? 0 : rowOffset]?.[
          sourceIsSingleCell ? 0 : columnOffset
        ];
      if (!sourceCell) throw new Error("The pasted range is incomplete.");
      const current = matchingValue(row.values, period);
      const address = xlmCellAddress(row, period);
      if (
        address.collection.endsWith("_reported_total") &&
        sourceCell.kind === "formula"
      ) {
        throw new Error("Breakdown reported totals accept only values.");
      }
      if (
        period.forecast &&
        sourceCell.kind === "number" &&
        !current?.comment?.trim()
      ) {
        throw new Error(
          `Add a forecast comment to ${row.label ?? row.node.name}, ${period.label} before pasting a hardcoded value.`,
        );
      }

      const nextValue = sourceCell.kind === "number" ? sourceCell.value : null;
      const nextFormula =
        sourceCell.kind === "formula" ? sourceCell.formula : null;
      if (
        (current?.value ?? null) === nextValue &&
        (current?.formula ?? null) === nextFormula
      ) {
        continue;
      }
      operations.push({
        kind: "edit_cell",
        cell: address,
        value: nextValue,
        formula: nextFormula,
      });
      undoOperations.push({
        kind: "edit_cell",
        cell: address,
        value: current?.value ?? null,
        formula: current?.formula ?? null,
      });
    }
  }
  if (operations.length === 0) {
    throw new Error("The pasted cells already match the model.");
  }
  return { operations, undoOperations, range };
}

function xlmClearOperations(
  rows: ViewerRow[],
  periods: XLMPeriodColumn[],
  range: GridSelectionRange,
): XLMPasteResult {
  const operations: XLMEditCellOperation[] = [];
  const undoOperations: XLMEditCellOperation[] = [];
  for (let rowIndex = range.startRow; rowIndex <= range.endRow; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!row || row.boundary !== null || !row.referenceable) {
      throw new Error("The selected range includes a read-only model row.");
    }
    for (
      let periodIndex = range.startPeriod;
      periodIndex <= range.endPeriod;
      periodIndex += 1
    ) {
      const period = periods[periodIndex];
      if (!period) {
        throw new Error("The selected range includes an unknown period.");
      }
      const current = matchingValue(row.values, period);
      if ((current?.value ?? null) === null && !current?.formula) continue;
      const cell = xlmCellAddress(row, period);
      operations.push({
        kind: "edit_cell",
        cell,
        value: null,
        formula: null,
      });
      undoOperations.push({
        kind: "edit_cell",
        cell,
        value: current?.value ?? null,
        formula: current?.formula ?? null,
      });
    }
  }
  if (operations.length === 0) {
    throw new Error("The selected cells are already blank.");
  }
  return { operations, undoOperations, range };
}

function expandedFillRange(
  source: GridSelectionRange,
  target: GridCellSelection,
): GridSelectionRange {
  return {
    startRow: Math.min(source.startRow, target.rowIndex),
    endRow: Math.max(source.endRow, target.rowIndex),
    startPeriod: Math.min(source.startPeriod, target.periodIndex),
    endPeriod: Math.max(source.endPeriod, target.periodIndex),
  };
}

function sameGridRange(
  left: GridSelectionRange,
  right: GridSelectionRange,
): boolean {
  return (
    left.startRow === right.startRow &&
    left.endRow === right.endRow &&
    left.startPeriod === right.startPeriod &&
    left.endPeriod === right.endPeriod
  );
}

function gridRangeReference(
  rows: ViewerRow[],
  periods: XLMPeriodColumn[],
  range: GridSelectionRange,
): ModelReference | null {
  const firstRow = rows[range.startRow];
  const lastRow = rows[range.endRow];
  const firstPeriod = periods[range.startPeriod];
  const lastPeriod = periods[range.endPeriod];
  if (!firstRow || !lastRow || !firstPeriod || !lastPeriod) return null;
  const start = modelReferenceCell(firstRow, firstPeriod);
  const end = modelReferenceCell(lastRow, lastPeriod);
  return {
    kind:
      range.startRow === range.endRow && range.startPeriod === range.endPeriod
        ? "cell"
        : "range",
    start,
    end,
  };
}

function xlmFillOperations(
  rows: ViewerRow[],
  periods: XLMPeriodColumn[],
  source: GridSelectionRange,
  target: GridCellSelection,
): XLMFillResult {
  const range = expandedFillRange(source, target);
  if (sameGridRange(range, source)) {
    throw new Error("Drag the fill handle beyond the selected formula.");
  }

  const sourceRows = source.endRow - source.startRow + 1;
  const sourcePeriods = source.endPeriod - source.startPeriod + 1;
  const operations: XLMEditCellOperation[] = [];
  const undoOperations: XLMEditCellOperation[] = [];
  for (let rowIndex = range.startRow; rowIndex <= range.endRow; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!row || row.boundary !== null || !row.referenceable) {
      throw new Error("The fill range includes a read-only model row.");
    }
    for (
      let periodIndex = range.startPeriod;
      periodIndex <= range.endPeriod;
      periodIndex += 1
    ) {
      if (
        rowIndex >= source.startRow &&
        rowIndex <= source.endRow &&
        periodIndex >= source.startPeriod &&
        periodIndex <= source.endPeriod
      ) {
        continue;
      }
      const period = periods[periodIndex];
      if (!period)
        throw new Error("The fill range includes an unknown period.");
      const sourceRowIndex =
        source.startRow +
        normalizedModulo(rowIndex - source.startRow, sourceRows);
      const sourcePeriodIndex =
        source.startPeriod +
        normalizedModulo(periodIndex - source.startPeriod, sourcePeriods);
      const sourceRow = rows[sourceRowIndex];
      const sourcePeriod = periods[sourcePeriodIndex];
      const sourceValue =
        sourceRow && sourcePeriod
          ? matchingValue(sourceRow.values, sourcePeriod)
          : undefined;
      if (!sourceRow?.referenceable || !sourcePeriod || !sourceValue?.formula) {
        throw new Error(
          "Select authored formula cells before using the fill handle.",
        );
      }

      const address = xlmCellAddress(row, period);
      if (address.collection.endsWith("_reported_total")) {
        throw new Error("Breakdown reported totals do not accept formulas.");
      }
      const formula = shiftXLMFormulaForFill(
        sourceValue.formula,
        sourcePeriod,
        period,
      );
      const current = matchingValue(row.values, period);
      if ((current?.value ?? null) === null && current?.formula === formula) {
        continue;
      }
      operations.push({
        kind: "edit_cell",
        cell: address,
        value: null,
        formula,
      });
      undoOperations.push({
        kind: "edit_cell",
        cell: address,
        value: current?.value ?? null,
        formula: current?.formula ?? null,
      });
    }
  }
  if (operations.length === 0) {
    throw new Error("The filled cells already match the model.");
  }
  return { operations, undoOperations, range };
}

/** Copy source for the toolbar action: every row and period in the current
 * cadence, regardless of search or collapsed presentation state. */
export function xlmWorkbookClipboardText(
  model: XLMModel,
  cadence: XLMCadence,
  mode: XLMClipboardMode = "formulas",
): string {
  const periods = displayedXLMPeriods(model, cadence);
  const rows = modelRows(model, cadence);
  return clipboardTableText(
    [
      ["Line item", "Unit", ...periods.map((period) => period.label)],
      ...rows.map((row) => {
        const unit = row.boundary
          ? ""
          : displayXLMUnit(row.unit, row.unitLabel, model.reporting_currency);
        return [
          row.label ?? row.node.name,
          unit === "—" ? "" : unit,
          ...periods.map((period) =>
            row.boundary ? "" : clipboardCellText(row, period, mode),
          ),
        ];
      }),
    ],
    mode === "formulas",
  );
}

type CellDisplayMode = "values" | "formulas";

function isHistoricallyFormulaDrivenPercentRow(
  model: XLMModel,
  row: ViewerRow,
): boolean {
  const unit = row.unit?.trim().toLocaleLowerCase();
  if (unit !== "percent" && unit !== "%") return false;
  const historicalValues = [
    ...(row.node.yearly_values ?? []).filter(
      (value) => !isForecast(model, "annual", value.year),
    ),
    ...(row.node.quarterly_values ?? []).filter(
      (value) => !isForecast(model, "quarterly", value.year, value.quarter),
    ),
    ...(row.node.half_year_values ?? []).filter(
      (value) => !isForecast(model, "half-year", value.year, value.half),
    ),
  ];
  let sawFormula = false;
  for (const value of historicalValues) {
    if (typeof value.value === "number") return false;
    if (value.formula) sawFormula = true;
  }
  return sawFormula;
}

function selectedCellForReference(
  model: XLMModel,
  reference: ModelReference,
): SelectedCell | null {
  if (reference.kind !== "cell") return null;
  const cadence = reference.start.period.cadence;
  const rows = modelRows(model, cadence);
  const periods = xlmPeriods(model, cadence);
  const row = rows.find(
    (candidate) =>
      candidate.selectable &&
      candidate.node.identifier === reference.start.identifier,
  );
  const period = periods.find((candidate) =>
    sameModelReferencePeriod(candidate, reference.start.period),
  );
  if (!row || !period) return null;
  const value =
    matchingValue(row.values, period) ??
    (row.selectable ? blankXLMValue(period) : undefined);
  return value ? { row, period, value } : null;
}

function cellDraft(value: XLMValue): CellDraft {
  return {
    value: typeof value.value === "number" ? String(value.value) : "",
    formula: value.formula ?? "",
    comment: value.comment ?? "",
    documentReferences: structuredClone(value.document_references ?? []),
  };
}

function nullableDraftText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function sameDocumentReferences(
  left: XLMDocumentReference[],
  right: XLMDocumentReference[],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function sameCellDraft(left: CellDraft, right: CellDraft): boolean {
  return (
    left.value === right.value &&
    left.formula === right.formula &&
    left.comment === right.comment &&
    sameDocumentReferences(left.documentReferences, right.documentReferences)
  );
}

function analyzeCellDraft(baseline: CellDraft, draft: CellDraft) {
  const parsedValue =
    draft.value.trim() === "" ? null : Number(draft.value.trim());
  const baselineValue =
    baseline.value.trim() === "" ? null : Number(baseline.value.trim());
  const valueValid = parsedValue === null || Number.isFinite(parsedValue);
  const formula = nullableDraftText(draft.formula);
  const baselineFormula = nullableDraftText(baseline.formula);
  const comment = nullableDraftText(draft.comment);
  const baselineComment = nullableDraftText(baseline.comment);
  const valueChanged = valueValid && parsedValue !== baselineValue;
  const formulaChanged = formula !== baselineFormula;
  const commentChanged = comment !== baselineComment;
  const referencesChanged = !sameDocumentReferences(
    draft.documentReferences,
    baseline.documentReferences,
  );

  return {
    parsedValue,
    baselineValue,
    valueValid,
    formula,
    baselineFormula,
    comment,
    baselineComment,
    valueChanged,
    formulaChanged,
    commentChanged,
    referencesChanged,
    dirty:
      !valueValid ||
      valueChanged ||
      formulaChanged ||
      commentChanged ||
      referencesChanged,
  };
}

function authoredCellText(draft: CellDraft): string {
  return draft.formula !== "" ? draft.formula : draft.value;
}

function updateAuthoredCellText(draft: CellDraft, text: string): CellDraft {
  return text.startsWith("=")
    ? { ...draft, value: "", formula: text }
    : { ...draft, value: text, formula: "" };
}

function formulaBarCellText(selected: SelectedCell): string {
  const authored = authoredCellText(cellDraft(selected.value));
  if (authored !== "") return authored;
  const computed = selected.value.computed_value;
  return typeof computed === "number" ? String(computed) : "";
}

function formulaBarSaveStatus(
  status: XLMCellSaveStatus,
  pendingCount: number,
): { label: string; title: string } | null {
  if (status === "saving") {
    return {
      label: pendingCount > 1 ? `Saving ${pendingCount}` : "Saving",
      title: "Edit applied locally. Saving in the background.",
    };
  }
  if (status === "saved") {
    return { label: "Saved", title: "Saved to the model revision." };
  }
  if (status === "failed") {
    return {
      label: "Save failed",
      title: "The edit remains applied locally. Retry the background save.",
    };
  }
  if (status === "conflict") {
    return {
      label: "Conflict",
      title: "The edit remains local until the revision conflict is resolved.",
    };
  }
  return null;
}

function FormulaBar({
  selected,
  model,
  editable,
  draft,
  dirty,
  validationError,
  saveStatus,
  pendingSaveCount,
  onDraftChange,
  onSubmit,
  onReset,
  onUndo,
  onRequestSave,
  inputRef,
  onExitEditing,
  onOpenFormulaReference,
}: {
  selected: SelectedCell | null;
  model: XLMModel;
  editable: boolean;
  draft: CellDraft | null;
  dirty: boolean;
  validationError: string | null;
  saveStatus: XLMCellSaveStatus;
  pendingSaveCount: number;
  onDraftChange: (update: (current: CellDraft) => CellDraft) => void;
  onSubmit: () => boolean;
  onReset: () => void;
  onUndo: () => void;
  onRequestSave: () => void;
  inputRef: { current: HTMLInputElement | null };
  onExitEditing: () => void;
  onOpenFormulaReference: (reference: ModelReference, inspect: boolean) => void;
}) {
  const [inputEditing, setInputEditing] = useState(false);
  const [caret, setCaret] = useState(0);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [autocompleteDismissed, setAutocompleteDismissed] = useState(false);
  const pendingAutocompleteCaret = useRef<number | null>(null);
  const suppressNextBlurSave = useRef(false);
  const status = formulaBarSaveStatus(saveStatus, pendingSaveCount);
  const label = selected
    ? (selected.row.label ?? selected.row.node.name)
    : null;
  const formula = draft?.formula ?? selected?.value.formula ?? "";
  const formulaParts =
    selected && formula
      ? modelFormulaParts(model, formula, selected.period)
      : [];
  const text = draft
    ? authoredCellText(draft)
    : selected
      ? formulaBarCellText(selected)
      : "";
  const autocompleteCaret = pendingAutocompleteCaret.current ?? caret;
  const autocompleteContext = formulaAutocompleteContext(
    text,
    autocompleteCaret,
  );
  const autocompleteSuggestions = useMemo(
    () =>
      formulaAutocompleteSuggestions(
        model,
        text,
        autocompleteCaret,
        selected?.period,
      ),
    [autocompleteCaret, model, selected?.period, text],
  );
  const autocompleteOpen =
    inputEditing &&
    !autocompleteDismissed &&
    autocompleteContext !== null &&
    autocompleteSuggestions.length > 0;
  const selectedSuggestionIndex = Math.min(
    activeSuggestion,
    Math.max(0, autocompleteSuggestions.length - 1),
  );
  const selectedSuggestion = autocompleteSuggestions[selectedSuggestionIndex];
  const autocompleteId = "statement-formula-autocomplete";
  useEffect(() => {
    setActiveSuggestion(0);
  }, [
    autocompleteContext?.kind,
    autocompleteContext?.query,
    selected?.period.label,
    selected?.row.key,
  ]);

  useBrowserLayoutEffect(() => {
    const nextCaret = pendingAutocompleteCaret.current;
    const input = inputRef.current;
    if (nextCaret === null || !input) return;
    input.focus({ preventScroll: true });
    input.setSelectionRange(nextCaret, nextCaret);
    setCaret(nextCaret);
    pendingAutocompleteCaret.current = null;
  }, [inputRef, text]);

  const acceptAutocomplete = (
    suggestion: FormulaAutocompleteSuggestion,
  ): void => {
    if (!autocompleteContext) return;
    const result = applyFormulaAutocomplete(
      text,
      autocompleteContext,
      suggestion,
    );
    pendingAutocompleteCaret.current = result.caret;
    setCaret(result.caret);
    setAutocompleteDismissed(suggestion.kind !== "line-item");
    onDraftChange((current) => updateAuthoredCellText(current, result.formula));
  };

  return (
    <section
      className="statement-formula-bar"
      aria-label="Formula bar"
      data-dirty={dirty ? "true" : undefined}
    >
      <div className="statement-formula-bar__name">
        {selected ? (
          <>
            <strong title={selected.row.node.identifier}>{label}</strong>
            <span>{selected.period.label}</span>
          </>
        ) : (
          <span>Select a cell</span>
        )}
      </div>
      <div className="statement-formula-bar__field">
        <span className="statement-formula-bar__fx" aria-hidden="true">
          fx
        </span>
        {editable && selected && draft ? (
          <input
            ref={inputRef}
            value={text}
            aria-label={`Formula or value for ${label}, ${selected.period.label}`}
            aria-invalid={validationError !== null}
            aria-autocomplete="list"
            aria-controls={autocompleteOpen ? autocompleteId : undefined}
            aria-expanded={autocompleteOpen}
            aria-activedescendant={
              autocompleteOpen && selectedSuggestion
                ? `${autocompleteId}-${selectedSuggestionIndex}`
                : undefined
            }
            aria-keyshortcuts="Enter Escape Control+Z Meta+Z"
            inputMode={draft.formula ? "text" : "decimal"}
            maxLength={20_000}
            placeholder="Enter a value or start a formula with ="
            spellCheck={false}
            onChange={(event) => {
              setCaret(
                event.target.selectionStart ?? event.target.value.length,
              );
              setAutocompleteDismissed(false);
              onDraftChange((current) =>
                updateAuthoredCellText(current, event.target.value),
              );
            }}
            onFocus={(event) => {
              setInputEditing(true);
              setAutocompleteDismissed(false);
              setCaret(
                event.currentTarget.selectionStart ??
                  event.currentTarget.value.length,
              );
            }}
            onBlur={() => {
              setInputEditing(false);
              if (suppressNextBlurSave.current) {
                suppressNextBlurSave.current = false;
                return;
              }
              onRequestSave();
            }}
            onSelect={(event) => {
              setCaret(
                pendingAutocompleteCaret.current ??
                  event.currentTarget.selectionStart ??
                  event.currentTarget.value.length,
              );
            }}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing || event.repeat) return;
              if (
                event.key.toLocaleLowerCase() === "z" &&
                (event.metaKey || event.ctrlKey) &&
                !event.altKey &&
                !event.shiftKey
              ) {
                event.preventDefault();
                onUndo();
                return;
              }
              if (autocompleteOpen && event.key === "ArrowDown") {
                event.preventDefault();
                setActiveSuggestion(
                  (selectedSuggestionIndex + 1) %
                    autocompleteSuggestions.length,
                );
                return;
              }
              if (autocompleteOpen && event.key === "ArrowUp") {
                event.preventDefault();
                setActiveSuggestion(
                  (selectedSuggestionIndex -
                    1 +
                    autocompleteSuggestions.length) %
                    autocompleteSuggestions.length,
                );
                return;
              }
              if (
                autocompleteOpen &&
                selectedSuggestion &&
                (event.key === "Tab" || event.key === "Enter")
              ) {
                event.preventDefault();
                acceptAutocomplete(selectedSuggestion);
                return;
              }
              if (autocompleteOpen && event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setAutocompleteDismissed(true);
                return;
              }
              if (event.key === "Enter") {
                event.preventDefault();
                suppressNextBlurSave.current = true;
                if (onSubmit()) {
                  onExitEditing();
                } else {
                  suppressNextBlurSave.current = false;
                }
              } else if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                onReset();
                onExitEditing();
              }
            }}
          />
        ) : null}
        <div
          className={`statement-formula-bar__pretty agent-cell-formula${
            selected ? "" : " is-placeholder"
          }${editable ? " is-editable" : ""}`}
          aria-label={
            formulaParts.length > 0
              ? "Formula with linked line-item names"
              : undefined
          }
          onClick={(event) => {
            if (
              !editable ||
              (event.target instanceof Element &&
                event.target.closest("button") !== null)
            ) {
              return;
            }
            const input = inputRef.current;
            if (!input) return;
            input.focus({ preventScroll: true });
            const end = input.value.length;
            input.setSelectionRange(end, end);
          }}
        >
          {formulaParts.length > 0 ? (
            <code>
              {formulaParts.map((part, index) =>
                part.kind === "reference" ? (
                  <button
                    type="button"
                    className="agent-cell-formula__reference"
                    key={`${index}:${part.raw}`}
                    aria-label={`Navigate to ${part.label}`}
                    title="Click to navigate · ⌘/Ctrl-click to inspect"
                    onClick={(event) =>
                      onOpenFormulaReference(
                        part.reference,
                        event.metaKey || event.ctrlKey,
                      )
                    }
                  >
                    {part.label}
                  </button>
                ) : (
                  <span key={`${index}:${part.text}`}>{part.text}</span>
                ),
              )}
            </code>
          ) : (
            <span>
              {selected ? text || "Blank cell" : "Select a cell to inspect it"}
            </span>
          )}
        </div>
        {status && (
          <span
            className="statement-formula-save-status"
            data-status={saveStatus}
            role="status"
            aria-live="polite"
            title={status.title}
          >
            <span
              className="statement-formula-save-status__dot"
              aria-hidden="true"
            />
            {status.label}
          </span>
        )}
        {autocompleteOpen && (
          <div
            id={autocompleteId}
            className="statement-formula-autocomplete"
            role="listbox"
            aria-label="Formula suggestions"
          >
            {autocompleteSuggestions.map((suggestion, index) => (
              <button
                id={`${autocompleteId}-${index}`}
                type="button"
                role="option"
                aria-selected={index === selectedSuggestionIndex}
                key={suggestion.key}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => acceptAutocomplete(suggestion)}
              >
                <span className="statement-formula-autocomplete__kind">
                  {suggestion.badge}
                </span>
                <strong>{suggestion.label}</strong>
                <code>{suggestion.detail}</code>
              </button>
            ))}
          </div>
        )}
      </div>
      {validationError && (
        <p className="statement-formula-bar__error" role="alert">
          {validationError}
        </p>
      )}
    </section>
  );
}

function BreakdownDetails({
  editor,
  reportingCurrency,
  disabled,
  onChange,
  onSave,
  onClose,
}: {
  editor: XLMBreakdownEditorState;
  reportingCurrency: string | null | undefined;
  disabled: boolean;
  onChange: (
    update: (current: XLMBreakdownEditorState) => XLMBreakdownEditorState,
  ) => void;
  onSave: () => Promise<boolean>;
  onClose: () => void;
}) {
  const source = editor.node.breakdown;
  const dirty = Boolean(
    source &&
    (editor.name.trim() !== editor.node.name ||
      editor.unit.trim() !==
        (editor.node.unit?.trim() ?? source.unit?.trim() ?? "") ||
      editor.description.trim() !== (editor.node.description?.trim() ?? "") ||
      editor.renderTotal !== (source.render_total !== false) ||
      editor.renderGrowth !== (source.render_growth === true)),
  );
  const saveSignature = JSON.stringify([
    editor.name.trim(),
    editor.unit.trim(),
    editor.description.trim(),
    editor.renderTotal,
    editor.renderGrowth,
  ]);
  const [saving, setSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<"idle" | "saved" | "failed">(
    "idle",
  );
  const lastSavedSignature = useRef(saveSignature);
  const lastFailedSignature = useRef<string | null>(null);
  useEffect(() => {
    if (
      saveSignature !== lastSavedSignature.current &&
      saveSignature !== lastFailedSignature.current
    ) {
      setSaveOutcome("idle");
    }
  }, [saveSignature]);
  const submit = useCallback(async (): Promise<boolean> => {
    if (!dirty || lastSavedSignature.current === saveSignature) return true;
    if (editor.name.trim() === "" || disabled || saving) return false;
    setSaving(true);
    const saved = await onSave();
    if (saved) {
      lastSavedSignature.current = saveSignature;
      lastFailedSignature.current = null;
      setSaveOutcome("saved");
    } else {
      lastFailedSignature.current = saveSignature;
      setSaveOutcome("failed");
    }
    setSaving(false);
    return saved;
  }, [dirty, disabled, editor.name, onSave, saveSignature, saving]);
  useEffect(() => {
    if (
      !dirty ||
      editor.name.trim() === "" ||
      disabled ||
      saving ||
      lastSavedSignature.current === saveSignature ||
      lastFailedSignature.current === saveSignature
    ) {
      return;
    }
    const timeout = window.setTimeout(() => {
      void submit();
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [dirty, disabled, editor.name, saveSignature, saving, submit]);
  const close = () => {
    if (dirty && editor.name.trim() !== "" && !disabled && !saving) {
      void submit().then((saved) => {
        if (saved) onClose();
      });
      return;
    }
    if (!dirty) onClose();
  };
  return (
    <aside
      className="agent-cell-sidepeek agent-breakdown-sidepeek"
      aria-labelledby="agent-breakdown-sidepeek-title"
    >
      <header>
        <div>
          <span className="eyebrow">Breakdown details</span>
          <h2 id="agent-breakdown-sidepeek-title">
            {editor.name.trim() || editor.node.name}
          </h2>
        </div>
        <div className="agent-cell-sidepeek__actions">
          <button
            type="button"
            aria-label="Close breakdown details"
            title="Close breakdown details"
            disabled={disabled || saving}
            onClick={close}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </header>
      <form
        className="agent-line-item-sidepeek__form agent-breakdown-sidepeek__form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p>
          This group contains {source?.items.length ?? 0} authored line items.
        </p>
        <label>
          <span>Name</span>
          <TextInput
            autoFocus
            aria-label="Breakdown name"
            value={editor.name}
            maxLength={2_000}
            disabled={disabled || saving}
            aria-invalid={editor.name.trim() === ""}
            onValueChange={(name) =>
              onChange((current) => ({ ...current, name }))
            }
          />
        </label>
        <label>
          <span>Unit</span>
          <RowUnitSelector
            value={editor.unit}
            disabled={disabled || saving}
            onValueChange={(unit) =>
              onChange((current) => ({ ...current, unit }))
            }
          />
        </label>
        <label>
          <span>Currency</span>
          <span className="agent-line-item-sidepeek__readonly">
            {reportingCurrency?.trim() || "—"}
          </span>
        </label>
        <TextArea
          label="Description"
          aria-label="Breakdown description"
          value={editor.description}
          maxLength={4_000}
          disabled={disabled || saving}
          rows={4}
          onValueChange={(description) =>
            onChange((current) => ({ ...current, description }))
          }
        />
        <div className="xlm-breakdown-editor__options">
          <Checkbox
            checked={editor.renderTotal}
            disabled={disabled || saving}
            label={
              <span>
                <strong>Total row</strong>
                <small>Show the generated breakdown total.</small>
              </span>
            }
            onCheckedChange={(renderTotal) =>
              onChange((current) => ({ ...current, renderTotal }))
            }
          />
          <Checkbox
            checked={editor.renderGrowth}
            disabled={disabled || saving}
            label={
              <span>
                <strong>Growth rows</strong>
                <small>Show generated growth rates for each line item.</small>
              </span>
            }
            onCheckedChange={(renderGrowth) =>
              onChange((current) => ({ ...current, renderGrowth }))
            }
          />
        </div>
        <small className="agent-line-item-sidepeek__save-status" role="status">
          {disabled || saving
            ? "Saving…"
            : saveOutcome === "saved"
              ? "Saved"
              : saveOutcome === "failed"
                ? "Couldn't save. Changes kept."
                : "Changes save automatically"}
        </small>
      </form>
    </aside>
  );
}

function LineItemDetails({
  row,
  model,
  target,
  onSave,
  onClose,
}: {
  row: ViewerRow;
  model: XLMModel;
  target: XLMAuthoredRowTarget;
  onSave: (operations: XLMEditOperation[]) => Promise<boolean>;
  onClose: () => void;
}) {
  const [baseline, setBaseline] = useState(() => ({
    name: target.node.name,
    type: target.node.type,
    unit: target.node.unit?.trim() ?? "",
    description: target.node.description?.trim() ?? "",
  }));
  const [name, setName] = useState(baseline.name);
  const [type, setType] = useState(baseline.type);
  const [unit, setUnit] = useState(baseline.unit);
  const [description, setDescription] = useState(baseline.description);
  const [saving, setSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<"idle" | "saved" | "failed">(
    "idle",
  );
  const reportingCurrency = model.reporting_currency?.trim() || "—";
  const nextName = name.trim();
  const nextDescription = description.trim();
  const nextUnit = XLM_VALUE_ROW_TYPES.has(type) ? unit.trim() : "";
  const dirty =
    nextName !== baseline.name ||
    nextDescription !== baseline.description ||
    (target.kind === "node" &&
      (type !== baseline.type || nextUnit !== baseline.unit));
  const saveSignature = JSON.stringify([
    nextName,
    type,
    nextUnit,
    nextDescription,
  ]);
  const lastSavedSignature = useRef(saveSignature);
  const lastFailedSignature = useRef<string | null>(null);
  useEffect(() => {
    if (
      saveSignature !== lastSavedSignature.current &&
      saveSignature !== lastFailedSignature.current
    ) {
      setSaveOutcome("idle");
    }
  }, [saveSignature]);
  const submit = useCallback(async (): Promise<boolean> => {
    if (!dirty || lastSavedSignature.current === saveSignature) return true;
    if (nextName === "" || saving) return false;
    const operation: XLMEditOperation =
      target.kind === "breakdown_item"
        ? {
            kind: "edit_breakdown_item",
            breakdown_identifier: target.breakdownIdentifier,
            identifier: target.node.identifier,
            ...(nextName !== baseline.name ? { name: nextName } : {}),
            ...(nextDescription !== baseline.description
              ? { description: nextDescription || null }
              : {}),
          }
        : {
            kind: "edit_node",
            identifier: target.node.identifier,
            ...(nextName !== baseline.name ? { name: nextName } : {}),
            ...(type !== baseline.type ? { type } : {}),
            ...(nextDescription !== baseline.description
              ? { description: nextDescription || null }
              : {}),
            ...(nextUnit !== baseline.unit ? { unit: nextUnit || null } : {}),
          };
    setSaving(true);
    const saved = await onSave([operation]);
    if (saved) {
      lastSavedSignature.current = saveSignature;
      lastFailedSignature.current = null;
      setBaseline({
        name: nextName,
        type,
        unit: nextUnit,
        description: nextDescription,
      });
      setSaveOutcome("saved");
    } else {
      lastFailedSignature.current = saveSignature;
      setSaveOutcome("failed");
    }
    setSaving(false);
    return saved;
  }, [
    baseline,
    dirty,
    nextDescription,
    nextName,
    nextUnit,
    onSave,
    saveSignature,
    saving,
    target,
    type,
  ]);
  useEffect(() => {
    if (
      !dirty ||
      nextName === "" ||
      saving ||
      lastSavedSignature.current === saveSignature ||
      lastFailedSignature.current === saveSignature
    ) {
      return;
    }
    const timeout = window.setTimeout(() => {
      void submit();
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [dirty, nextName, saveSignature, saving, submit]);
  const close = () => {
    if (dirty && nextName !== "" && !saving) {
      void submit().then((saved) => {
        if (saved) onClose();
      });
      return;
    }
    if (!dirty) onClose();
  };
  return (
    <aside
      className="agent-cell-sidepeek agent-line-item-sidepeek"
      aria-labelledby="agent-line-item-sidepeek-title"
    >
      <header>
        <div>
          <span className="eyebrow">Line item details</span>
          <h2 id="agent-line-item-sidepeek-title">
            {nextName || row.node.name}
          </h2>
        </div>
        <div className="agent-cell-sidepeek__actions">
          <button
            type="button"
            onClick={close}
            disabled={saving}
            aria-label="Close line item details"
            title="Close line item details"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </header>
      <form
        className="agent-line-item-sidepeek__form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label>
          <span>Name</span>
          <TextInput
            autoFocus
            aria-label="Line item name"
            value={name}
            maxLength={2_000}
            disabled={saving}
            aria-invalid={nextName === ""}
            onValueChange={setName}
          />
        </label>
        <label>
          <span>Type</span>
          {target.kind === "node" ? (
            <Selector
              ariaLabel="Line item type"
              options={XLM_ROW_TYPE_OPTIONS}
              value={type}
              disabled={saving}
              portalMenu
              onValueChange={setType}
            />
          ) : (
            <span className="agent-line-item-sidepeek__readonly">
              {rowTypeLabel(type)}
            </span>
          )}
        </label>
        <label>
          <span>Unit</span>
          {target.kind === "node" && XLM_VALUE_ROW_TYPES.has(type) ? (
            <RowUnitSelector
              value={unit}
              disabled={saving}
              onValueChange={setUnit}
            />
          ) : (
            <span className="agent-line-item-sidepeek__readonly">
              {rowUnitDetailLabel(row)}
            </span>
          )}
        </label>
        <label>
          <span>Currency</span>
          <span className="agent-line-item-sidepeek__readonly">
            {reportingCurrency}
          </span>
        </label>
        <TextArea
          label="Description"
          aria-label="Line item description"
          value={description}
          maxLength={4_000}
          disabled={saving}
          rows={4}
          onValueChange={setDescription}
        />
        <small className="agent-line-item-sidepeek__save-status" role="status">
          {saving
            ? "Saving…"
            : saveOutcome === "saved"
              ? "Saved"
              : saveOutcome === "failed"
                ? "Couldn't save. Changes kept."
                : "Changes save automatically"}
        </small>
      </form>
    </aside>
  );
}

function CellDetails({
  selected,
  model,
  companyId,
  documents,
  onOpenFormulaReference,
  collapsed,
  onCollapsedChange,
  editable,
  dirty,
  draft,
  onDraftChange,
  onRequestSave,
  editRequest,
  onOpenReview,
}: {
  selected: SelectedCell;
  model: XLMModel;
  companyId: string;
  documents: AgentStoredDocument[];
  onOpenFormulaReference: (reference: ModelReference, inspect: boolean) => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  editable: boolean;
  dirty: boolean;
  draft: CellDraft | null;
  onDraftChange: (update: (current: CellDraft) => CellDraft) => void;
  onRequestSave: () => void;
  editRequest: number;
  onOpenReview?: (tab: XLMReviewTab, recordId: string) => void;
}) {
  const [commentEditing, setCommentEditing] = useState(false);
  useEffect(() => {
    if (editRequest > 0) setCommentEditing(true);
  }, [editRequest]);
  const formula = selected.value.formula;
  const formulaParts = formula
    ? modelFormulaParts(model, formula, selected.period)
    : [];
  const unitLabel = displayXLMUnit(selected.row.unit, selected.row.unitLabel);
  const headerContext =
    unitLabel === "—"
      ? selected.period.label
      : `${selected.period.label} · ${unitLabel}`;
  const reviewLinks = xlmCellReviewLinks(
    model,
    xlmCellAddress(selected.row, selected.period),
  );
  if (collapsed) {
    return (
      <aside
        className="agent-cell-sidepeek agent-cell-sidepeek--collapsed"
        aria-label="Cell details"
      >
        <button
          type="button"
          className="agent-cell-sidepeek__expand"
          aria-label="Expand cell details"
          title="Expand cell details"
          onClick={() => onCollapsedChange(false)}
        >
          <span aria-hidden="true">‹</span>
        </button>
      </aside>
    );
  }
  return (
    <aside
      className="agent-cell-sidepeek"
      aria-labelledby="agent-cell-sidepeek-title"
    >
      <header>
        <div>
          <span className="eyebrow">{headerContext}</span>
          <h2 id="agent-cell-sidepeek-title">
            {selected.row.label ?? selected.row.node.name}
          </h2>
        </div>
        <div className="agent-cell-sidepeek__actions">
          <button
            type="button"
            onClick={() => {
              if (dirty) onRequestSave();
              setCommentEditing(false);
              onCollapsedChange(true);
            }}
            aria-label="Collapse cell details"
            title="Collapse cell details"
          >
            <span aria-hidden="true">›</span>
          </button>
        </div>
      </header>
      <dl className="definition-list">
        <div>
          <dt>Displayed</dt>
          <dd>
            {displayXLMValue(
              selected.value,
              selected.row.unit ?? selected.row.unitLabel,
            )}
          </dd>
        </div>
        {formula && (
          <div>
            <dt>Formula</dt>
            <dd className="agent-cell-formula">
              {formulaParts.map((part, index) =>
                part.kind === "reference" ? (
                  <button
                    type="button"
                    className="agent-cell-formula__reference"
                    key={`${index}:${part.raw}`}
                    aria-label={`Navigate to ${part.label}`}
                    title="Click to navigate · ⌘/Ctrl-click to inspect"
                    onClick={(event) =>
                      onOpenFormulaReference(
                        part.reference,
                        event.metaKey || event.ctrlKey,
                      )
                    }
                  >
                    {part.label}
                  </button>
                ) : (
                  <span key={`${index}:${part.text}`}>{part.text}</span>
                ),
              )}
            </dd>
          </div>
        )}
        <div className="agent-cell-comment-row">
          <dt>Comment</dt>
          <dd>
            {commentEditing && editable && draft ? (
              <TextArea
                className="agent-cell-comment-editor"
                label="Comment"
                maxLength={20_000}
                rows={3}
                value={draft.comment}
                autoFocus
                onBlur={() => {
                  onRequestSave();
                  setCommentEditing(false);
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Escape") return;
                  event.preventDefault();
                  onRequestSave();
                  setCommentEditing(false);
                }}
                onValueChange={(commentValue) =>
                  onDraftChange((current) => ({
                    ...current,
                    comment: commentValue,
                  }))
                }
              />
            ) : (
              <div
                className={`agent-cell-comment${editable && draft ? " is-editable" : ""}`}
                role={editable && draft ? "button" : undefined}
                tabIndex={editable && draft ? 0 : undefined}
                aria-label={editable && draft ? "Edit comment" : undefined}
                title={
                  editable && draft ? "Double-click to edit comment" : undefined
                }
                onDoubleClick={() => {
                  if (editable && draft) setCommentEditing(true);
                }}
                onKeyDown={(event) => {
                  if (
                    editable &&
                    draft &&
                    (event.key === "Enter" || event.key === "F2")
                  ) {
                    event.preventDefault();
                    setCommentEditing(true);
                  }
                }}
              >
                {draft?.comment || selected.value.comment || "—"}
              </div>
            )}
          </dd>
        </div>
      </dl>
      {onOpenReview && reviewLinks.length > 0 && (
        <section
          className="agent-cell-review-links"
          aria-label="Related reviews"
        >
          <header>
            <h3>Related reviews</h3>
            <span>{reviewLinks.length}</span>
          </header>
          <div>
            {reviewLinks.map((link) => (
              <Button
                key={`${link.tab}:${link.recordId}`}
                type="button"
                tone="quiet"
                size="xs"
                iconRight={<OpenIcon />}
                onClick={() => onOpenReview(link.tab, link.recordId)}
              >
                <span>
                  <strong>{link.label}</strong>
                  <small>{link.detail}</small>
                </span>
              </Button>
            ))}
          </div>
        </section>
      )}
      <section className="agent-cell-evidence-section">
        <XLMDocumentEvidence
          companyId={companyId}
          documents={documents}
          references={
            editable && draft
              ? draft.documentReferences
              : (selected.value.document_references ?? [])
          }
          focusTerms={[
            selected.row.label ?? "",
            selected.row.node.name,
            selected.row.node.identifier.replaceAll("_", " "),
          ]}
          editing={editable && Boolean(draft)}
          onChange={(documentReferences) => {
            if (!editable || !draft) return;
            onDraftChange((current) => ({
              ...current,
              documentReferences,
            }));
            onRequestSave();
          }}
        />
      </section>
    </aside>
  );
}

export default function AgentXLMViewer({
  model: sourceModel,
  leadingActions,
  actions,
  companyId,
  documents,
  reference = null,
  inspectReference = false,
  onClearReference,
  onOpenReference,
  revision,
  saveStatus = "idle",
  pendingSaveCount = 0,
  onSaveCell,
  onSaveOperations,
  onUndoStructural,
  allowQueuedRowMoves = false,
  pasteLocked = false,
  onOpenHistory,
  onEditorDirtyChange,
  showFullScreenControl = true,
  onOpenReview,
}: {
  model: XLMModel;
  leadingActions?: ReactNode;
  actions?: ReactNode;
  companyId: string;
  documents: AgentStoredDocument[];
  reference?: ModelReference | null;
  inspectReference?: boolean;
  onClearReference?: () => void;
  onOpenReference?: (reference: ModelReference) => void;
  revision?: number;
  saveStatus?: XLMCellSaveStatus;
  pendingSaveCount?: number;
  onSaveCell?: (operation: XLMEditCellOperation) => boolean;
  onSaveOperations?: (
    operations: XLMEditOperation[],
    options?: XLMStructuralSaveOptions,
  ) => Promise<boolean>;
  onUndoStructural?: () => Promise<boolean>;
  allowQueuedRowMoves?: boolean;
  pasteLocked?: boolean;
  onOpenHistory?: () => void;
  onEditorDirtyChange?: (dirty: boolean) => void;
  showFullScreenControl?: boolean;
  onOpenReview?: (tab: XLMReviewTab, recordId: string) => void;
}) {
  const [pendingRowMoves, setPendingRowMoves] = useState<XLMPendingRowMove[]>(
    [],
  );
  const pendingRowMoveId = useRef(0);
  const canQueueRowMove = allowQueuedRowMoves || pendingRowMoves.length > 0;
  const model = useMemo(() => {
    let projected = sourceModel;
    for (const pending of pendingRowMoves) {
      projected =
        applyOptimisticXLMStructuralModel(projected, [pending.operation]) ??
        projected;
    }
    return projected;
  }, [pendingRowMoves, sourceModel]);
  const available = useMemo<XLMCadence[]>(() => {
    const result: XLMCadence[] = [];
    if (model.fiscal_years.length > 0) result.push("annual");
    if (model.quarters.length > 0) result.push("quarterly");
    if (model.half_years.length > 0) result.push("half-year");
    return result;
  }, [model]);
  const [choice, setChoice] = useState<XLMCadence>(available[0] ?? "annual");
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  const [cellEditor, setCellEditor] = useState<ActiveCellDraft | null>(null);
  const [sidepeekVisible, setSidepeekVisible] = useState(false);
  const [sidepeekCollapsed, setSidepeekCollapsed] = useState(false);
  const [rowDetailsKey, setRowDetailsKey] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    new Set<string>(),
  );
  const [cellDisplayMode, setCellDisplayMode] =
    useState<CellDisplayMode>("values");
  const [statementZoomPercent, setStatementZoomPercent] = useState(100);
  const [fullScreen, setFullScreen] = useState(false);
  const [localReference, setLocalReference] = useState<ModelReference | null>(
    null,
  );
  const [selectionPreview, setSelectionPreview] =
    useState<ModelReference | null>(null);
  const [rowSelection, setRowSelection] = useState<GridSelectionRange | null>(
    null,
  );
  const [autoSaveRequest, setAutoSaveRequest] = useState(0);
  const [rowEditor, setRowEditor] = useState<XLMRowEditorState | null>(null);
  const [rowEditCommitRequest, setRowEditCommitRequest] = useState(0);
  const [breakdownEditor, setBreakdownEditor] =
    useState<XLMBreakdownEditorState | null>(null);
  const [rowDrag, setRowDrag] = useState<XLMRowDragState | null>(null);
  const [rowDeleteTargets, setRowDeleteTargets] = useState<
    XLMAuthoredRowTarget[]
  >([]);
  const [contextMenu, setContextMenu] =
    useState<StatementContextMenuState | null>(null);
  const [commentEditRequest, setCommentEditRequest] = useState(0);
  const selectionAnchor = useRef<GridCellSelection | null>(null);
  const rowSelectionAnchor = useRef<number | null>(null);
  const dragSelection = useRef<DragSelection | null>(null);
  const fillDrag = useRef<FillDrag | null>(null);
  const handledPointerClick = useRef(false);
  const directSelectionTarget = useRef<string | null>(null);
  const appliedExternalReference = useRef<string | null>(null);
  const formulaInputRef = useRef<HTMLInputElement | null>(null);
  const cellControlRefs = useRef(new Map<string, HTMLButtonElement>());
  const pendingCellFocus = useRef<{
    cellKey: string;
    visibleRowIndex: number;
  } | null>(null);
  const pendingFormulaFocus = useRef<{
    cellKey: string;
    selectAll: boolean;
  } | null>(null);
  const pendingFormulaCaret = useRef<number | null>(null);
  const undoStack = useRef<XLMUndoEntry[]>([]);
  const undoingRevision = useRef(false);
  const handledAutoSaveRequest = useRef(0);
  const handledRowEditCommitRequest = useRef(0);
  useEffect(() => {
    if (commentEditRequest === 0) return;
    const timeout = window.setTimeout(() => setCommentEditRequest(0), 0);
    return () => window.clearTimeout(timeout);
  }, [commentEditRequest]);
  const canEdit = Boolean(onSaveCell);
  const canEditRows = Boolean(onSaveOperations);
  const saveCell = useCallback(
    (operation: XLMEditCellOperation) => onSaveCell?.(operation) ?? false,
    [onSaveCell],
  );
  const saveStructuralOperations = useCallback(
    async (
      operations: XLMEditOperation[],
      options?: XLMStructuralSaveOptions,
    ): Promise<boolean> => {
      if (!onSaveOperations) return false;
      const saved = options
        ? await onSaveOperations(operations, options)
        : await onSaveOperations(operations);
      if (saved) undoStack.current.push({ kind: "structural" });
      return saved;
    },
    [onSaveOperations],
  );
  const saveSidePeekOperations = useCallback(
    (operations: XLMEditOperation[]) =>
      saveStructuralOperations(operations, { optimistic: false }),
    [saveStructuralOperations],
  );
  const selectedCellKey = selected
    ? xlmCellAddressKey(selected.row, selected.period)
    : null;
  const selectedReferenceKey = selected
    ? modelReferenceTarget({
        kind: "cell",
        start: modelReferenceCell(selected.row, selected.period),
        end: modelReferenceCell(selected.row, selected.period),
      })
    : null;
  const activeCellEditor =
    canEdit &&
    selected?.row.referenceable &&
    cellEditor?.cellKey === selectedCellKey
      ? cellEditor
      : null;
  const activeCellAnalysis = activeCellEditor
    ? analyzeCellDraft(activeCellEditor.baseline, activeCellEditor.draft)
    : null;
  const editorDirty = Boolean(activeCellAnalysis?.dirty);
  useEffect(() => {
    if (!canEdit || !selected || !selected.row.referenceable) {
      setCellEditor(null);
      return;
    }
    const cellKey = xlmCellAddressKey(selected.row, selected.period);
    setCellEditor((current) => {
      const initialDraft = cellDraft(selected.value);
      if (current?.cellKey === cellKey) {
        if (analyzeCellDraft(current.baseline, current.draft).dirty) {
          return current;
        }
        if (
          sameCellDraft(current.baseline, initialDraft) &&
          sameCellDraft(current.draft, initialDraft)
        ) {
          return current;
        }
      }
      return {
        cellKey,
        baseline: structuredClone(initialDraft),
        draft: initialDraft,
        validationError: null,
      };
    });
  }, [canEdit, selected, selectedCellKey]);

  const updateCellDraft = useCallback(
    (update: (current: CellDraft) => CellDraft) => {
      setCellEditor((current) => {
        if (!current || current.cellKey !== selectedCellKey) return current;
        return {
          ...current,
          draft: update(current.draft),
          validationError: null,
        };
      });
    },
    [selectedCellKey],
  );

  const resetCellDraft = useCallback(() => {
    setCellEditor((current) => {
      if (!current || current.cellKey !== selectedCellKey) return current;
      return {
        ...current,
        draft: structuredClone(current.baseline),
        validationError: null,
      };
    });
  }, [selectedCellKey]);

  const submitCellDraft = useCallback((): boolean => {
    if (!selected || !activeCellEditor) return false;
    const analysis = analyzeCellDraft(
      activeCellEditor.baseline,
      activeCellEditor.draft,
    );
    const setValidationError = (validationError: string | null) => {
      setCellEditor((current) =>
        current?.cellKey === activeCellEditor.cellKey
          ? { ...current, validationError }
          : current,
      );
    };
    if (!analysis.valueValid) {
      setValidationError("Value must be a finite number or blank.");
      return false;
    }
    if (analysis.formula !== null && !analysis.formula.startsWith("=")) {
      setValidationError("Formula must start with = or be blank.");
      return false;
    }
    const effectiveFormula = analysis.formulaChanged
      ? analysis.formula
      : analysis.baselineFormula;
    const effectiveValue = analysis.valueChanged
      ? analysis.parsedValue
      : analysis.baselineValue;
    const effectiveComment = analysis.commentChanged
      ? analysis.comment
      : analysis.baselineComment;
    const address = xlmCellAddress(selected.row, selected.period);
    if (
      address.collection.endsWith("_reported_total") &&
      effectiveFormula !== null
    ) {
      setValidationError("Breakdown reported totals accept only a value.");
      return false;
    }
    if (
      (analysis.valueChanged || analysis.formulaChanged) &&
      effectiveValue !== null &&
      effectiveFormula !== null
    ) {
      setValidationError(
        "A cell cannot keep both an authored value and a formula.",
      );
      return false;
    }
    if (
      selected.period.forecast &&
      effectiveValue !== null &&
      effectiveFormula === null &&
      effectiveComment === null
    ) {
      setValidationError("Forecast hardcoded values require a comment.");
      return false;
    }
    if (!analysis.dirty) return false;

    const operation: XLMEditCellOperation = {
      kind: "edit_cell",
      cell: address,
      ...(analysis.valueChanged ? { value: analysis.parsedValue } : {}),
      ...(analysis.formulaChanged ? { formula: analysis.formula } : {}),
      ...(analysis.commentChanged ? { comment: analysis.comment } : {}),
      ...(analysis.referencesChanged
        ? {
            document_references: activeCellEditor.draft.documentReferences,
          }
        : {}),
    };
    const undoEntry: CellUndoEntry = {
      kind: "cell",
      cellKey: activeCellEditor.cellKey,
      draft: structuredClone(activeCellEditor.baseline),
      operation: {
        kind: "edit_cell",
        cell: address,
        ...(analysis.valueChanged ? { value: analysis.baselineValue } : {}),
        ...(analysis.formulaChanged
          ? { formula: analysis.baselineFormula }
          : {}),
        ...(analysis.commentChanged
          ? { comment: analysis.baselineComment }
          : {}),
        ...(analysis.referencesChanged
          ? {
              document_references: structuredClone(
                activeCellEditor.baseline.documentReferences,
              ),
            }
          : {}),
      },
    };
    setValidationError(null);
    if (!saveCell(operation)) return false;
    undoStack.current.push(undoEntry);
    const saved = structuredClone(activeCellEditor.draft);
    setCellEditor((current) =>
      current?.cellKey === activeCellEditor.cellKey
        ? {
            ...current,
            baseline: structuredClone(saved),
            draft: saved,
            validationError: null,
          }
        : current,
    );
    return true;
  }, [activeCellEditor, saveCell, selected]);
  const undoCellEdit = useCallback((): boolean => {
    if (editorDirty) {
      resetCellDraft();
      return true;
    }
    const entry = undoStack.current.at(-1);
    if (!entry) return false;
    if (entry.kind === "structural") {
      if (!onUndoStructural || undoingRevision.current) return false;
      undoingRevision.current = true;
      void onUndoStructural()
        .then((saved) => {
          if (saved && undoStack.current.at(-1) === entry) {
            undoStack.current.pop();
          }
        })
        .finally(() => {
          undoingRevision.current = false;
        });
      return true;
    }
    if (entry.kind === "batch") {
      if (!onSaveOperations || undoingRevision.current) return false;
      undoingRevision.current = true;
      void onSaveOperations(entry.operations)
        .then((saved) => {
          if (saved && undoStack.current.at(-1) === entry) {
            undoStack.current.pop();
          }
        })
        .finally(() => {
          undoingRevision.current = false;
        });
      return true;
    }
    if (!saveCell(entry.operation)) return false;
    if (undoStack.current.at(-1) === entry) undoStack.current.pop();
    setCellEditor((current) =>
      current?.cellKey === entry.cellKey
        ? {
            ...current,
            baseline: structuredClone(entry.draft),
            draft: structuredClone(entry.draft),
            validationError: null,
          }
        : current,
    );
    return true;
  }, [
    editorDirty,
    onSaveOperations,
    onUndoStructural,
    resetCellDraft,
    saveCell,
  ]);
  const requestAutoSave = useCallback(() => {
    setAutoSaveRequest((current) => current + 1);
  }, []);
  useEffect(() => {
    if (autoSaveRequest === handledAutoSaveRequest.current) return;
    handledAutoSaveRequest.current = autoSaveRequest;
    if (editorDirty) submitCellDraft();
  }, [autoSaveRequest, editorDirty, submitCellDraft]);
  const externalReferenceKey = reference
    ? modelReferenceTarget(reference)
    : null;
  const committedReference = localReference ?? reference;
  const activeReference = selectionPreview ?? committedReference;
  const referenceKey = committedReference
    ? modelReferenceTarget(committedReference)
    : null;
  const referencedCadence = activeReference?.start.period.cadence;
  const cadence =
    referencedCadence && available.includes(referencedCadence)
      ? referencedCadence
      : available.includes(choice)
        ? choice
        : (available[0] ?? "annual");
  const periods = useMemo(
    () => displayedXLMPeriods(model, cadence),
    [model, cadence],
  );
  const figureColorModel = useMemo(() => xlmFigureColorContext(model), [model]);
  const rows = useMemo(
    () => modelRows(model, cadence, canEdit),
    [canEdit, cadence, model],
  );
  const rowDetails = useMemo(
    () =>
      rowDetailsKey === null
        ? null
        : (rows.find((row) => row.key === rowDetailsKey) ?? null),
    [rowDetailsKey, rows],
  );
  const rowDetailsTarget = useMemo(
    () => (rowDetails ? authoredRowTarget(model, rowDetails) : null),
    [model, rowDetails],
  );
  const rowIndexes = useMemo(
    () => new Map(rows.map((row, index) => [row.key, index])),
    [rows],
  );
  const selectedAuthoredRowTargets = useMemo(
    () => authoredRowTargetsForSelection(model, rows, rowSelection),
    [model, rowSelection, rows],
  );
  const referenceRange = useMemo(
    () => gridReferenceRange(rows, periods, activeReference, cadence),
    [activeReference, cadence, periods, rows],
  );
  const displaySelectionRange = selectionPreview
    ? referenceRange
    : (rowSelection ?? referenceRange);
  const committedReferenceRange = useMemo(
    () => gridReferenceRange(rows, periods, committedReference, cadence),
    [cadence, committedReference, periods, rows],
  );
  const formulaDependencyCellKeys = useMemo(() => {
    const formula = activeCellEditor?.draft.formula ?? selected?.value.formula;
    if (!selected || !formula) return new Set<string>();
    const cellKeys = new Set<string>();
    for (const dependency of modelFormulaReferences(
      model,
      formula,
      selected.period,
    )) {
      const range = gridReferenceRange(rows, periods, dependency, cadence);
      if (!range) continue;
      for (
        let rowIndex = range.startRow;
        rowIndex <= range.endRow;
        rowIndex += 1
      ) {
        const dependencyRow = rows[rowIndex];
        if (!dependencyRow) continue;
        for (
          let periodIndex = range.startPeriod;
          periodIndex <= range.endPeriod;
          periodIndex += 1
        ) {
          const dependencyPeriod = periods[periodIndex];
          if (dependencyPeriod) {
            cellKeys.add(xlmCellAddressKey(dependencyRow, dependencyPeriod));
          }
        }
      }
    }
    return cellKeys;
  }, [
    activeCellEditor?.draft.formula,
    cadence,
    model,
    periods,
    rows,
    selected,
  ]);
  const selectionHasOnlyFormulas = useMemo(() => {
    if (!committedReferenceRange || rowSelection) return false;
    for (
      let rowIndex = committedReferenceRange.startRow;
      rowIndex <= committedReferenceRange.endRow;
      rowIndex += 1
    ) {
      const row = rows[rowIndex];
      if (!row?.referenceable) return false;
      for (
        let periodIndex = committedReferenceRange.startPeriod;
        periodIndex <= committedReferenceRange.endPeriod;
        periodIndex += 1
      ) {
        const period = periods[periodIndex];
        if (!period || !matchingValue(row.values, period)?.formula)
          return false;
      }
    }
    return true;
  }, [committedReferenceRange, periods, rowSelection, rows]);
  const selectedClipboardPayload = useMemo(
    () =>
      rowSelection
        ? gridSelectionClipboardPayload(
            rows,
            periods,
            rowSelection,
            true,
            model.reporting_currency,
          )
        : committedReferenceRange
          ? gridSelectionClipboardPayload(
              rows,
              periods,
              committedReferenceRange,
            )
          : null,
    [
      committedReferenceRange,
      model.reporting_currency,
      periods,
      rowSelection,
      rows,
    ],
  );
  const selectedValueClipboardPayload = useMemo(
    () =>
      rowSelection
        ? gridSelectionClipboardPayload(
            rows,
            periods,
            rowSelection,
            true,
            model.reporting_currency,
            "values",
          )
        : committedReferenceRange
          ? gridSelectionClipboardPayload(
              rows,
              periods,
              committedReferenceRange,
              false,
              null,
              "values",
            )
          : null,
    [
      committedReferenceRange,
      model.reporting_currency,
      periods,
      rowSelection,
      rows,
    ],
  );
  const pasteSelectionRange = rowSelection ?? committedReferenceRange;
  const referenceAnchor = useRef<HTMLTableCellElement | null>(null);
  const scrollElement = useRef<HTMLDivElement | null>(null);
  const pendingZoomAnchor = useRef<{
    index: number;
    scrollLeft: number;
    viewportOffset: number;
  } | null>(null);
  const activeSectionVirtualIndex = useRef<number | null>(null);
  const activeHeadingVirtualIndex = useRef<number | null>(null);

  useEffect(() => {
    setSelected((current) => {
      if (!current) return current;
      const row = rows.find((candidate) => candidate.key === current.row.key);
      const period = periods.find(
        (candidate) =>
          candidate.cadence === current.period.cadence &&
          candidate.year === current.period.year &&
          candidate.part === current.period.part,
      );
      if (!row || !period || !row.selectable) return null;
      const value = matchingValue(row.values, period) ?? blankXLMValue(period);
      if (
        row === current.row &&
        period === current.period &&
        value === current.value
      ) {
        return current;
      }
      return { row, period, value };
    });
  }, [periods, rows]);

  useEffect(() => {
    onEditorDirtyChange?.(editorDirty);
  }, [editorDirty, onEditorDirtyChange]);

  useEffect(
    () => () => {
      onEditorDirtyChange?.(false);
    },
    [onEditorDirtyChange],
  );

  useEffect(() => {
    setLocalReference(null);
    if (externalReferenceKey !== null) {
      setRowSelection(null);
      rowSelectionAnchor.current = null;
    }
  }, [externalReferenceKey]);

  useEffect(() => {
    if (!reference || externalReferenceKey === null) {
      appliedExternalReference.current = null;
      return;
    }
    const applicationKey = `${externalReferenceKey}:${inspectReference ? "inspect" : "select"}`;
    if (appliedExternalReference.current === applicationKey) return;
    if (reference.kind !== "cell") {
      appliedExternalReference.current = applicationKey;
      setSelected(null);
      setRowDetailsKey(null);
      setSidepeekVisible(false);
      return;
    }
    const nextSelected = selectedCellForReference(model, reference);
    if (!nextSelected) return;
    appliedExternalReference.current = applicationKey;
    setSelected(nextSelected);
    if (inspectReference) {
      setRowDetailsKey(null);
      setSidepeekVisible(true);
      setSidepeekCollapsed(false);
    }
  }, [externalReferenceKey, inspectReference, model, reference]);

  useEffect(() => {
    if (!referenceKey) return;
    setQuery("");
    setCollapsed(new Set<string>());
  }, [referenceKey]);
  const clearReference = (): boolean => {
    if (editorDirty) {
      toast.error("Save or reset this cell before changing the model view.");
      return false;
    }
    dragSelection.current = null;
    fillDrag.current = null;
    selectionAnchor.current = null;
    rowSelectionAnchor.current = null;
    setSelectionPreview(null);
    setRowSelection(null);
    setLocalReference(null);
    directSelectionTarget.current = null;
    setRowDetailsKey(null);
    setSidepeekVisible(false);
    onClearReference?.();
    return true;
  };
  const commitGridSelection = useCallback(
    (
      focus: GridCellSelection,
      nextReference: ModelReference,
      anchor: GridCellSelection,
      inspect: boolean,
    ): boolean => {
      if (
        editorDirty &&
        selected &&
        xlmCellAddressKey(selected.row, selected.period) !==
          xlmCellAddressKey(focus.row, focus.period)
      ) {
        setSelectionPreview(null);
        if (!submitCellDraft()) return false;
      }
      selectionAnchor.current = anchor;
      rowSelectionAnchor.current = null;
      setSelectionPreview(null);
      setRowSelection(null);
      directSelectionTarget.current = modelReferenceTarget(nextReference);
      setLocalReference(nextReference);
      if (nextReference.kind === "cell") {
        if (inspect) {
          setRowDetailsKey(null);
          setSidepeekVisible(true);
          setSidepeekCollapsed(false);
        }
        setSelected({
          row: focus.row,
          period: focus.period,
          value: focus.value,
        });
      } else {
        setSidepeekVisible(false);
        setSelected(null);
      }
      return true;
    },
    [editorDirty, selected, submitCellDraft],
  );
  const openCellContextMenu = useCallback(
    (cell: GridCellSelection, x: number, y: number) => {
      const insideSelectedRows = Boolean(
        rowSelection &&
        cell.rowIndex >= rowSelection.startRow &&
        cell.rowIndex <= rowSelection.endRow,
      );
      if (
        !insideSelectedRows &&
        !commitGridSelection(
          cell,
          gridSelectionReference(cell, cell),
          cell,
          false,
        )
      ) {
        return;
      }
      setContextMenu({ kind: "cell", x, y, cell });
    },
    [commitGridSelection, rowSelection],
  );
  const selectLineItems = useCallback(
    (rowIndex: number, extend: boolean) => {
      if (editorDirty) {
        toast.error("Save or reset this cell before selecting line items.");
        return;
      }
      if (periods.length === 0) return;
      const anchor =
        extend && rowSelectionAnchor.current !== null
          ? rowSelectionAnchor.current
          : rowIndex;
      rowSelectionAnchor.current = anchor;
      selectionAnchor.current = null;
      dragSelection.current = null;
      fillDrag.current = null;
      setSelectionPreview(null);
      setLocalReference(null);
      directSelectionTarget.current = null;
      setSidepeekVisible(false);
      setSelected(null);
      setRowSelection({
        startRow: Math.min(anchor, rowIndex),
        endRow: Math.max(anchor, rowIndex),
        startPeriod: 0,
        endPeriod: periods.length - 1,
      });
      onClearReference?.();
    },
    [editorDirty, onClearReference, periods.length],
  );
  const openRowContextMenu = useCallback(
    (row: ViewerRow, rowIndex: number, x: number, y: number) => {
      selectLineItems(rowIndex, false);
      setContextMenu({ kind: "row", x, y, row, rowIndex });
    },
    [selectLineItems],
  );
  const finishPointerSelection = useCallback(
    (event: PointerEvent) => {
      const drag = dragSelection.current;
      if (!drag) return;
      dragSelection.current = null;
      const nextReference = gridSelectionReference(drag.anchor, drag.focus);
      if (drag.formulaSelection) {
        setSelectionPreview(null);
        if (!activeCellEditor || !selected) return;
        let expression: string;
        try {
          expression = formulaReferenceExpression(
            drag.anchor,
            drag.focus,
            rows,
            periods,
            selected.period,
          );
        } catch (error) {
          toast.error(
            error instanceof Error
              ? error.message
              : "The formula reference could not be inserted.",
          );
          return;
        }
        const formula = activeCellEditor.draft.formula;
        const start = Math.min(drag.formulaSelection.start, formula.length);
        const end = Math.min(drag.formulaSelection.end, formula.length);
        const nextFormula = `${formula.slice(0, start)}${expression}${formula.slice(end)}`;
        pendingFormulaCaret.current = start + expression.length;
        updateCellDraft((current) => ({
          ...current,
          value: "",
          formula: nextFormula,
        }));
        return;
      }
      if (
        !commitGridSelection(
          drag.focus,
          nextReference,
          drag.anchor,
          event.metaKey || event.ctrlKey,
        )
      ) {
        return;
      }
      cellControlRefs.current
        .get(xlmCellAddressKey(drag.focus.row, drag.focus.period))
        ?.focus({ preventScroll: true });
    },
    [
      activeCellEditor,
      commitGridSelection,
      periods,
      rows,
      selected,
      updateCellDraft,
    ],
  );
  const cancelPointerSelection = useCallback(() => {
    dragSelection.current = null;
    setSelectionPreview(null);
  }, []);

  useEffect(() => {
    window.addEventListener("pointerup", finishPointerSelection);
    window.addEventListener("pointercancel", cancelPointerSelection);
    return () => {
      window.removeEventListener("pointerup", finishPointerSelection);
      window.removeEventListener("pointercancel", cancelPointerSelection);
    };
  }, [cancelPointerSelection, finishPointerSelection]);

  useEffect(() => {
    if (!selectedClipboardPayload) return;
    const copySelectedCells = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.key.toLocaleLowerCase() !== "c" ||
        (!event.metaKey && !event.ctrlKey) ||
        event.altKey ||
        preservesNativeKeyboardInput(event.target)
      ) {
        return;
      }
      const textSelection = window.getSelection();
      if (textSelection && !textSelection.isCollapsed) return;

      event.preventDefault();
      const copyValues = event.shiftKey;
      const payload = copyValues
        ? selectedValueClipboardPayload
        : selectedClipboardPayload;
      if (!payload) return;
      void copyText(payload.text).catch(() =>
        toast.error("The selection could not be copied."),
      );
    };
    window.addEventListener("keydown", copySelectedCells);
    return () => window.removeEventListener("keydown", copySelectedCells);
  }, [selectedClipboardPayload, selectedValueClipboardPayload]);
  const pasteText = useCallback(
    async (text: string, selection: GridSelectionRange): Promise<boolean> => {
      if (!canEdit || !onSaveOperations) return false;
      if (editorDirty) {
        toast.error("Save or reset the current cell before pasting.");
        return false;
      }
      if (pasteLocked) {
        toast.error("Resolve the current model save before pasting again.");
        return false;
      }
      let result: XLMPasteResult;
      try {
        result = xlmPasteOperations(text, rows, periods, selection);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "The cells could not be pasted.",
        );
        return false;
      }
      if (!(await onSaveOperations(result.operations))) return false;
      undoStack.current.push({
        kind: "batch",
        operations: result.undoOperations,
      });
      const firstRow = rows[result.range.startRow];
      const lastRow = rows[result.range.endRow];
      const firstPeriod = periods[result.range.startPeriod];
      const lastPeriod = periods[result.range.endPeriod];
      if (firstRow && lastRow && firstPeriod && lastPeriod) {
        const start = modelReferenceCell(firstRow, firstPeriod);
        const end = modelReferenceCell(lastRow, lastPeriod);
        setLocalReference({
          kind:
            result.range.startRow === result.range.endRow &&
            result.range.startPeriod === result.range.endPeriod
              ? "cell"
              : "range",
          start,
          end,
        });
      }
      return true;
    },
    [canEdit, editorDirty, onSaveOperations, pasteLocked, periods, rows],
  );
  const clearSelectedCells = useCallback(
    async (range: GridSelectionRange): Promise<boolean> => {
      if (!canEdit || !onSaveOperations) return false;
      if (editorDirty) {
        toast.error("Save or reset the current cell before clearing a range.");
        return false;
      }
      if (pasteLocked) {
        toast.error("Resolve the current model save before clearing a range.");
        return false;
      }
      let result: XLMPasteResult;
      try {
        result = xlmClearOperations(rows, periods, range);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "The selected cells could not be cleared.",
        );
        return false;
      }
      if (!(await onSaveOperations(result.operations))) return false;
      undoStack.current.push({
        kind: "batch",
        operations: result.undoOperations,
      });
      const nextReference = gridRangeReference(rows, periods, result.range);
      if (nextReference) setLocalReference(nextReference);
      return true;
    },
    [canEdit, editorDirty, onSaveOperations, pasteLocked, periods, rows],
  );
  const fillSelection = useCallback(
    async (
      source: GridSelectionRange,
      target: GridCellSelection,
    ): Promise<boolean> => {
      if (!canEdit || !onSaveOperations) return false;
      if (editorDirty) {
        toast.error("Save or reset the current cell before filling formulas.");
        return false;
      }
      if (pasteLocked) {
        toast.error("Resolve the current model save before filling formulas.");
        return false;
      }
      let result: XLMFillResult;
      try {
        result = xlmFillOperations(rows, periods, source, target);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "The formula could not be filled.",
        );
        return false;
      }
      if (!(await onSaveOperations(result.operations))) return false;
      undoStack.current.push({
        kind: "batch",
        operations: result.undoOperations,
      });
      const nextReference = gridRangeReference(rows, periods, result.range);
      if (nextReference) setLocalReference(nextReference);
      toast.success(
        result.operations.length === 1
          ? "Formula filled."
          : `${result.operations.length} formulas filled.`,
      );
      return true;
    },
    [canEdit, editorDirty, onSaveOperations, pasteLocked, periods, rows],
  );
  const beginFormulaFill = useCallback(
    (
      event: ReactPointerEvent<HTMLButtonElement>,
      source: GridSelectionRange,
      target: GridCellSelection,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      if (editorDirty || pasteLocked || pendingSaveCount > 0) return;
      fillDrag.current = { source, target };
      const preview = gridRangeReference(
        rows,
        periods,
        expandedFillRange(source, target),
      );
      if (preview) setSelectionPreview(preview);
    },
    [editorDirty, pasteLocked, pendingSaveCount, periods, rows],
  );
  const updateFormulaFillTarget = useCallback(
    (target: GridCellSelection) => {
      const drag = fillDrag.current;
      if (!drag) return false;
      drag.target = target;
      const preview = gridRangeReference(
        rows,
        periods,
        expandedFillRange(drag.source, target),
      );
      if (preview) setSelectionPreview(preview);
      return true;
    },
    [periods, rows],
  );
  const finishFormulaFill = useCallback(() => {
    const drag = fillDrag.current;
    if (!drag) return;
    fillDrag.current = null;
    setSelectionPreview(null);
    if (
      sameGridRange(expandedFillRange(drag.source, drag.target), drag.source)
    ) {
      return;
    }
    void fillSelection(drag.source, drag.target);
  }, [fillSelection]);
  const cancelFormulaFill = useCallback(() => {
    if (!fillDrag.current) return;
    fillDrag.current = null;
    setSelectionPreview(null);
  }, []);
  useEffect(() => {
    window.addEventListener("pointerup", finishFormulaFill);
    window.addEventListener("pointercancel", cancelFormulaFill);
    return () => {
      window.removeEventListener("pointerup", finishFormulaFill);
      window.removeEventListener("pointercancel", cancelFormulaFill);
    };
  }, [cancelFormulaFill, finishFormulaFill]);
  useEffect(() => {
    if (!canEdit || !onSaveOperations || !pasteSelectionRange) return;
    const pasteSelectedCells = (event: ClipboardEvent) => {
      if (
        event.defaultPrevented ||
        preservesNativeKeyboardInput(event.target)
      ) {
        return;
      }
      const text = event.clipboardData?.getData("text/plain");
      if (text === undefined) return;
      event.preventDefault();
      void pasteText(text, pasteSelectionRange);
    };
    window.addEventListener("paste", pasteSelectedCells);
    return () => window.removeEventListener("paste", pasteSelectedCells);
  }, [canEdit, onSaveOperations, pasteText, pasteSelectionRange]);
  useEffect(() => {
    const undoModelEdit = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.isComposing ||
        event.key.toLocaleLowerCase() !== "z" ||
        (!event.metaKey && !event.ctrlKey) ||
        event.altKey ||
        event.shiftKey ||
        preservesNativeKeyboardInput(event.target)
      ) {
        return;
      }
      if (undoCellEdit()) event.preventDefault();
    };
    window.addEventListener("keydown", undoModelEdit);
    return () => window.removeEventListener("keydown", undoModelEdit);
  }, [undoCellEdit]);
  const hasFormulas = rows.some((row) =>
    row.values?.some((value) => Boolean(value.formula)),
  );
  const boundaryIds = rows
    .filter((row) => row.boundary && row.descendantCount > 0)
    .map((row) => row.key);
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (needle === "") {
      return rows.filter(
        (row) => !row.ancestorKeys.some((key) => collapsed.has(key)),
      );
    }
    const direct = new Set(
      rows
        .filter((row) =>
          [row.label, row.node.name, row.node.description, row.unitLabel]
            .filter((value): value is string => Boolean(value))
            .some((value) => value.toLocaleLowerCase().includes(needle)),
        )
        .map((row) => row.key),
    );
    const matchingBoundaries = new Set(
      rows
        .filter((row) => row.boundary && direct.has(row.key))
        .map((row) => row.key),
    );
    return rows.filter(
      (row) =>
        direct.has(row.key) ||
        row.ancestorKeys.some(
          (key) => direct.has(key) || matchingBoundaries.has(key),
        ) ||
        (row.boundary !== null &&
          rows.some(
            (candidate) =>
              direct.has(candidate.key) &&
              candidate.ancestorKeys.includes(row.key),
          )),
    );
  }, [collapsed, query, rows]);
  const inlineAddRowIndex =
    rowEditor?.mode === "add"
      ? rowPlacementInsertionIndex(model, visibleRows, rowEditor.placement)
      : null;
  const inlineAddVirtualIndex =
    inlineAddRowIndex === null || visibleRows.length === 0
      ? -1
      : Math.min(inlineAddRowIndex, visibleRows.length - 1);
  const virtualSectionIndexes = useMemo(
    () =>
      visibleRows.flatMap((row, index) =>
        row.boundary === "section" ? [index] : [],
      ),
    [visibleRows],
  );
  const virtualHeadingIndexes = useMemo(
    () =>
      visibleRows.flatMap((row, index) =>
        row.boundary === "heading" ? [index] : [],
      ),
    [visibleRows],
  );
  const referenceRowKey =
    referenceRange === null ? null : rows[referenceRange.startRow]?.key;
  const referenceVirtualIndex =
    referenceRowKey === null || referenceRowKey === undefined
      ? -1
      : visibleRows.findIndex((row) => row.key === referenceRowKey);
  const showRowGaps = query.trim() === "";
  const statementZoom =
    (statementZoomPercent * STATEMENT_ZOOM_BASE_PERCENT) / 10_000;
  const statementValueColumnWidth =
    cellDisplayMode === "formulas"
      ? STATEMENT_FORMULA_COLUMN_WIDTH
      : STATEMENT_VALUE_COLUMN_WIDTH;
  const statementTableWidth =
    STATEMENT_LINE_COLUMN_WIDTH +
    STATEMENT_UNIT_COLUMN_WIDTH +
    periods.length * statementValueColumnWidth;
  const statementTableStyle = {
    zoom: statementZoom,
    width: statementTableWidth,
    minWidth: statementTableWidth,
    maxWidth: statementTableWidth,
    "--statement-line-column-width": `${STATEMENT_LINE_COLUMN_WIDTH}px`,
    "--statement-unit-column-width": `${STATEMENT_UNIT_COLUMN_WIDTH}px`,
    "--statement-value-column-width": `${statementValueColumnWidth}px`,
  } as CSSProperties;
  const estimateVirtualRowSize = useCallback(
    (index: number) =>
      statementRowSize(visibleRows[index], showRowGaps) * statementZoom,
    [showRowGaps, statementZoom, visibleRows],
  );
  const virtualRowKey = useCallback(
    (index: number) => visibleRows[index]?.key ?? index,
    [visibleRows],
  );
  const extractVirtualRange = useCallback(
    (range: Parameters<typeof defaultRangeExtractor>[0]) => {
      const activeSection = lastIndexAtOrBefore(
        virtualSectionIndexes,
        range.startIndex,
      );
      const sectionFloor = activeSection ?? -1;
      const nextSection =
        virtualSectionIndexes.find((index) => index > sectionFloor) ??
        Number.POSITIVE_INFINITY;
      const activeHeading = lastIndexAtOrBefore(
        virtualHeadingIndexes.filter(
          (index) => index > sectionFloor && index < nextSection,
        ),
        range.startIndex,
      );
      activeSectionVirtualIndex.current = activeSection;
      activeHeadingVirtualIndex.current = activeHeading;

      const indexes = defaultRangeExtractor(range);
      if (activeSection !== null) indexes.push(activeSection);
      if (activeHeading !== null) indexes.push(activeHeading);
      if (referenceVirtualIndex >= 0) indexes.push(referenceVirtualIndex);
      if (inlineAddVirtualIndex >= 0) indexes.push(inlineAddVirtualIndex);
      return [...new Set(indexes)].sort((left, right) => left - right);
    },
    [
      inlineAddVirtualIndex,
      referenceVirtualIndex,
      virtualHeadingIndexes,
      virtualSectionIndexes,
    ],
  );
  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollElement.current,
    observeElementRect: observeStatementRect,
    estimateSize: estimateVirtualRowSize,
    getItemKey: virtualRowKey,
    rangeExtractor: extractVirtualRange,
    overscan: STATEMENT_VIRTUAL_OVERSCAN,
    initialRect: STATEMENT_INITIAL_RECT,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  const openInlineRowDraft = useCallback(
    (placement: XLMRowPlacement) => {
      if (editorDirty || pendingSaveCount > 0) return;
      setContextMenu(null);
      setQuery("");
      setCollapsed(new Set<string>());
      setRowEditor(addRowDraft(placement));
      const index = rowPlacementInsertionIndex(model, rows, placement);
      window.requestAnimationFrame(() => {
        if (rows.length > 0) {
          rowVirtualizer.scrollToIndex(Math.min(index, rows.length - 1), {
            align: "center",
          });
        }
      });
    },
    [editorDirty, model, pendingSaveCount, rowVirtualizer, rows],
  );
  const updateInlineRowDraft = useCallback(
    (update: (draft: XLMAddRowDraft) => XLMAddRowDraft) => {
      setRowEditor((current) =>
        current?.mode === "add" ? update(current) : current,
      );
    },
    [],
  );
  const submitInlineRowDraft = useCallback(async (): Promise<boolean> => {
    if (!onSaveOperations || rowEditor?.mode !== "add") return false;
    const name =
      rowEditor.type === "separator" ? "Blank row" : rowEditor.name.trim();
    const identifier =
      rowEditor.placement.kind === "node"
        ? uniqueInlineNodeIdentifier(model, name)
        : inlineRowIdentifier(name);
    if (name === "" || identifier === "") {
      updateInlineRowDraft((draft) => ({ ...draft, attempted: true }));
      return false;
    }
    const operation: XLMEditOperation =
      rowEditor.placement.kind === "breakdown_item"
        ? {
            kind: "add_breakdown_item",
            breakdown_identifier: rowEditor.placement.breakdownIdentifier,
            identifier,
            name,
            description: null,
            ...(rowEditor.placement.afterIdentifier
              ? { after_identifier: rowEditor.placement.afterIdentifier }
              : {}),
            ...(rowEditor.placement.beforeIdentifier
              ? { before_identifier: rowEditor.placement.beforeIdentifier }
              : {}),
          }
        : {
            kind: "add_node",
            identifier,
            name,
            type: rowEditor.type,
            description: null,
            unit: XLM_VALUE_ROW_TYPES.has(rowEditor.type)
              ? rowEditor.unit.trim() || null
              : null,
            summable: XLM_VALUE_ROW_TYPES.has(rowEditor.type),
            ...(rowEditor.placement.afterIdentifier
              ? { after_identifier: rowEditor.placement.afterIdentifier }
              : {}),
            ...(rowEditor.placement.beforeIdentifier
              ? { before_identifier: rowEditor.placement.beforeIdentifier }
              : {}),
          };
    const pendingSave = saveStructuralOperations([operation]);
    setRowEditor(null);
    const saved = await pendingSave;
    if (!saved) setRowEditor((current) => current ?? rowEditor);
    return saved;
  }, [
    model,
    onSaveOperations,
    rowEditor,
    saveStructuralOperations,
    updateInlineRowDraft,
  ]);
  const openInlineRowEdit = useCallback(
    (target: XLMAuthoredRowTarget, initialFocus: "name" | "unit" = "name") => {
      if (editorDirty || pendingSaveCount > 0) return;
      setContextMenu(null);
      setRowEditor(editRowDraft(target, initialFocus));
    },
    [editorDirty, pendingSaveCount],
  );
  const updateInlineRowEdit = useCallback(
    (update: (draft: XLMEditRowDraft) => XLMEditRowDraft) => {
      setRowEditor((current) =>
        current?.mode === "edit" ? update(current) : current,
      );
    },
    [],
  );
  const submitInlineRowEdit = useCallback(async (): Promise<boolean> => {
    if (!onSaveOperations || rowEditor?.mode !== "edit") return false;
    const name = rowEditor.name.trim();
    const unit = rowEditor.unit.trim() || null;
    if (name === "") {
      updateInlineRowEdit((draft) => ({ ...draft, attempted: true }));
      return false;
    }
    const nameChanged = name !== rowEditor.target.node.name;
    const unitChanged = unit !== (rowEditor.target.node.unit ?? null);
    if (!nameChanged && !unitChanged && !rowEditor.convertToTruth) {
      setRowEditor(null);
      return true;
    }
    const operation: XLMEditOperation =
      rowEditor.target.kind === "breakdown_item"
        ? {
            kind: "edit_breakdown_item",
            breakdown_identifier: rowEditor.target.breakdownIdentifier,
            identifier: rowEditor.target.node.identifier,
            name,
          }
        : {
            kind: "edit_node",
            identifier: rowEditor.target.node.identifier,
            ...(nameChanged || rowEditor.convertToTruth ? { name } : {}),
            ...(unitChanged || rowEditor.convertToTruth ? { unit } : {}),
            ...(rowEditor.convertToTruth
              ? { type: "truth" as const, summable: true }
              : {}),
          };
    const pendingSave = saveStructuralOperations([operation]);
    setRowEditor(null);
    const saved = await pendingSave;
    if (!saved) setRowEditor((current) => current ?? rowEditor);
    return saved;
  }, [
    onSaveOperations,
    rowEditor,
    saveStructuralOperations,
    updateInlineRowEdit,
  ]);
  useEffect(() => {
    if (rowEditCommitRequest === handledRowEditCommitRequest.current) return;
    handledRowEditCommitRequest.current = rowEditCommitRequest;
    void submitInlineRowEdit();
  }, [rowEditCommitRequest, submitInlineRowEdit]);
  const openBreakdownEditor = useCallback(
    (node: XLMNode) => {
      if (!node.breakdown || editorDirty || pendingSaveCount > 0) return;
      setBreakdownEditor({
        node,
        name: node.name,
        unit: node.unit ?? node.breakdown.unit ?? "",
        description: node.description ?? "",
        renderTotal: node.breakdown.render_total !== false,
        renderGrowth: node.breakdown.render_growth === true,
      });
      setRowDetailsKey(null);
      setSidepeekVisible(false);
    },
    [editorDirty, pendingSaveCount],
  );
  const submitBreakdownEditor = useCallback(async (): Promise<boolean> => {
    if (!breakdownEditor) return false;
    const current = breakdownEditor;
    const sourceBreakdown = current.node.breakdown;
    if (!sourceBreakdown) return false;
    const name = current.name.trim();
    const unit = current.unit.trim() || null;
    const description = current.description.trim() || null;
    if (name === "") return false;
    const nameChanged = name !== current.node.name;
    const unitChanged =
      unit !==
      (current.node.unit?.trim() ?? sourceBreakdown.unit?.trim() ?? null);
    const descriptionChanged =
      description !== (current.node.description?.trim() || null);
    const totalChanged =
      current.renderTotal !== (sourceBreakdown.render_total !== false);
    const growthChanged =
      current.renderGrowth !== (sourceBreakdown.render_growth === true);
    if (
      !nameChanged &&
      !unitChanged &&
      !descriptionChanged &&
      !totalChanged &&
      !growthChanged
    ) {
      return true;
    }
    const saved = await saveStructuralOperations(
      [
        {
          kind: "edit_node",
          identifier: current.node.identifier,
          ...(nameChanged ? { name } : {}),
          ...(unitChanged ? { unit } : {}),
          ...(descriptionChanged ? { description } : {}),
          ...(totalChanged ? { render_total: current.renderTotal } : {}),
          ...(growthChanged ? { render_growth: current.renderGrowth } : {}),
        },
      ],
      { optimistic: false },
    );
    if (saved) {
      setBreakdownEditor((value) => {
        if (!value || value.node.identifier !== current.node.identifier) {
          return value;
        }
        const breakdown = value.node.breakdown;
        if (!breakdown) return value;
        return {
          ...value,
          node: {
            ...value.node,
            name,
            unit,
            description,
            breakdown: {
              ...breakdown,
              unit,
              render_total: current.renderTotal,
              render_growth: current.renderGrowth,
            },
          },
        };
      });
    }
    return saved;
  }, [breakdownEditor, saveStructuralOperations]);
  const saveRowMove = useCallback(
    (
      source: XLMAuthoredRowTarget,
      target: XLMAuthoredRowTarget,
      position: XLMRowDropPosition,
    ): boolean => {
      if (!onSaveOperations || (pendingSaveCount > 0 && !canQueueRowMove)) {
        return false;
      }
      const operation = xlmMoveRowOperation(model, source, target, position);
      if (!operation) return false;
      pendingRowMoveId.current += 1;
      const pendingId = pendingRowMoveId.current;
      setPendingRowMoves((current) => [
        ...current,
        { id: pendingId, operation },
      ]);
      setRowDrag(null);
      void saveStructuralOperations([operation]).finally(() => {
        setPendingRowMoves((current) =>
          current.filter((pending) => pending.id !== pendingId),
        );
      });
      return true;
    },
    [
      canQueueRowMove,
      model,
      onSaveOperations,
      pendingSaveCount,
      saveStructuralOperations,
    ],
  );
  const startRowDrag = useCallback(
    (
      event: ReactDragEvent<HTMLButtonElement>,
      source: XLMAuthoredRowTarget,
    ) => {
      if (editorDirty || (pendingSaveCount > 0 && !canQueueRowMove)) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", source.node.identifier);
      setRowDrag({ source, target: null, position: null });
    },
    [canQueueRowMove, editorDirty, pendingSaveCount],
  );
  const previewRowDrop = useCallback(
    (
      event: ReactDragEvent<HTMLTableRowElement>,
      target: XLMAuthoredRowTarget,
    ) => {
      if (!rowDrag) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const position =
        event.clientY < rect.top + rect.height / 2 ? "before" : "after";
      if (!xlmMoveRowOperation(model, rowDrag.source, target, position)) {
        event.dataTransfer.dropEffect = "none";
        if (rowDrag.target || rowDrag.position) {
          setRowDrag({ ...rowDrag, target: null, position: null });
        }
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (
        rowDrag.position !== position ||
        !rowDrag.target ||
        !sameAuthoredRowTarget(rowDrag.target, target)
      ) {
        setRowDrag({ ...rowDrag, target, position });
      }
    },
    [model, rowDrag],
  );
  const dropRow = useCallback(
    (
      event: ReactDragEvent<HTMLTableRowElement>,
      target: XLMAuthoredRowTarget,
    ) => {
      if (!rowDrag?.position) return;
      event.preventDefault();
      if (!saveRowMove(rowDrag.source, target, rowDrag.position)) {
        setRowDrag(null);
      }
    },
    [rowDrag, saveRowMove],
  );
  const requestRowDelete = useCallback(
    (targets: readonly XLMAuthoredRowTarget[]) => {
      if (targets.length === 0 || editorDirty || pendingSaveCount > 0) {
        return;
      }
      setContextMenu(null);
      setRowDeleteTargets([...targets]);
    },
    [editorDirty, pendingSaveCount],
  );
  const confirmRowDelete = useCallback(() => {
    if (
      !onSaveOperations ||
      rowDeleteTargets.length === 0 ||
      pendingSaveCount > 0
    ) {
      return;
    }
    const operations: XLMEditOperation[] = rowDeleteTargets.map((target) =>
      target.kind === "breakdown_item"
        ? {
            kind: "delete_breakdown_item",
            breakdown_identifier: target.breakdownIdentifier,
            identifier: target.node.identifier,
          }
        : {
            kind: "delete_node",
            identifier: target.node.identifier,
          },
    );
    setRowDeleteTargets([]);
    setRowSelection(null);
    rowSelectionAnchor.current = null;
    void saveStructuralOperations(operations);
  }, [
    onSaveOperations,
    pendingSaveCount,
    rowDeleteTargets,
    saveStructuralOperations,
  ]);

  const focusCellControl = useCallback(
    (cellKey: string, reveal: boolean): boolean => {
      const control = cellControlRefs.current.get(cellKey);
      if (!control) return false;
      control.focus({ preventScroll: true });
      if (reveal) {
        const container = scrollElement.current;
        const containerRect = container?.getBoundingClientRect();
        const controlRect = control.getBoundingClientRect();
        if (
          !containerRect ||
          controlRect.top < containerRect.top ||
          controlRect.bottom > containerRect.bottom ||
          controlRect.left < containerRect.left ||
          controlRect.right > containerRect.right
        ) {
          control.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
      }
      return true;
    },
    [],
  );
  const focusFormulaInput = useCallback((selectAll: boolean): boolean => {
    const input = formulaInputRef.current;
    if (!input) return false;
    input.focus({ preventScroll: true });
    const end = input.value.length;
    input.setSelectionRange(selectAll ? 0 : end, end);
    return true;
  }, []);
  const focusSelectedCell = useCallback(() => {
    if (selectedCellKey) focusCellControl(selectedCellKey, false);
  }, [focusCellControl, selectedCellKey]);
  const requestFormulaFocus = useCallback(
    (cellKey: string, selectAll: boolean) => {
      pendingFormulaFocus.current = { cellKey, selectAll };
      if (
        activeCellEditor?.cellKey === cellKey &&
        focusFormulaInput(selectAll)
      ) {
        pendingFormulaFocus.current = null;
      }
    },
    [activeCellEditor?.cellKey, focusFormulaInput],
  );
  const beginGridCellEdit = useCallback(
    (cell: GridCellSelection, replacement?: string) => {
      if (!canEdit || !cell.row.referenceable) return;
      const cellKey = xlmCellAddressKey(cell.row, cell.period);
      if (
        selectedCellKey !== cellKey &&
        !commitGridSelection(
          cell,
          gridSelectionReference(cell, cell),
          cell,
          false,
        )
      ) {
        return;
      }
      setCellEditor((current) => {
        const initialDraft = cellDraft(cell.value);
        const editor =
          current?.cellKey === cellKey
            ? current
            : {
                cellKey,
                baseline: structuredClone(initialDraft),
                draft: initialDraft,
                validationError: null,
              };
        if (replacement === undefined) return editor;
        return {
          ...editor,
          draft: updateAuthoredCellText(editor.draft, replacement),
          validationError: null,
        };
      });
      requestFormulaFocus(cellKey, false);
    },
    [canEdit, commitGridSelection, requestFormulaFocus, selectedCellKey],
  );
  const gridCellFor = useCallback(
    (row: ViewerRow, periodIndex: number): GridCellSelection | null => {
      if (!row.selectable) return null;
      const period = periods[periodIndex];
      const rowIndex = rowIndexes.get(row.key);
      if (!period || rowIndex === undefined) return null;
      return {
        row,
        period,
        value: matchingValue(row.values, period) ?? blankXLMValue(period),
        rowIndex,
        periodIndex,
      };
    },
    [periods, rowIndexes],
  );
  const navigateGridCell = useCallback(
    (
      current: GridCellSelection,
      key: "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight" | "Tab",
      extend: boolean,
    ) => {
      const currentVisibleRowIndex = visibleRows.findIndex(
        (row) => row.key === current.row.key,
      );
      const currentPeriodIndex = periods.findIndex((period) =>
        sameModelReferencePeriod(period, current.period),
      );
      if (currentVisibleRowIndex < 0 || currentPeriodIndex < 0) return;

      let next: GridCellSelection | null = null;
      if (key === "ArrowLeft" || key === "ArrowRight") {
        next = gridCellFor(
          current.row,
          currentPeriodIndex + (key === "ArrowLeft" ? -1 : 1),
        );
      } else if (key === "ArrowUp" || key === "ArrowDown") {
        const step = key === "ArrowUp" ? -1 : 1;
        for (
          let index = currentVisibleRowIndex + step;
          index >= 0 && index < visibleRows.length;
          index += step
        ) {
          const row = visibleRows[index];
          if (!row) continue;
          next = gridCellFor(row, currentPeriodIndex);
          if (next) break;
        }
      } else {
        const selectableRows = visibleRows.filter((row) => row.selectable);
        const currentSelectableRowIndex = selectableRows.findIndex(
          (row) => row.key === current.row.key,
        );
        if (currentSelectableRowIndex >= 0) {
          const step = extend ? -1 : 1;
          const linearIndex =
            currentSelectableRowIndex * periods.length +
            currentPeriodIndex +
            step;
          if (
            linearIndex >= 0 &&
            linearIndex < selectableRows.length * periods.length
          ) {
            const row =
              selectableRows[Math.floor(linearIndex / periods.length)];
            if (row) next = gridCellFor(row, linearIndex % periods.length);
          }
        }
      }
      if (!next) return;

      const anchor =
        extend && key !== "Tab" ? (selectionAnchor.current ?? current) : next;
      if (
        !commitGridSelection(
          next,
          gridSelectionReference(anchor, next),
          anchor,
          false,
        )
      ) {
        return;
      }
      const cellKey = xlmCellAddressKey(next.row, next.period);
      const visibleRowIndex = visibleRows.findIndex(
        (row) => row.key === next.row.key,
      );
      pendingCellFocus.current = { cellKey, visibleRowIndex };
      if (!cellControlRefs.current.has(cellKey) && visibleRowIndex >= 0) {
        rowVirtualizer.scrollToIndex(visibleRowIndex, { align: "auto" });
      }
    },
    [commitGridSelection, gridCellFor, periods, rowVirtualizer, visibleRows],
  );
  const handleGridCellKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>, cell: GridCellSelection) => {
      if (event.nativeEvent.isComposing) return;
      if (
        event.key.toLocaleLowerCase() === "z" &&
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
        undoCellEdit();
        return;
      }
      if (
        canEditRows &&
        event.altKey &&
        event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        (event.key === "ArrowUp" || event.key === "ArrowDown")
      ) {
        event.preventDefault();
        openInlineRowDraft(
          addRowPlacement(
            model,
            cell.row,
            event.key === "ArrowUp" ? "before" : "after",
          ),
        );
        return;
      }
      if (
        canEditRows &&
        event.altKey &&
        event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        (event.key === "Backspace" || event.key === "Delete")
      ) {
        const selectionTargets =
          rowSelection &&
          cell.rowIndex >= rowSelection.startRow &&
          cell.rowIndex <= rowSelection.endRow
            ? selectedAuthoredRowTargets
            : null;
        const target = authoredRowTarget(model, cell.row);
        const targets = selectionTargets ?? (target ? [target] : null);
        if (targets) {
          event.preventDefault();
          requestRowDelete(targets);
        }
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (
        event.key === "ArrowUp" ||
        event.key === "ArrowDown" ||
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight" ||
        event.key === "Tab"
      ) {
        event.preventDefault();
        navigateGridCell(cell, event.key, event.shiftKey);
        return;
      }
      if (event.key === "Enter" || event.key === "F2") {
        event.preventDefault();
        beginGridCellEdit(cell);
        return;
      }
      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        const selectedCellRange =
          !rowSelection &&
          committedReferenceRange &&
          (committedReferenceRange.startRow !==
            committedReferenceRange.endRow ||
            committedReferenceRange.startPeriod !==
              committedReferenceRange.endPeriod)
            ? committedReferenceRange
            : null;
        if (selectedCellRange) {
          void clearSelectedCells(selectedCellRange);
          return;
        }
        beginGridCellEdit(cell, "");
        return;
      }
      if (event.key === "Escape" && editorDirty) {
        event.preventDefault();
        resetCellDraft();
        return;
      }
      if (event.key.length === 1) {
        event.preventDefault();
        beginGridCellEdit(cell, event.key);
      }
    },
    [
      beginGridCellEdit,
      canEditRows,
      clearSelectedCells,
      committedReferenceRange,
      editorDirty,
      model,
      navigateGridCell,
      openInlineRowDraft,
      requestRowDelete,
      resetCellDraft,
      rowSelection,
      selectedAuthoredRowTargets,
      undoCellEdit,
    ],
  );

  useBrowserLayoutEffect(() => {
    const pending = pendingCellFocus.current;
    if (!pending) return;
    if (focusCellControl(pending.cellKey, true)) {
      pendingCellFocus.current = null;
      return;
    }
    if (pending.visibleRowIndex >= 0) {
      rowVirtualizer.scrollToIndex(pending.visibleRowIndex, { align: "auto" });
    }
  }, [focusCellControl, rowVirtualizer, virtualRows]);

  useBrowserLayoutEffect(() => {
    const pending = pendingFormulaFocus.current;
    if (!pending || activeCellEditor?.cellKey !== pending.cellKey) return;
    if (focusFormulaInput(pending.selectAll)) {
      pendingFormulaFocus.current = null;
    }
  }, [activeCellEditor, focusFormulaInput]);

  useBrowserLayoutEffect(() => {
    const caret = pendingFormulaCaret.current;
    const input = formulaInputRef.current;
    if (caret === null || !input) return;
    input.focus({ preventScroll: true });
    input.setSelectionRange(caret, caret);
    pendingFormulaCaret.current = null;
  }, [activeCellEditor?.draft.formula]);

  useBrowserLayoutEffect(() => {
    rowVirtualizer.measure();
    const anchor = pendingZoomAnchor.current;
    const container = scrollElement.current;
    if (anchor === null || !container) return;
    const itemOffset =
      visibleRows
        .slice(0, anchor.index)
        .reduce(
          (offset, row) => offset + statementRowSize(row, showRowGaps),
          0,
        ) * statementZoom;
    const headerHeight =
      container
        .querySelector<HTMLTableCellElement>("thead th")
        ?.getBoundingClientRect().height ?? 0;
    container.scrollTop = Math.max(
      0,
      itemOffset + headerHeight - anchor.viewportOffset,
    );
    container.scrollLeft = anchor.scrollLeft;
    pendingZoomAnchor.current = null;
  }, [rowVirtualizer, showRowGaps, statementZoom, visibleRows]);

  useEffect(() => {
    if (!showFullScreenControl) return;
    const toggleFullScreen = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.isComposing ||
        event.key.toLocaleLowerCase() !== "f" ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey ||
        preservesNativeKeyboardInput(event.target)
      ) {
        return;
      }
      event.preventDefault();
      setFullScreen((current) => !current);
    };
    window.addEventListener("keydown", toggleFullScreen);
    return () => window.removeEventListener("keydown", toggleFullScreen);
  }, [showFullScreenControl]);

  useEffect(() => {
    if (!fullScreen) return;
    const previousOverflow = document.body.style.overflow;
    const exitOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFullScreen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", exitOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", exitOnEscape);
    };
  }, [fullScreen]);

  const copyWorkbook = useCallback(() => {
    const text = xlmWorkbookClipboardText(model, cadence);
    void copyText(text).catch(() =>
      toast.error("The workbook could not be copied."),
    );
  }, [cadence, model]);

  const copyContextCell = useCallback(
    (cell: GridCellSelection, mode: XLMClipboardMode) => {
      const payload = gridSelectionClipboardPayload(
        rows,
        periods,
        {
          startRow: cell.rowIndex,
          endRow: cell.rowIndex,
          startPeriod: cell.periodIndex,
          endPeriod: cell.periodIndex,
        },
        false,
        null,
        mode,
      );
      if (!payload) return;
      void copyText(payload.text).catch(() =>
        toast.error("The cell could not be copied."),
      );
    },
    [periods, rows],
  );

  const copyContextRow = useCallback(
    (rowIndex: number, mode: XLMClipboardMode) => {
      const payload = gridSelectionClipboardPayload(
        rows,
        periods,
        {
          startRow: rowIndex,
          endRow: rowIndex,
          startPeriod: 0,
          endPeriod: periods.length - 1,
        },
        true,
        model.reporting_currency,
        mode,
      );
      if (!payload) return;
      void copyText(payload.text).catch(() =>
        toast.error("The line item could not be copied."),
      );
    },
    [model.reporting_currency, periods, rows],
  );

  const contextCell = contextMenu?.kind === "cell" ? contextMenu.cell : null;
  const contextRow =
    contextMenu?.kind === "cell"
      ? contextMenu.cell.row
      : contextMenu?.kind === "row"
        ? contextMenu.row
        : null;
  const contextRowIndex =
    contextMenu?.kind === "cell"
      ? contextMenu.cell.rowIndex
      : contextMenu?.kind === "row"
        ? contextMenu.rowIndex
        : null;
  const contextRowTarget = contextRow
    ? authoredRowTarget(model, contextRow)
    : null;
  const contextBreakdown = contextRow?.breakdown?.node ?? null;
  const contextUsesSelectedRows = Boolean(
    contextRowIndex !== null &&
    rowSelection &&
    contextRowIndex >= rowSelection.startRow &&
    contextRowIndex <= rowSelection.endRow,
  );
  const contextRowTargets = contextUsesSelectedRows
    ? (selectedAuthoredRowTargets ?? [])
    : contextRowTarget
      ? [contextRowTarget]
      : [];
  const contextHasMultipleRows = Boolean(
    contextRowTargets.length > 1 ||
    (contextUsesSelectedRows &&
      rowSelection &&
      rowSelection.startRow !== rowSelection.endRow),
  );
  const contextMenuActions: StatementContextMenuAction[] =
    contextMenu && contextRow && contextRowIndex !== null
      ? [
          {
            label: "Insert row above",
            group: "row",
            shortcut: ["⌥", "⇧", "↑"],
            disabled: !canEditRows || editorDirty || pendingSaveCount > 0,
            onSelect: () =>
              openInlineRowDraft(addRowPlacement(model, contextRow, "before")),
          },
          {
            label: "Insert row below",
            group: "row",
            shortcut: ["⌥", "⇧", "↓"],
            disabled: !canEditRows || editorDirty || pendingSaveCount > 0,
            onSelect: () =>
              openInlineRowDraft(addRowPlacement(model, contextRow, "after")),
          },
          ...(contextBreakdown
            ? [
                {
                  label: "Edit breakdown",
                  group: "row" as const,
                  disabled: !canEditRows || editorDirty || pendingSaveCount > 0,
                  onSelect: () => openBreakdownEditor(contextBreakdown),
                },
              ]
            : []),
          {
            label: "Edit row",
            group: "row",
            disabled:
              !canEditRows ||
              !contextRowTarget ||
              contextHasMultipleRows ||
              editorDirty ||
              pendingSaveCount > 0,
            onSelect: () => {
              if (contextRowTarget) {
                openInlineRowEdit(contextRowTarget);
              }
            },
          },
          {
            label: contextHasMultipleRows ? "Delete rows" : "Delete row",
            group: "row",
            shortcut: ["⌥", "⇧", "⌫"],
            destructive: true,
            disabled:
              !canEditRows ||
              contextRowTargets.length === 0 ||
              editorDirty ||
              pendingSaveCount > 0,
            onSelect: () => {
              requestRowDelete(contextRowTargets);
            },
          },
          ...(contextCell
            ? [
                {
                  label: "Copy",
                  group: "clipboard" as const,
                  onSelect: () => copyContextCell(contextCell, "formulas"),
                },
                {
                  label: "Copy values",
                  group: "clipboard" as const,
                  onSelect: () => copyContextCell(contextCell, "values"),
                },
                {
                  label: "Paste",
                  group: "clipboard" as const,
                  disabled:
                    !canEdit ||
                    pasteLocked ||
                    typeof navigator.clipboard?.readText !== "function",
                  onSelect: () => {
                    const range = {
                      startRow: contextCell.rowIndex,
                      endRow: contextCell.rowIndex,
                      startPeriod: contextCell.periodIndex,
                      endPeriod: contextCell.periodIndex,
                    };
                    void navigator.clipboard.readText().then(
                      (text) => pasteText(text, range),
                      () => toast.error("Clipboard access was not available."),
                    );
                  },
                },
                {
                  label: "Edit cell",
                  group: "cell" as const,
                  disabled: !canEdit,
                  onSelect: () => beginGridCellEdit(contextCell),
                },
                {
                  label: contextCell.value.comment?.trim()
                    ? "Edit comment"
                    : "Add comment",
                  group: "cell" as const,
                  disabled: !canEdit,
                  onSelect: () => {
                    setRowDetailsKey(null);
                    setSidepeekVisible(true);
                    setSidepeekCollapsed(false);
                    setCommentEditRequest((current) => current + 1);
                  },
                },
              ]
            : [
                {
                  label: "Copy line item",
                  group: "clipboard" as const,
                  onSelect: () => copyContextRow(contextRowIndex, "formulas"),
                },
                {
                  label: "Copy line-item values",
                  group: "clipboard" as const,
                  onSelect: () => copyContextRow(contextRowIndex, "values"),
                },
              ]),
        ]
      : [];

  const openFormulaReference = useCallback(
    (nextReference: ModelReference, inspect: boolean) => {
      if (editorDirty) {
        toast.error("Save or reset this cell before following its formula.");
        return;
      }
      directSelectionTarget.current = null;
      if (inspect) {
        const nextSelected = selectedCellForReference(model, nextReference);
        if (nextSelected) {
          setRowDetailsKey(null);
          setSidepeekVisible(true);
          setSidepeekCollapsed(false);
          setSelected(nextSelected);
        }
      }
      if (onOpenReference) {
        onOpenReference(nextReference);
      } else {
        setLocalReference(nextReference);
      }
    },
    [editorDirty, model, onOpenReference],
  );

  useEffect(() => {
    const anchor = referenceAnchor.current;
    if (!referenceKey || !referenceRange || !anchor) return;
    if (selectedReferenceKey === referenceKey) {
      if (localReference === null) directSelectionTarget.current = null;
      return;
    }
    if (directSelectionTarget.current !== null) {
      if (directSelectionTarget.current === referenceKey) {
        if (localReference === null) directSelectionTarget.current = null;
        return;
      }
      directSelectionTarget.current = null;
    }
    anchor.scrollIntoView({ block: "center", inline: "center" });
    if (!selected) anchor.focus({ preventScroll: true });
  }, [
    localReference,
    referenceKey,
    referenceRange,
    selected,
    selectedReferenceKey,
    visibleRows,
  ]);

  function changeStatementZoom(nextPercent: number): void {
    const bounded = Math.min(
      STATEMENT_ZOOM_MAX,
      Math.max(STATEMENT_ZOOM_MIN, nextPercent),
    );
    if (bounded === statementZoomPercent) return;
    const container = scrollElement.current;
    if (container) {
      let anchorIndex = 0;
      let anchorOffset = 0;
      while (anchorIndex < visibleRows.length - 1) {
        const nextOffset =
          anchorOffset +
          statementRowSize(visibleRows[anchorIndex], showRowGaps) *
            statementZoom;
        if (nextOffset > container.scrollTop) break;
        anchorOffset = nextOffset;
        anchorIndex += 1;
      }
      const headerHeight =
        container
          .querySelector<HTMLTableCellElement>("thead th")
          ?.getBoundingClientRect().height ?? 0;
      pendingZoomAnchor.current = {
        index: anchorIndex,
        scrollLeft: container.scrollLeft * (bounded / statementZoomPercent),
        viewportOffset: headerHeight + anchorOffset - container.scrollTop,
      };
    }
    setStatementZoomPercent(bounded);
  }

  const inlineAddRow = rowEditor?.mode === "add" && (
    <tr
      className="statement-line-row xlm-inline-row-editor xlm-inline-add-row"
      onKeyDown={(event) => {
        if (event.key === "Escape" && pendingSaveCount === 0) {
          event.preventDefault();
          setRowEditor(null);
          return;
        }
        if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
          event.preventDefault();
          void submitInlineRowDraft();
        }
      }}
    >
      <th className="statement-table__line" scope="row">
        <div className="xlm-inline-row-editor__line">
          <label>
            <span className="sr-only">New row name</span>
            <input
              autoFocus
              value={rowEditor.type === "separator" ? "" : rowEditor.name}
              aria-label="New row name"
              aria-invalid={
                rowEditor.type !== "separator" &&
                rowEditor.attempted &&
                rowEditor.name.trim() === ""
              }
              placeholder={
                rowEditor.type === "separator" ? "Blank row" : "New row name"
              }
              maxLength={2_000}
              disabled={pendingSaveCount > 0 || rowEditor.type === "separator"}
              onChange={(event) =>
                updateInlineRowDraft((draft) => ({
                  ...draft,
                  name: event.target.value,
                  attempted: false,
                }))
              }
            />
          </label>
          {rowEditor.placement.kind === "node" && (
            <Selector
              className="xlm-inline-row-editor__type"
              ariaLabel="Row type"
              options={XLM_ROW_TYPE_OPTIONS}
              value={rowEditor.type}
              disabled={pendingSaveCount > 0}
              portalMenu
              onValueChange={(type) =>
                updateInlineRowDraft((draft) => ({
                  ...draft,
                  type,
                  attempted: false,
                }))
              }
            />
          )}
          <div className="xlm-inline-row-editor__actions">
            <button
              type="button"
              aria-label="Add row"
              title="Add row · Enter"
              disabled={pendingSaveCount > 0}
              onClick={() => void submitInlineRowDraft()}
            >
              {pendingSaveCount > 0 ? "…" : "✓"}
            </button>
            <button
              type="button"
              aria-label="Cancel row"
              title="Cancel · Escape"
              disabled={pendingSaveCount > 0}
              onClick={() => setRowEditor(null)}
            >
              ×
            </button>
          </div>
        </div>
      </th>
      <td className="statement-table__unit">
        {rowEditor.placement.kind === "node" &&
        XLM_VALUE_ROW_TYPES.has(rowEditor.type) ? (
          <RowUnitSelector
            value={rowEditor.unit}
            disabled={pendingSaveCount > 0}
            onValueChange={(unit) =>
              updateInlineRowDraft((draft) => ({ ...draft, unit }))
            }
          />
        ) : XLM_VALUE_ROW_TYPES.has(rowEditor.type) ? (
          displayXLMUnit(
            rowEditor.unit.trim() || null,
            null,
            model.reporting_currency,
          )
        ) : (
          "—"
        )}
      </td>
      {periods.map((period) => (
        <td
          key={`draft:${period.cadence}:${period.year}:${period.part ?? 0}`}
          className={`statement-value-cell${period.forecast ? " is-forecast" : ""}`}
          aria-label={`New row preview, ${period.label}`}
        >
          <span aria-hidden="true">—</span>
        </td>
      ))}
    </tr>
  );

  if (available.length === 0) {
    return <Empty>This model has no registered periods.</Empty>;
  }

  const inspectorVisible = Boolean(
    breakdownEditor ||
    (rowDetails && rowDetailsTarget) ||
    (selected && sidepeekVisible),
  );
  const inspectorOpen = Boolean(
    breakdownEditor ||
    (rowDetails && rowDetailsTarget) ||
    (selected && sidepeekVisible && !sidepeekCollapsed),
  );

  return (
    <section
      className={[
        "statement-workspace",
        fullScreen ? "statement-workspace--full-screen" : "",
        canEdit ? "statement-workspace--editable" : "",
        inspectorVisible ? "statement-workspace--inspector-visible" : "",
        inspectorOpen ? "statement-workspace--inspector-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Financial workbook"
    >
      <header className="statement-toolbar">
        <div className="statement-toolbar__controls">
          {available.length > 1 && (
            <SegmentedControl
              label="Statement cadence"
              value={cadence}
              options={available.map((value) => ({
                value,
                label:
                  value === "annual"
                    ? "Annual"
                    : value === "quarterly"
                      ? "Quarterly"
                      : "Semiannual",
              }))}
              onChange={(value) => {
                if (!clearReference()) return;
                setChoice(value);
              }}
            />
          )}
          {hasFormulas && (
            <SegmentedControl
              label="Statement cell display"
              value={cellDisplayMode}
              options={[
                { value: "values", label: "Values" },
                { value: "formulas", label: "Formulas" },
              ]}
              onChange={setCellDisplayMode}
            />
          )}
          <label className="search-field statement-search">
            <span className="sr-only">Search line items</span>
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" />
              <path d="m10.5 10.5 3 3" stroke="currentColor" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(event) => {
                if (!clearReference()) return;
                setQuery(event.target.value);
              }}
              placeholder="Find a line item"
            />
          </label>
          {boundaryIds.length > 0 && (
            <Button
              tone="quiet"
              onClick={() => {
                if (!clearReference()) return;
                setCollapsed(
                  collapsed.size > 0 ? new Set<string>() : new Set(boundaryIds),
                );
              }}
            >
              {collapsed.size > 0 ? "Expand all" : "Collapse all"}
            </Button>
          )}
          <div
            className="statement-zoom-control"
            role="group"
            aria-label="Statement zoom"
          >
            <Button
              tone="quiet"
              size="xs"
              aria-label="Zoom out"
              title="Zoom out"
              disabled={statementZoomPercent <= STATEMENT_ZOOM_MIN}
              onClick={() =>
                changeStatementZoom(statementZoomPercent - STATEMENT_ZOOM_STEP)
              }
            >
              −
            </Button>
            <Button
              tone="quiet"
              size="xs"
              className="statement-zoom-control__value"
              aria-label={`Reset statement zoom to 100% (currently ${statementZoomPercent}%)`}
              title="Reset zoom to 100%"
              onClick={() => changeStatementZoom(100)}
            >
              {statementZoomPercent}%
            </Button>
            <Button
              tone="quiet"
              size="xs"
              aria-label="Zoom in"
              title="Zoom in"
              disabled={statementZoomPercent >= STATEMENT_ZOOM_MAX}
              onClick={() =>
                changeStatementZoom(statementZoomPercent + STATEMENT_ZOOM_STEP)
              }
            >
              +
            </Button>
          </div>
          {leadingActions}
          {showFullScreenControl && (
            <IconButton
              tone="quiet"
              label={fullScreen ? "Exit full screen" : "Full screen"}
              tooltip={
                fullScreen
                  ? "Exit full screen (F)."
                  : "Expand the workbook to full screen (F)."
              }
              icon={<FullscreenIcon active={fullScreen} />}
              aria-pressed={fullScreen}
              aria-keyshortcuts="F"
              onClick={() => setFullScreen((current) => !current)}
            />
          )}
          {onOpenHistory && (
            <IconButton
              tone="quiet"
              label="Revision history"
              tooltip={
                revision === undefined
                  ? "Loading revision history…"
                  : editorDirty
                    ? "Save or reset this cell before opening revision history."
                    : pendingSaveCount > 0
                      ? `Wait for ${pendingSaveCount === 1 ? "the pending edit" : `${pendingSaveCount} pending edits`} to save before opening revision history.`
                      : saveStatus === "failed"
                        ? "Retry the failed edit before opening revision history."
                        : saveStatus === "conflict"
                          ? "Resolve the edit conflict before opening revision history."
                          : "Open revision history."
              }
              icon={<HistoryIcon />}
              disabled={
                revision === undefined ||
                editorDirty ||
                pendingSaveCount > 0 ||
                saveStatus === "failed" ||
                saveStatus === "conflict"
              }
              aria-controls="xlm-revision-history-sidepeek"
              onClick={onOpenHistory}
            />
          )}
          <StatementShortcutHelp />
          {actions}
          <IconButton
            tone="quiet"
            label="Copy workbook"
            tooltip="Copy the complete workbook with authored formulas."
            icon={<CopyIcon />}
            onClick={copyWorkbook}
          />
        </div>
        <FormulaBar
          selected={selected}
          model={model}
          editable={Boolean(activeCellEditor)}
          draft={activeCellEditor?.draft ?? null}
          dirty={editorDirty}
          validationError={activeCellEditor?.validationError ?? null}
          saveStatus={saveStatus}
          pendingSaveCount={pendingSaveCount}
          onDraftChange={updateCellDraft}
          onSubmit={submitCellDraft}
          onReset={resetCellDraft}
          onUndo={undoCellEdit}
          onRequestSave={requestAutoSave}
          inputRef={formulaInputRef}
          onExitEditing={focusSelectedCell}
          onOpenFormulaReference={openFormulaReference}
        />
      </header>

      {visibleRows.length === 0 ? (
        <Empty>No line items match “{query}”.</Empty>
      ) : (
        <div
          ref={scrollElement}
          className="statement-scroll"
          tabIndex={0}
          aria-label="Scrollable financial statement"
        >
          <table
            aria-rowcount={visibleRows.length + 1}
            style={statementTableStyle}
            className={`statement-table${
              cellDisplayMode === "formulas"
                ? " statement-table--formula-view"
                : ""
            }`}
          >
            <caption className="sr-only">
              {cadence} financial statement with actual and forecast periods
            </caption>
            <colgroup>
              <col className="statement-table__line-column" />
              <col className="statement-table__unit-column" />
              {periods.map((period) => (
                <col
                  className="statement-table__value-column"
                  key={`${period.cadence}:${period.year}:${period.part ?? 0}`}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="statement-table__line">
                  <div className="statement-table__line-heading">
                    <span>Line item</span>
                  </div>
                </th>
                <th scope="col" className="statement-table__unit">
                  Unit
                </th>
                {periods.map((period) => (
                  <th
                    scope="col"
                    key={`${period.cadence}:${period.year}:${period.part ?? 0}`}
                    className={period.forecast ? "is-forecast" : undefined}
                    title={period.endingDate ?? undefined}
                  >
                    <span className="statement-table__period-label">
                      {period.label}
                    </span>
                    {period.endingDate && (
                      <time
                        className="statement-table__period-end"
                        dateTime={period.endingDate}
                      >
                        {period.endingDate}
                      </time>
                    )}
                    {period.forecast && <small>Estimate</small>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {virtualRows.map((virtualRow, virtualIndex) => {
                const row = visibleRows[virtualRow.index];
                if (!row) return null;
                const previousVirtualRow = virtualRows[virtualIndex - 1];
                const virtualGap = Math.max(
                  0,
                  virtualRow.start - (previousVirtualRow?.end ?? 0),
                );
                const rowIndex = rowIndexes.get(row.key) ?? -1;
                const isCheck = row.section.toLowerCase() === "checks";
                const virtualSpacer = virtualGap > 0 && (
                  <tr
                    className="statement-virtual-spacer-row"
                    aria-hidden="true"
                  >
                    <td
                      colSpan={periods.length + 2}
                      style={{ height: virtualGap / statementZoom }}
                    />
                  </tr>
                );
                const spacer = row.gapBefore && showRowGaps && (
                  <tr className="statement-spacer-row" aria-hidden="true">
                    <td
                      colSpan={periods.length + 2}
                      style={{ height: STATEMENT_ROW_GAP }}
                    />
                  </tr>
                );
                const breakdown = row.breakdown;
                const breakdownRowTarget: XLMAuthoredRowTarget | null =
                  breakdown ? { kind: "node", node: breakdown.node } : null;
                const draggingBreakdown = Boolean(
                  rowDrag &&
                  breakdownRowTarget &&
                  sameAuthoredRowTarget(rowDrag.source, breakdownRowTarget),
                );
                const breakdownDropPosition =
                  rowDrag?.target &&
                  breakdownRowTarget &&
                  sameAuthoredRowTarget(rowDrag.target, breakdownRowTarget)
                    ? rowDrag.position
                    : null;
                const hiddenBreakdownControl =
                  breakdown?.first &&
                  row.boundary === null &&
                  breakdownRowTarget ? (
                    <tr
                      className={`statement-breakdown-control-row${
                        draggingBreakdown ? " statement-line-row--dragging" : ""
                      }${
                        breakdownDropPosition === "before"
                          ? " statement-line-row--drop-before"
                          : breakdownDropPosition === "after"
                            ? " statement-line-row--drop-after"
                            : ""
                      }`}
                      aria-label={`Breakdown ${breakdown.node.name}`}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        openRowContextMenu(
                          row,
                          rowIndex,
                          event.clientX,
                          event.clientY,
                        );
                      }}
                      onDragOver={(event) =>
                        previewRowDrop(event, breakdownRowTarget)
                      }
                      onDrop={(event) => dropRow(event, breakdownRowTarget)}
                    >
                      <th
                        className="statement-breakdown-control-cell"
                        scope="rowgroup"
                        colSpan={2}
                      >
                        <div className="statement-breakdown-control">
                          {canEditRows && (
                            <button
                              type="button"
                              className="statement-row-drag-handle statement-breakdown-drag-handle"
                              aria-label={`Move complete breakdown ${breakdown.node.name}`}
                              title="Drag to move the complete breakdown"
                              draggable={
                                !editorDirty &&
                                (pendingSaveCount === 0 || canQueueRowMove)
                              }
                              disabled={
                                editorDirty ||
                                (pendingSaveCount > 0 && !canQueueRowMove)
                              }
                              onDragStart={(event) =>
                                startRowDrag(event, breakdownRowTarget)
                              }
                              onDragEnd={() => setRowDrag(null)}
                            >
                              <svg
                                width="12"
                                height="14"
                                viewBox="0 0 12 14"
                                aria-hidden="true"
                              >
                                <circle cx="3" cy="3" r="1" />
                                <circle cx="9" cy="3" r="1" />
                                <circle cx="3" cy="7" r="1" />
                                <circle cx="9" cy="7" r="1" />
                                <circle cx="3" cy="11" r="1" />
                                <circle cx="9" cy="11" r="1" />
                              </svg>
                            </button>
                          )}
                          <span className="statement-breakdown-kicker">
                            Breakdown
                          </span>
                          <strong>{breakdown.node.name}</strong>
                          {canEditRows && (
                            <EditButton
                              label={`Edit breakdown ${breakdown.node.name}`}
                              disabled={editorDirty || pendingSaveCount > 0}
                              onClick={() =>
                                openBreakdownEditor(breakdown.node)
                              }
                            />
                          )}
                        </div>
                      </th>
                      <td
                        className="statement-breakdown-control-fill"
                        colSpan={periods.length}
                        aria-hidden="true"
                      />
                    </tr>
                  ) : null;
                if (row.boundary) {
                  const authoredBoundaryTarget = authoredRowTarget(model, row);
                  const boundaryRowTarget =
                    authoredBoundaryTarget?.kind === "node"
                      ? authoredBoundaryTarget
                      : null;
                  const draggingBoundary = Boolean(
                    rowDrag &&
                    boundaryRowTarget &&
                    sameAuthoredRowTarget(rowDrag.source, boundaryRowTarget),
                  );
                  const boundaryDropPosition =
                    rowDrag?.target &&
                    boundaryRowTarget &&
                    sameAuthoredRowTarget(rowDrag.target, boundaryRowTarget)
                      ? rowDrag.position
                      : null;
                  const isCollapsed =
                    query.trim() === "" && collapsed.has(row.key);
                  const boundaryIndent = 10 + row.depth * 14;
                  const boundaryKindLabel =
                    row.boundary === "section" ? "section" : "title";
                  let stickyBoundaryClass = "";
                  if (
                    row.boundary === "section" &&
                    virtualRow.index === activeSectionVirtualIndex.current
                  ) {
                    stickyBoundaryClass =
                      " statement-boundary-row--sticky-section";
                  } else if (
                    row.boundary === "heading" &&
                    virtualRow.index === activeHeadingVirtualIndex.current
                  ) {
                    stickyBoundaryClass =
                      " statement-boundary-row--sticky-heading";
                  }
                  const collapsible = row.descendantCount > 0;
                  const inlineEditingBoundary = Boolean(
                    rowEditor?.mode === "edit" &&
                    boundaryRowTarget &&
                    rowEditor.target.node.identifier ===
                      boundaryRowTarget.node.identifier,
                  );
                  const content = (
                    <span className="statement-boundary-title">
                      <span>{row.label ?? row.node.name}</span>
                      {row.breakdown && (
                        <span className="statement-breakdown-badge">
                          Breakdown
                        </span>
                      )}
                    </span>
                  );
                  return (
                    <Fragment key={row.key}>
                      {virtualSpacer}
                      {spacer}
                      {inlineAddRowIndex === virtualRow.index && inlineAddRow}
                      <tr
                        className={`statement-boundary-row statement-boundary-row--${row.boundary}${stickyBoundaryClass}${
                          row.breakdown
                            ? " statement-boundary-row--breakdown"
                            : ""
                        }${
                          draggingBoundary
                            ? " statement-line-row--dragging"
                            : ""
                        }${
                          boundaryDropPosition === "before"
                            ? " statement-line-row--drop-before"
                            : boundaryDropPosition === "after"
                              ? " statement-line-row--drop-after"
                              : ""
                        }${inlineEditingBoundary ? " xlm-inline-row-editor xlm-inline-edit-row" : ""}`}
                        aria-rowindex={virtualRow.index + 2}
                        data-index={virtualRow.index}
                        data-statement-section-id={
                          row.boundary === "section" ? row.key : undefined
                        }
                        onContextMenu={(event) => {
                          event.preventDefault();
                          openRowContextMenu(
                            row,
                            rowIndex,
                            event.clientX,
                            event.clientY,
                          );
                        }}
                        onDragOver={
                          boundaryRowTarget
                            ? (event) =>
                                previewRowDrop(event, boundaryRowTarget)
                            : undefined
                        }
                        onDrop={
                          boundaryRowTarget
                            ? (event) => dropRow(event, boundaryRowTarget)
                            : undefined
                        }
                        onKeyDown={
                          inlineEditingBoundary
                            ? (event) => {
                                if (
                                  event.key === "Escape" &&
                                  pendingSaveCount === 0
                                ) {
                                  event.preventDefault();
                                  setRowEditor(null);
                                  return;
                                }
                                if (
                                  event.key === "Enter" &&
                                  event.target instanceof HTMLInputElement
                                ) {
                                  event.preventDefault();
                                  void submitInlineRowEdit();
                                }
                              }
                            : undefined
                        }
                      >
                        <th
                          className="statement-boundary-label-cell"
                          scope="rowgroup"
                          colSpan={2}
                        >
                          {inlineEditingBoundary &&
                          rowEditor?.mode === "edit" ? (
                            <div className="xlm-inline-row-editor__line">
                              <label>
                                <span className="sr-only">
                                  Edit {boundaryKindLabel} name
                                </span>
                                <input
                                  autoFocus
                                  value={rowEditor.name}
                                  aria-label={`Edit ${boundaryKindLabel} name ${rowEditor.target.node.name}`}
                                  aria-invalid={
                                    rowEditor.attempted &&
                                    rowEditor.name.trim() === ""
                                  }
                                  maxLength={2_000}
                                  disabled={pendingSaveCount > 0}
                                  style={{
                                    paddingInlineStart: boundaryIndent,
                                    paddingInlineEnd: 10,
                                  }}
                                  onChange={(event) =>
                                    updateInlineRowEdit((draft) => ({
                                      ...draft,
                                      name: event.target.value,
                                      attempted: false,
                                    }))
                                  }
                                  onBlur={() => void submitInlineRowEdit()}
                                />
                              </label>
                            </div>
                          ) : (
                            <div className="statement-boundary-control">
                              {canEditRows && boundaryRowTarget && (
                                <button
                                  type="button"
                                  className="statement-row-drag-handle statement-boundary-drag-handle"
                                  aria-label={
                                    row.breakdown
                                      ? `Move complete breakdown ${row.breakdown.node.name}`
                                      : `Reorder ${row.boundary === "section" ? "section" : "group"} ${row.node.name}`
                                  }
                                  title={
                                    row.breakdown
                                      ? "Drag to move the complete breakdown"
                                      : `Drag to move the complete ${row.boundary === "section" ? "section" : "group"}`
                                  }
                                  draggable={
                                    !editorDirty &&
                                    (pendingSaveCount === 0 || canQueueRowMove)
                                  }
                                  disabled={
                                    editorDirty ||
                                    (pendingSaveCount > 0 && !canQueueRowMove)
                                  }
                                  onDragStart={(event) =>
                                    startRowDrag(event, boundaryRowTarget)
                                  }
                                  onDragEnd={() => setRowDrag(null)}
                                >
                                  <svg
                                    width="12"
                                    height="14"
                                    viewBox="0 0 12 14"
                                    aria-hidden="true"
                                  >
                                    <circle cx="3" cy="3" r="1" />
                                    <circle cx="9" cy="3" r="1" />
                                    <circle cx="3" cy="7" r="1" />
                                    <circle cx="9" cy="7" r="1" />
                                    <circle cx="3" cy="11" r="1" />
                                    <circle cx="9" cy="11" r="1" />
                                  </svg>
                                </button>
                              )}
                              {collapsible && (
                                <button
                                  type="button"
                                  className="statement-boundary-collapse"
                                  aria-expanded={!isCollapsed}
                                  aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${row.label ?? row.node.name}`}
                                  onClick={() => {
                                    if (!clearReference()) return;
                                    const next = new Set(collapsed);
                                    if (next.has(row.key)) next.delete(row.key);
                                    else next.add(row.key);
                                    setCollapsed(next);
                                  }}
                                >
                                  <span aria-hidden="true">
                                    {isCollapsed ? "▸" : "▾"}
                                  </span>
                                </button>
                              )}
                              {canEditRows && boundaryRowTarget ? (
                                <button
                                  type="button"
                                  className="statement-boundary-label statement-boundary-editor-trigger"
                                  style={{
                                    paddingInlineStart: collapsible
                                      ? Math.max(0, boundaryIndent - 28)
                                      : boundaryIndent,
                                  }}
                                  aria-label={`Edit ${boundaryKindLabel} name ${row.label ?? row.node.name}`}
                                  title={`Click to edit ${boundaryKindLabel} name`}
                                  disabled={editorDirty || pendingSaveCount > 0}
                                  onClick={() =>
                                    openInlineRowEdit(boundaryRowTarget)
                                  }
                                >
                                  {content}
                                </button>
                              ) : (
                                <div
                                  className="statement-boundary-label"
                                  style={{
                                    paddingInlineStart: collapsible
                                      ? Math.max(0, boundaryIndent - 28)
                                      : boundaryIndent,
                                  }}
                                >
                                  {content}
                                </div>
                              )}
                              {canEditRows && breakdown && (
                                <EditButton
                                  label={`Edit breakdown ${breakdown.node.name}`}
                                  disabled={editorDirty || pendingSaveCount > 0}
                                  onClick={() =>
                                    openBreakdownEditor(breakdown.node)
                                  }
                                />
                              )}
                              {canEditRows &&
                                boundaryRowTarget &&
                                !breakdown && (
                                  <EditButton
                                    label={`Open line item details ${boundaryRowTarget.node.name}`}
                                    disabled={
                                      editorDirty || pendingSaveCount > 0
                                    }
                                    onClick={() => {
                                      setBreakdownEditor(null);
                                      setSidepeekVisible(false);
                                      setRowDetailsKey(row.key);
                                    }}
                                  />
                                )}
                            </div>
                          )}
                        </th>
                        <td
                          className="statement-boundary-fill"
                          colSpan={periods.length}
                          aria-hidden="true"
                        />
                      </tr>
                    </Fragment>
                  );
                }
                const computedPercent = isHistoricallyFormulaDrivenPercentRow(
                  model,
                  row,
                );
                const completeLineItemSelected = Boolean(
                  rowSelection &&
                  rowIndex >= rowSelection.startRow &&
                  rowIndex <= rowSelection.endRow,
                );
                const rowEditTarget = authoredRowTarget(model, row);
                const rowUnitEditTarget =
                  rowEditTarget?.kind === "node" &&
                  XLM_VALUE_ROW_TYPES.has(rowEditTarget.node.type)
                    ? rowEditTarget
                    : null;
                const rowDropTarget =
                  rowDrag?.source.kind === "node" && breakdownRowTarget
                    ? breakdownRowTarget
                    : rowEditTarget;
                const inlineEditingRow = Boolean(
                  rowEditor?.mode === "edit" &&
                  rowEditTarget &&
                  rowEditor.target.node.identifier ===
                    rowEditTarget.node.identifier,
                );
                const draggingRow = Boolean(
                  rowDrag &&
                  ((rowEditTarget &&
                    sameAuthoredRowTarget(rowDrag.source, rowEditTarget)) ||
                    (breakdownRowTarget &&
                      sameAuthoredRowTarget(
                        rowDrag.source,
                        breakdownRowTarget,
                      ))),
                );
                const rowDropPosition =
                  rowDrag?.target &&
                  rowDropTarget &&
                  sameAuthoredRowTarget(rowDrag.target, rowDropTarget)
                    ? rowDrag.position
                    : null;
                const rowDeleteSelectionTargets = completeLineItemSelected
                  ? selectedAuthoredRowTargets
                  : rowEditTarget
                    ? [rowEditTarget]
                    : null;
                return (
                  <Fragment key={row.key}>
                    {virtualSpacer}
                    {spacer}
                    {hiddenBreakdownControl}
                    {inlineAddRowIndex === virtualRow.index && inlineAddRow}
                    <tr
                      className={`statement-line-row statement-line-row--${row.role}${
                        computedPercent
                          ? " statement-line-row--computed-percent"
                          : ""
                      }${row.bold ? " statement-line-row--bold" : ""}${
                        row.italic ? " statement-line-row--italic" : ""
                      }${breakdown ? " statement-line-row--breakdown" : ""}${
                        breakdown?.first
                          ? " statement-line-row--breakdown-first"
                          : ""
                      }${
                        breakdown?.last
                          ? " statement-line-row--breakdown-last"
                          : ""
                      }${
                        completeLineItemSelected
                          ? " statement-line-row--selected"
                          : ""
                      }${
                        completeLineItemSelected &&
                        rowIndex === rowSelection?.startRow
                          ? " statement-line-row--selected-top"
                          : ""
                      }${
                        completeLineItemSelected &&
                        rowIndex === rowSelection?.endRow
                          ? " statement-line-row--selected-bottom"
                          : ""
                      }${inlineEditingRow ? " xlm-inline-row-editor xlm-inline-edit-row" : ""}${
                        draggingRow ? " statement-line-row--dragging" : ""
                      }${
                        rowDropPosition === "before"
                          ? " statement-line-row--drop-before"
                          : rowDropPosition === "after"
                            ? " statement-line-row--drop-after"
                            : ""
                      }`}
                      aria-rowindex={virtualRow.index + 2}
                      data-index={virtualRow.index}
                      onKeyDown={
                        inlineEditingRow
                          ? (event) => {
                              if (
                                event.key === "Escape" &&
                                pendingSaveCount === 0
                              ) {
                                event.preventDefault();
                                setRowEditor(null);
                                return;
                              }
                              if (
                                event.key === "Enter" &&
                                event.target instanceof HTMLInputElement
                              ) {
                                event.preventDefault();
                                void submitInlineRowEdit();
                              }
                            }
                          : undefined
                      }
                      onDragOver={
                        rowDropTarget
                          ? (event) => previewRowDrop(event, rowDropTarget)
                          : undefined
                      }
                      onDrop={
                        rowDropTarget
                          ? (event) => dropRow(event, rowDropTarget)
                          : undefined
                      }
                    >
                      <th className="statement-table__line" scope="row">
                        {inlineEditingRow && rowEditor?.mode === "edit" ? (
                          <div className="xlm-inline-row-editor__line">
                            <label>
                              <span className="sr-only">Edit row name</span>
                              <input
                                autoFocus={rowEditor.initialFocus === "name"}
                                value={rowEditor.name}
                                aria-label={`Edit row name ${rowEditor.target.node.name}`}
                                aria-invalid={
                                  rowEditor.attempted &&
                                  rowEditor.name.trim() === ""
                                }
                                maxLength={2_000}
                                disabled={pendingSaveCount > 0}
                                style={{
                                  paddingInlineStart:
                                    10 +
                                    (row.depth + (computedPercent ? 1 : 0)) *
                                      14,
                                  paddingInlineEnd: 10,
                                }}
                                onChange={(event) =>
                                  updateInlineRowEdit((draft) => ({
                                    ...draft,
                                    name: event.target.value,
                                    attempted: false,
                                  }))
                                }
                                onBlur={(event) => {
                                  const relatedTarget = event.relatedTarget;
                                  if (
                                    relatedTarget instanceof Node &&
                                    event.currentTarget
                                      .closest("tr")
                                      ?.contains(relatedTarget)
                                  ) {
                                    return;
                                  }
                                  void submitInlineRowEdit();
                                }}
                              />
                            </label>
                          </div>
                        ) : (
                          <div className="statement-line-control">
                            {canEditRows && rowEditTarget && (
                              <button
                                type="button"
                                className="statement-row-drag-handle"
                                aria-label={`Reorder row ${rowEditTarget.node.name}`}
                                title="Drag to reorder row"
                                draggable={
                                  !editorDirty &&
                                  (pendingSaveCount === 0 || canQueueRowMove)
                                }
                                disabled={
                                  editorDirty ||
                                  (pendingSaveCount > 0 && !canQueueRowMove)
                                }
                                onDragStart={(event) =>
                                  startRowDrag(event, rowEditTarget)
                                }
                                onDragEnd={() => setRowDrag(null)}
                              >
                                <svg
                                  width="12"
                                  height="14"
                                  viewBox="0 0 12 14"
                                  aria-hidden="true"
                                >
                                  <circle cx="3" cy="3" r="1" />
                                  <circle cx="9" cy="3" r="1" />
                                  <circle cx="3" cy="7" r="1" />
                                  <circle cx="9" cy="7" r="1" />
                                  <circle cx="3" cy="11" r="1" />
                                  <circle cx="9" cy="11" r="1" />
                                </svg>
                              </button>
                            )}
                            {canEditRows && breakdown && !rowEditTarget && (
                              <span
                                className="statement-row-drag-placeholder"
                                aria-hidden="true"
                              />
                            )}
                            <button
                              type="button"
                              className="statement-line-selector"
                              aria-label={
                                canEditRows && rowEditTarget
                                  ? `Edit row name ${row.label ?? row.node.name}`
                                  : `Select complete line item ${row.label ?? row.node.name}`
                              }
                              aria-pressed={completeLineItemSelected}
                              aria-keyshortcuts={
                                canEditRows
                                  ? "Delete Backspace Alt+Shift+Delete"
                                  : undefined
                              }
                              title={
                                canEditRows && rowEditTarget
                                  ? "Click to edit row name · Shift-click to select complete line item"
                                  : "Click to select complete line item"
                              }
                              onClick={(event) => {
                                if (
                                  canEditRows &&
                                  rowEditTarget &&
                                  !event.shiftKey
                                ) {
                                  openInlineRowEdit(rowEditTarget, "name");
                                  return;
                                }
                                selectLineItems(rowIndex, event.shiftKey);
                              }}
                              onContextMenu={(event) => {
                                event.preventDefault();
                                openRowContextMenu(
                                  row,
                                  rowIndex,
                                  event.clientX,
                                  event.clientY,
                                );
                              }}
                              onKeyDown={(event) => {
                                if (
                                  event.key === "ContextMenu" ||
                                  (event.key === "F10" && event.shiftKey)
                                ) {
                                  event.preventDefault();
                                  const rect =
                                    event.currentTarget.getBoundingClientRect();
                                  openRowContextMenu(
                                    row,
                                    rowIndex,
                                    rect.left + 12,
                                    rect.top + 12,
                                  );
                                  return;
                                }
                                if (
                                  !canEditRows ||
                                  !rowDeleteSelectionTargets ||
                                  event.metaKey ||
                                  event.ctrlKey ||
                                  (event.key !== "Delete" &&
                                    event.key !== "Backspace")
                                ) {
                                  return;
                                }
                                event.preventDefault();
                                requestRowDelete(rowDeleteSelectionTargets);
                              }}
                            >
                              <span
                                className={`statement-line-label${
                                  row.node.type === "separator"
                                    ? " statement-line-label--blank"
                                    : ""
                                }`}
                                style={{
                                  paddingInlineStart:
                                    (row.depth + (computedPercent ? 1 : 0)) *
                                    14,
                                }}
                                title={row.node.description ?? undefined}
                              >
                                {row.node.type === "separator" ? (
                                  <span className="sr-only">Blank row</span>
                                ) : (
                                  (row.label ?? row.node.name)
                                )}
                              </span>
                            </button>
                            {canEditRows && rowEditTarget && (
                              <EditButton
                                label={`Open line item details ${rowEditTarget.node.name}`}
                                disabled={editorDirty || pendingSaveCount > 0}
                                onClick={() => {
                                  setBreakdownEditor(null);
                                  setSidepeekVisible(false);
                                  setRowDetailsKey(row.key);
                                }}
                              />
                            )}
                            {canEditRows &&
                              rowEditTarget?.node.type === "separator" && (
                                <TrashButton
                                  label={`Delete row ${rowEditTarget.node.name}`}
                                  disabled={editorDirty || pendingSaveCount > 0}
                                  onClick={() =>
                                    requestRowDelete([rowEditTarget])
                                  }
                                />
                              )}
                          </div>
                        )}
                      </th>
                      <td
                        className="statement-table__unit"
                        title={
                          inlineEditingRow
                            ? undefined
                            : displayXLMUnit(
                                row.unit,
                                row.unitLabel,
                                model.reporting_currency,
                              )
                        }
                      >
                        {inlineEditingRow &&
                        rowEditor?.mode === "edit" &&
                        rowEditor.target.kind === "node" &&
                        (XLM_VALUE_ROW_TYPES.has(rowEditor.target.node.type) ||
                          rowEditor.convertToTruth) ? (
                          <RowUnitSelector
                            value={rowEditor.unit}
                            disabled={pendingSaveCount > 0}
                            autoFocus={rowEditor.initialFocus === "unit"}
                            onValueChange={(unit) =>
                              updateInlineRowEdit((draft) => ({
                                ...draft,
                                unit,
                              }))
                            }
                            onCommit={() =>
                              setRowEditCommitRequest((current) => current + 1)
                            }
                          />
                        ) : row.node.type ===
                          "separator" ? null : rowUnitEditTarget ? (
                          <button
                            type="button"
                            className="statement-unit-editor-trigger"
                            aria-label={`Edit row unit ${row.label ?? row.node.name}`}
                            title="Click to edit row unit"
                            disabled={editorDirty || pendingSaveCount > 0}
                            onClick={() =>
                              openInlineRowEdit(rowUnitEditTarget, "unit")
                            }
                          >
                            {displayXLMUnit(
                              row.unit,
                              row.unitLabel,
                              model.reporting_currency,
                            )}
                          </button>
                        ) : (
                          displayXLMUnit(
                            row.unit,
                            row.unitLabel,
                            model.reporting_currency,
                          )
                        )}
                      </td>
                      {periods.map((period, periodIndex) => {
                        const value = matchingValue(row.values, period);
                        const selectableValue =
                          value ??
                          (row.selectable ? blankXLMValue(period) : undefined);
                        const resolved = value?.computed_value ?? value?.value;
                        const unit = row.unit ?? row.unitLabel;
                        const failedCheck = isCheck && resolved === false;
                        const passedCheck = isCheck && resolved === true;
                        const formulaMode =
                          cellDisplayMode === "formulas" &&
                          Boolean(value?.formula);
                        const figureColorRole = xlmFigureColorRole(value, {
                          forecast: period.forecast,
                          formulaOnlyPercentRow: computedPercent,
                          model: figureColorModel,
                        });
                        const figureColorStyle:
                          | StatementFigureColorStyle
                          | undefined = figureColorRole
                          ? {
                              "--statement-figure-color":
                                XLM_FIGURE_COLOR_CSS_BY_ROLE[figureColorRole],
                            }
                          : undefined;
                        const sourced = Boolean(
                          value?.source ||
                          (value?.document_references?.length ?? 0) > 0,
                        );
                        const referenced = Boolean(
                          displaySelectionRange &&
                          rowIndex >= displaySelectionRange.startRow &&
                          rowIndex <= displaySelectionRange.endRow &&
                          periodIndex >= displaySelectionRange.startPeriod &&
                          periodIndex <= displaySelectionRange.endPeriod,
                        );
                        const referenceAnchorCell = Boolean(
                          displaySelectionRange &&
                          rowIndex === displaySelectionRange.startRow &&
                          periodIndex === displaySelectionRange.startPeriod,
                        );
                        const fillHandleCell = Boolean(
                          canEdit &&
                          onSaveOperations &&
                          selectionHasOnlyFormulas &&
                          committedReferenceRange &&
                          rowIndex === committedReferenceRange.endRow &&
                          periodIndex === committedReferenceRange.endPeriod,
                        );
                        const gridCell = selectableValue
                          ? {
                              row,
                              period,
                              value: selectableValue,
                              rowIndex,
                              periodIndex,
                            }
                          : null;
                        const cellKey = row.selectable
                          ? xlmCellAddressKey(row, period)
                          : null;
                        const formulaDependency = Boolean(
                          cellKey && formulaDependencyCellKeys.has(cellKey),
                        );
                        const inlineDraft =
                          activeCellEditor?.cellKey === cellKey
                            ? activeCellEditor.draft
                            : null;
                        const inlineAnalysis =
                          activeCellEditor && inlineDraft
                            ? analyzeCellDraft(
                                activeCellEditor.baseline,
                                inlineDraft,
                              )
                            : null;
                        const inlineDirty = Boolean(inlineAnalysis?.dirty);
                        return (
                          <td
                            ref={
                              referenceAnchorCell ? referenceAnchor : undefined
                            }
                            key={`${row.key}:${period.year}:${period.part ?? 0}`}
                            tabIndex={referenceAnchorCell ? -1 : undefined}
                            aria-current={referenced ? "location" : undefined}
                            data-formula-dependency={
                              formulaDependency ? "true" : undefined
                            }
                            data-figure-color-role={
                              figureColorRole ?? undefined
                            }
                            style={figureColorStyle}
                            className={[
                              "statement-value-cell",
                              value ? "has-value" : "",
                              selectableValue ? "has-cell-control" : "",
                              canEdit && row.referenceable ? "is-editable" : "",
                              canEdit && row.referenceable && !value
                                ? "is-empty-editable"
                                : "",
                              inlineDirty ? "has-draft" : "",
                              period.forecast ? "is-forecast" : "",
                              failedCheck ? "is-check-failed" : "",
                              passedCheck ? "is-check-passed" : "",
                              sourced ? "is-sourced-value" : "",
                              value?.formula ? "has-formula" : "",
                              value?.comment?.trim() ? "has-comment" : "",
                              formulaMode ? "is-formula-view" : "",
                              fillHandleCell ? "has-fill-handle" : "",
                              formulaDependency ? "is-formula-dependency" : "",
                              referenced ? "is-summary-reference" : "",
                              referenced &&
                              displaySelectionRange &&
                              rowIndex === displaySelectionRange.startRow
                                ? "is-summary-reference-top"
                                : "",
                              referenced &&
                              displaySelectionRange &&
                              rowIndex === displaySelectionRange.endRow
                                ? "is-summary-reference-bottom"
                                : "",
                              referenced &&
                              displaySelectionRange &&
                              periodIndex === displaySelectionRange.startPeriod
                                ? "is-summary-reference-left"
                                : "",
                              referenced &&
                              displaySelectionRange &&
                              periodIndex === displaySelectionRange.endPeriod
                                ? "is-summary-reference-right"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            {selectableValue ? (
                              <button
                                ref={(control) => {
                                  if (!cellKey) return;
                                  if (control) {
                                    cellControlRefs.current.set(
                                      cellKey,
                                      control,
                                    );
                                  } else {
                                    cellControlRefs.current.delete(cellKey);
                                  }
                                }}
                                type="button"
                                className="statement-cell-trigger"
                                aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Alt+Shift+ArrowUp Alt+Shift+ArrowDown Alt+Shift+Delete Tab Enter F2 Delete Backspace ContextMenu Shift+F10 Control+C Meta+C Control+V Meta+V Control+Z Meta+Z"
                                aria-label={
                                  row.referenceable && !canEdit
                                    ? value
                                      ? `Review ${
                                          selectableValue.formula
                                            ? "formula and "
                                            : ""
                                        }sources for ${row.label ?? row.node.name}, ${period.label}`
                                      : `Review blank cell ${row.label ?? row.node.name}, ${period.label}`
                                    : `Select ${row.label ?? row.node.name}, ${period.label}`
                                }
                                title={
                                  [
                                    "Click to select · type or press Enter to edit · ⌘/Ctrl-click to open side view",
                                    formulaDependency
                                      ? "Used by the selected formula"
                                      : null,
                                    selectableValue.comment,
                                    ...(
                                      selectableValue.document_references ?? []
                                    ).flatMap((reference) =>
                                      reference.locations.map((location) =>
                                        xlmLocatorLabel(location.locator),
                                      ),
                                    ),
                                  ]
                                    .filter(Boolean)
                                    .join(" · ") || undefined
                                }
                                onPointerDown={(
                                  event: ReactPointerEvent<HTMLButtonElement>,
                                ) => {
                                  if (event.button !== 0 || !gridCell) return;
                                  const formulaInput = formulaInputRef.current;
                                  const pickingFormulaReference = Boolean(
                                    formulaInput &&
                                    document.activeElement === formulaInput &&
                                    activeCellEditor?.draft.formula.startsWith(
                                      "=",
                                    ),
                                  );
                                  if (pickingFormulaReference) {
                                    event.preventDefault();
                                  }
                                  rowSelectionAnchor.current = null;
                                  setRowSelection(null);
                                  handledPointerClick.current = true;
                                  const anchor =
                                    !pickingFormulaReference &&
                                    event.shiftKey &&
                                    selectionAnchor.current
                                      ? selectionAnchor.current
                                      : gridCell;
                                  if (!pickingFormulaReference) {
                                    selectionAnchor.current = anchor;
                                  }
                                  dragSelection.current = {
                                    anchor,
                                    focus: gridCell,
                                    startX: event.clientX,
                                    startY: event.clientY,
                                    ...(pickingFormulaReference && formulaInput
                                      ? {
                                          formulaSelection: {
                                            start:
                                              formulaInput.selectionStart ??
                                              formulaInput.value.length,
                                            end:
                                              formulaInput.selectionEnd ??
                                              formulaInput.value.length,
                                          },
                                        }
                                      : {}),
                                  };
                                  setSelectionPreview(
                                    gridSelectionReference(anchor, gridCell),
                                  );
                                }}
                                onPointerMove={(
                                  event: ReactPointerEvent<HTMLButtonElement>,
                                ) => {
                                  if (
                                    gridCell &&
                                    updateFormulaFillTarget(gridCell)
                                  ) {
                                    return;
                                  }
                                  const drag = dragSelection.current;
                                  if (
                                    !drag ||
                                    event.buttons !== 1 ||
                                    !gridCell
                                  ) {
                                    return;
                                  }
                                  const distance = Math.hypot(
                                    event.clientX - drag.startX,
                                    event.clientY - drag.startY,
                                  );
                                  if (distance < STATEMENT_DRAG_THRESHOLD) {
                                    return;
                                  }
                                  drag.focus = gridCell;
                                  setSelectionPreview(
                                    gridSelectionReference(
                                      drag.anchor,
                                      gridCell,
                                    ),
                                  );
                                }}
                                onClick={(
                                  event: ReactMouseEvent<HTMLButtonElement>,
                                ) => {
                                  if (
                                    event.detail > 0 &&
                                    handledPointerClick.current
                                  ) {
                                    handledPointerClick.current = false;
                                    return;
                                  }
                                  if (!gridCell) return;
                                  const anchor =
                                    event.shiftKey && selectionAnchor.current
                                      ? selectionAnchor.current
                                      : gridCell;
                                  commitGridSelection(
                                    gridCell,
                                    gridSelectionReference(anchor, gridCell),
                                    anchor,
                                    event.metaKey || event.ctrlKey,
                                  );
                                }}
                                onDoubleClick={(
                                  event: ReactMouseEvent<HTMLButtonElement>,
                                ) => {
                                  if (
                                    !gridCell ||
                                    event.metaKey ||
                                    event.ctrlKey
                                  ) {
                                    return;
                                  }
                                  event.preventDefault();
                                  beginGridCellEdit(gridCell);
                                }}
                                onContextMenu={(event) => {
                                  if (!gridCell) return;
                                  event.preventDefault();
                                  openCellContextMenu(
                                    gridCell,
                                    event.clientX,
                                    event.clientY,
                                  );
                                }}
                                onKeyDown={(event) => {
                                  if (!gridCell) return;
                                  if (
                                    event.key === "ContextMenu" ||
                                    (event.key === "F10" && event.shiftKey)
                                  ) {
                                    event.preventDefault();
                                    const rect =
                                      event.currentTarget.getBoundingClientRect();
                                    openCellContextMenu(
                                      gridCell,
                                      rect.left + 12,
                                      rect.top + 12,
                                    );
                                    return;
                                  }
                                  handleGridCellKeyDown(event, gridCell);
                                }}
                              >
                                {inlineDirty && inlineDraft ? (
                                  <span
                                    className={`statement-cell-draft${
                                      inlineDraft.formula
                                        ? " statement-cell-draft--formula"
                                        : ""
                                    }`}
                                  >
                                    {authoredCellText(inlineDraft)}
                                  </span>
                                ) : formulaMode ? (
                                  <span
                                    className="statement-formula"
                                    title={selectableValue.formula ?? undefined}
                                  >
                                    <code>{selectableValue.formula}</code>
                                  </span>
                                ) : (
                                  <span className="statement-cell-number">
                                    {displayXLMValue(selectableValue, unit)}
                                  </span>
                                )}
                              </button>
                            ) : row.node.type === "separator" ? null : (
                              "—"
                            )}
                            {fillHandleCell &&
                              gridCell &&
                              committedReferenceRange && (
                                <button
                                  type="button"
                                  className="statement-fill-handle"
                                  aria-label={`Fill formula from ${row.label ?? row.node.name}, ${period.label}`}
                                  title="Drag to fill this formula across adjacent cells"
                                  disabled={
                                    editorDirty ||
                                    pasteLocked ||
                                    pendingSaveCount > 0
                                  }
                                  onPointerDown={(event) =>
                                    beginFormulaFill(
                                      event,
                                      committedReferenceRange,
                                      gridCell,
                                    )
                                  }
                                />
                              )}
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })}
              {inlineAddRowIndex === visibleRows.length && inlineAddRow}
              {virtualRows.length > 0 && (
                <tr className="statement-virtual-spacer-row" aria-hidden="true">
                  <td
                    colSpan={periods.length + 2}
                    style={{
                      height:
                        Math.max(
                          0,
                          rowVirtualizer.getTotalSize() -
                            (virtualRows[virtualRows.length - 1]?.end ?? 0),
                        ) / statementZoom,
                    }}
                  />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {contextMenu && (
        <StatementContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          actions={contextMenuActions}
          onClose={() => setContextMenu(null)}
        />
      )}
      {rowDeleteTargets.length > 0 && (
        <ConfirmDialog
          title={
            rowDeleteTargets.length === 1
              ? `Delete “${rowDeleteTargets[0]?.node.name ?? "row"}”?`
              : `Delete ${rowDeleteTargets.length} rows?`
          }
          description={
            rowDeleteTargets.length === 1
              ? "This removes the authored row and its model values in a new immutable revision. Revision history is preserved."
              : "This removes the selected authored rows and their model values in one immutable revision. Revision history is preserved."
          }
          confirmLabel={
            rowDeleteTargets.length === 1 ? "Delete row" : "Delete rows"
          }
          busy={pendingSaveCount > 0}
          onCancel={() => setRowDeleteTargets([])}
          onConfirm={confirmRowDelete}
        />
      )}
      {breakdownEditor ? (
        <BreakdownDetails
          key={breakdownEditor.node.identifier}
          editor={breakdownEditor}
          reportingCurrency={model.reporting_currency}
          disabled={pendingSaveCount > 0}
          onChange={(update) =>
            setBreakdownEditor((current) =>
              current ? update(current) : current,
            )
          }
          onSave={submitBreakdownEditor}
          onClose={() => setBreakdownEditor(null)}
        />
      ) : rowDetails && rowDetailsTarget ? (
        <LineItemDetails
          key={rowDetails.key}
          row={rowDetails}
          model={model}
          target={rowDetailsTarget}
          onSave={saveSidePeekOperations}
          onClose={() => setRowDetailsKey(null)}
        />
      ) : selected && sidepeekVisible ? (
        <CellDetails
          key={xlmCellAddressKey(selected.row, selected.period)}
          selected={selected}
          model={model}
          companyId={companyId}
          documents={documents}
          collapsed={sidepeekCollapsed}
          onCollapsedChange={setSidepeekCollapsed}
          editable={canEdit && selected.row.referenceable}
          dirty={editorDirty}
          draft={activeCellEditor?.draft ?? null}
          onDraftChange={updateCellDraft}
          onRequestSave={requestAutoSave}
          editRequest={commentEditRequest}
          onOpenFormulaReference={openFormulaReference}
          onOpenReview={onOpenReview}
        />
      ) : null}
    </section>
  );
}
